import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { X, Users, Car, Clock, ChevronDown, ChevronRight, Plus, MapPin, Smartphone, AlertCircle, DollarSign, CheckCircle, Phone, Mail, Calendar, Package, CalendarClock, Check } from 'lucide-react';
import { Customer, CustomerVehicle, getCustomer, parseCustomerAdditionalAddresses } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { createMobileBooking, linkFieldQuotesToBooking, resolveCandidateFieldQuotes, type FieldQuoteCandidate } from '../lib/bookings';
import SelectResourceModal from './manage/SelectResourceModal';
import EditVehicleModal from './booking/EditVehicleModal';
import EditCustomerModal from './manage/EditCustomerModal';
import { Service, ServiceAddon, getServiceAddons } from '../lib/services';
import SelectCustomerModal from './manage/SelectCustomerModal';
import ExistingCustomerProposalsModal from './ExistingCustomerProposalsModal';
import {
  getConvertibleProposalsForCustomer,
  getProposal,
  type ConvertibleProposalSummary,
} from '../lib/proposals';
import EditServiceModal from './booking/EditServiceModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import SelectServiceModal from './manage/SelectServiceModal';

import { supabase } from '../lib/supabase';
import SelectServiceAddonsModal from './manage/SelectServiceAddonsModal';
import { format, getDaysInMonth } from 'date-fns';
import AddressAutocompleteModal from './AddressAutocompleteModal';
import { createMembership } from '../lib/memberships';
import { createBookingSeries, SERIES_FREQUENCIES, SERIES_LENGTHS, type SeriesFrequency } from '../lib/bookingSeries';
import { toast } from 'react-hot-toast';
// Removed all persistence systems
import { triggerBookingAutomations } from '../lib/automationsV2';
import { cancelBookingAutomations } from '../services/automationCancellationService';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import { prefetchSelectCustomers } from '../lib/customers/selectCustomerCache';
import { getWorkflowByServiceId, getWorkflowSteps } from '../lib/workflows';
import { usePowerUps } from '../hooks/usePowerUps';
import { applyReferralDiscount, getReferralProgram } from '../lib/referralProgram';
import { applyDiscount } from '../lib/discounts';
import CreateScheduleEditor from './booking/CreateScheduleEditor';
import SchedulePickerPanel from './booking/SchedulePickerPanel';

function InlineDiscountForm({ initialDiscount, bookingSubtotal, onApply }: {
  initialDiscount: { title: string; type: 'percentage' | 'fixed'; amount: number } | null;
  bookingSubtotal: number;
  onApply: (discount: { title: string; type: 'percentage' | 'fixed'; amount: number }) => void;
}) {
  const [title, setTitle] = useState(initialDiscount?.title || '');
  const [type, setType] = useState<'percentage' | 'fixed'>(initialDiscount?.type || 'percentage');
  const [amount, setAmount] = useState(initialDiscount?.amount?.toString() || '');

  const discountAmt = type === 'percentage'
    ? Math.min(Math.round((bookingSubtotal * ((parseFloat(amount) || 0) / 100)) * 100) / 100, bookingSubtotal)
    : Math.min(parseFloat(amount) || 0, bookingSubtotal);

  const canApply = title.trim() && parseFloat(amount) > 0;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Discount Name</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Loyalty discount"
          className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ fontSize: '16px' }}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Type</label>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button type="button" onClick={() => setType('percentage')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${type === 'percentage' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Percentage
          </button>
          <button type="button" onClick={() => setType('fixed')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${type === 'fixed' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Fixed Amount
          </button>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">{type === 'percentage' ? 'Percentage' : 'Amount'}</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">{type === 'percentage' ? '%' : '$'}</span>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            min="0"
            max={type === 'percentage' ? '100' : undefined}
            step={type === 'percentage' ? '1' : '0.01'}
            placeholder="0"
            className="w-full pl-8 pr-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ fontSize: '16px' }}
          />
        </div>
      </div>
      {discountAmt > 0 && (
        <div className="flex items-center justify-between px-3 py-2 bg-green-50 rounded-xl text-sm">
          <span className="text-green-700">Discount</span>
          <span className="font-semibold text-green-700">-${discountAmt.toFixed(2)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={() => onApply({ title: title.trim(), type, amount: parseFloat(amount) || 0 })}
        disabled={!canApply}
        className="w-full py-2.5 text-sm font-semibold text-white rounded-xl transition-all disabled:bg-gray-300 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
      >
        Apply Discount
      </button>
    </div>
  );
}

// Update Service interface to include duration_minutes
interface ExtendedService extends Service {
  duration_minutes?: number;
  tax_rate?: number;
  tax_exempt?: boolean;
}

type SavedCustomerAddressOption = {
  id: string;
  label: string;
  address: string;
  lat: number | null;
  lng: number | null;
};

function buildCustomerSavedAddressOptions(customer: Customer | null): SavedCustomerAddressOption[] {
  if (!customer) return [];
  const opts: SavedCustomerAddressOption[] = [];
  const main = customer.address?.trim();
  if (main) {
    opts.push({
      id: 'primary',
      label: 'Primary address',
      address: main,
      lat: customer.lat ?? null,
      lng: customer.lng ?? null,
    });
  }
  const extras = parseCustomerAdditionalAddresses(customer.additional_addresses);
  extras.forEach((ex, i) => {
    const a = ex.address?.trim();
    if (!a) return;
    opts.push({
      id: `additional-${i}`,
      label: ex.label?.trim() || `Additional address ${i + 1}`,
      address: a,
      lat: ex.lat ?? null,
      lng: ex.lng ?? null,
    });
  });
  return opts;
}

interface CreateMobileBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLocation?: () => void;
  initialProjectId?: string | null;
  initialOpportunityId?: number | null;
  initialProposal?: any;
  initialFieldQuoteId?: string | null;
  /** Customer to preselect when the modal opens (e.g. from the AI assistant). */
  initialCustomer?: Customer | null;
  isReschedule?: boolean;
  existingBookingId?: string;
  prefilledData?: {
    customer?: any;
    services?: Array<{
      vehicle: CustomerVehicle;
      service: ExtendedService;
      priceOption: any;
    }>;
    scheduleData?: {
      dropOffDate: string;
      dropOffTime: string;
      startDate: string;
      startTime: string;
      endDate: string;
      endTime: string;
      customer_notes: string;
      availableHours?: number;
    };
    bookingAddress?: string;
    bookingColor?: string;
    bookingSecondaryColor?: string;
    bookingSource?: string;
    // Quick booking specific data
    resource?: {
      id: string;
      name: string;
      resource_id: string;
    };
    asset?: {
      id: string;
      name: string;
      asset_id: string;
    };
    isQuickBooking?: boolean;
  };
}

// Update the interfaces to match the database structure
interface Shift {
  start: string;
  end: string;
}

interface DaySchedule {
  radius: number;
  shifts: Shift[];
  enabled: boolean;
  postal_codes?: string[]; // Array of postal codes that can be serviced on this day
}

interface WorkingHours {
  [day: string]: DaySchedule;
}

// Interface for mobile availability
interface MobileAvailability {
  id: string;
  resource_id: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at: string;
  updated_at: string;
}

// Update the Resource interface
interface Resource {
  id: string;           // This is the mobile_availability record ID
  resource_id: string;  // This is the ID from the resources table
  name: string;
  business_id: string;
  mobile_available: boolean;
  working_hours: WorkingHours;
  postal_codes: {
    [day: string]: string[];
  };
  created_at?: string;
  updated_at?: string;
}

// Add interface for the resources query response
interface ResourceQueryResponse {
  id: string;
  resource_id: string;
  resources: {
    id: string;
    name: string;
  };
}

// Add new interface for blocked time slots
interface BlockedTimeSlot {
  date: string;
  blockedSlots: string[];
}

interface BlockedTimeSlots {
  [resourceId: string]: BlockedTimeSlot[];
}

// Helper function to get days in month with proper day offset
const getDaysInMonthArray = (date: Date) => {
  // Create date objects for first and last day of month, using noon to avoid timezone issues
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0);
  const days = [];
  
  // Get the day of week (0-6, where 0 is Sunday)
  let startingDay = firstDay.getDay();
  // Convert to Monday-based week (0-6, where 0 is Monday)
  startingDay = startingDay === 0 ? 6 : startingDay - 1;
  
  // Add empty days for padding at start
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  
  // Add actual days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(i);
  }
  
  return days;
};

export default function CreateMobileBookingModal({
  isOpen,
  onClose,
  onSuccess,
  onSwitchToLocation,
  initialProjectId = null,
  initialOpportunityId = null,
  initialProposal,
  initialFieldQuoteId = null,
  initialCustomer = null,
  isReschedule = false,
  existingBookingId,
  prefilledData
}: CreateMobileBookingModalProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  // Resolve z-index from the modal stack so this modal (and its nested sub-modals) always render
  // above whatever opened it — e.g. the CustomerMessageModal (z ~100160). When opened standalone
  // this resolves to the original 100002 base, preserving existing behavior. All sub-modal z-indexes
  // below are expressed as `baseZ + <original offset from 100002>` to keep their relative ordering.
  const baseZ = useModalZIndex(100002);
  const { isPowerUpEnabled } = usePowerUps('services');
  const workflowsPowerUpEnabled = isPowerUpEnabled('workflows');

  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('Mobile Booking');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedServices, setSelectedServices] = useState<Array<{
    vehicle: CustomerVehicle;
    service: ExtendedService;
    priceOption: any;
    _uid?: string;
    discount_type?: string;
    discount_value?: number;
    discount_amount?: number;
  }>>([]);
  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);

  // Preselect a customer passed in from outside (e.g. the AI assistant's
  // lookup) — fetch the full record so vehicles/addresses come along.
  useEffect(() => {
    if (!isOpen || !initialCustomer) return;
    let cancelled = false;
    (async () => {
      let full: Customer = initialCustomer;
      if (initialCustomer.id) {
        try {
          full = await getCustomer(initialCustomer.id);
        } catch { /* fall back to the partial record */ }
      }
      if (!cancelled) setSelectedCustomer(full);
    })();
    return () => { cancelled = true; };
  }, [isOpen, initialCustomer]);
  const [existingProposals, setExistingProposals] = useState<ConvertibleProposalSummary[]>([]);
  const [isExistingProposalsModalOpen, setIsExistingProposalsModalOpen] = useState(false);
  const [convertingExistingProposal, setConvertingExistingProposal] = useState(false);
  const [convertedProposal, setConvertedProposal] = useState<any>(null);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [selectedServiceToEdit, setSelectedServiceToEdit] = useState<any>(null);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState<CustomerVehicle | null>(null);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);
  const [serviceAdditionalTaxes, setServiceAdditionalTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', selectedBusiness.id)
      .then(({ data }) => { if (data) setBusinessTaxRates(data); });
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const serviceIds = selectedServices.map(s => s.service.id);
    if (serviceIds.length === 0) { setServiceAdditionalTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', serviceIds)
      .then(({ data }) => { if (data) setServiceAdditionalTaxes(data); });
  }, [selectedServices]);

  const combinedBusinessRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;
  const businessTaxLabel = businessTaxRates.length > 0
    ? businessTaxRates.map(r => `${r.name} (${r.rate}%)`).join(' + ')
    : 'Tax';

  // Debug effect to track modal state changes
  useEffect(() => {
    console.log('🔍 DEBUG: isSelectServiceModalOpen changed to:', isSelectServiceModalOpen);
  }, [isSelectServiceModalOpen]);
  const [scheduleData, setScheduleData] = useState({
    dropOffDate: '',
    dropOffTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    customer_notes: ''
  });
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  // Unscheduled ("no date yet") mode — creates the mobile booking in a limbo state.
  const [isUnscheduled, setIsUnscheduled] = useState(false);
  const [removeFromAutomations, setRemoveFromAutomations] = useState(false);
  // Recurring booking series — fans out repeat bookings after the first is created.
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<SeriesFrequency>('monthly');
  const [recurringLengthMonths, setRecurringLengthMonths] = useState(12);
  const [bookingColor, setBookingColor] = useState('#3b82f6'); // Default blue
  const [bookingSecondaryColor, setBookingSecondaryColor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Field-quote link confirmation gate (shown before the booking is actually created)
  const [fqPickerOpen, setFqPickerOpen] = useState(false);
  const [fqCandidates, setFqCandidates] = useState<FieldQuoteCandidate[]>([]);
  const [fqSelectedIds, setFqSelectedIds] = useState<string[]>([]);
  const resolvedFieldQuoteIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (isOpen) {
      resolvedFieldQuoteIdsRef.current = null;
      setFqPickerOpen(false);
      setFqCandidates([]);
      setFqSelectedIds([]);
    }
  }, [isOpen]);
  const isHomeServices = selectedBusiness?.industry === 'Home Services';
  
  // Membership recurring settings
  const [membershipFrequency, setMembershipFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [membershipInterval, setMembershipInterval] = useState<number>(1);
  const [membershipEndType, setMembershipEndType] = useState<'after_occurrences'>('after_occurrences');
  const [membershipOccurrences, setMembershipOccurrences] = useState<number>(12);
  const [membershipDayOfWeek, setMembershipDayOfWeek] = useState<number>(1); // 0=Sunday, 1=Monday, etc.
  const [membershipStartTime, setMembershipStartTime] = useState<string>('09:00');
  const [membershipAvailabilityResults, setMembershipAvailabilityResults] = useState<Array<{
    date: string;
    time: string;
    available: boolean;
    conflict?: string;
    resource?: {
      id: string;
      name: string;
    };
  }>>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [editingBookingIndex, setEditingBookingIndex] = useState<number | null>(null);
  const [editingBookingDate, setEditingBookingDate] = useState<string>('');
  const [editingBookingTime, setEditingBookingTime] = useState<string>('09:00');
  const [isCalendarPickerOpen, setIsCalendarPickerOpen] = useState(false);
  const [resultsUpdateCounter, setResultsUpdateCounter] = useState(0);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<ExtendedService | null>(null);
  const [currentServiceUid, setCurrentServiceUid] = useState<string | null>(null);
  const [selectedServiceAddons, setSelectedServiceAddons] = useState<Record<string, ServiceAddon[]>>({});
  const [expandedServiceDescriptions, setExpandedServiceDescriptions] = useState<Set<string>>(new Set());
  const [serviceHasAddons, setServiceHasAddons] = useState<Record<string, boolean>>({});
  const [pendingDiscount, setPendingDiscount] = useState<{ title: string; type: 'percentage' | 'fixed'; amount: number } | null>(null);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [mobileAvailability, setMobileAvailability] = useState<MobileAvailability[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [postalCode, setPostalCode] = useState<string>('');
  const [bookingsByServiceAreaEnabled, setBookingsByServiceAreaEnabled] = useState(true);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isCustomerSectionExpanded, setIsCustomerSectionExpanded] = useState(true);
  const [isServicesSectionExpanded, setIsServicesSectionExpanded] = useState(true);
  const [isResourceSelectorOpen, setIsResourceSelectorOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState<Resource[]>([]);
  const [resourceNames, setResourceNames] = useState<Record<string, string>>({});
  const [bookingAddress, setBookingAddress] = useState<string>('');
  const [bookingLat, setBookingLat] = useState<number | null>(null);
  const [bookingLng, setBookingLng] = useState<number | null>(null);
  const [savedAddressPickerOpen, setSavedAddressPickerOpen] = useState(false);
  const savedAddressButtonRef = useRef<HTMLButtonElement>(null);
  const savedAddressMenuPortalRef = useRef<HTMLDivElement>(null);
  const [blockedTimeSlots, setBlockedTimeSlots] = useState<BlockedTimeSlots>({});
  const [blockedSlotsLoading, setBlockedSlotsLoading] = useState(false);

  useEffect(() => {
    if (selectedServices.length > 0 && selectedCustomer) {
      setIsCustomerSectionExpanded(false);
    }
  }, [selectedServices.length]);

  // Initialize with pre-filled data when in reschedule mode or quick booking mode
  useEffect(() => {
    const loadRescheduleData = async () => {
      // Reschedule / quick-booking are handled here. A plain conversion (e.g. CRM
      // "Mobile Booking") falls through to the `else if (prefilledData)` branch
      // below so the customer gets populated while the modal is open.
      if (prefilledData && isOpen && (isReschedule || prefilledData.isQuickBooking)) {
        if (isReschedule) {
          setTitle('Reschedule Mobile Booking');
          // Fetch full customer with vehicles if customer is provided
          if (prefilledData.customer?.id) {
            try {
              const fullCustomer = await getCustomer(prefilledData.customer.id);
              setSelectedCustomer(fullCustomer);
            } catch (error) {
              console.error('Error loading customer with vehicles:', error);
              setSelectedCustomer(prefilledData.customer || null);
            }
          } else {
            setSelectedCustomer(null);
          }
          setSelectedServices((prefilledData.services || []).map((s: any) => ({ ...s, _uid: s._uid || crypto.randomUUID() })));
          setScheduleData(prefilledData.scheduleData || {
            dropOffDate: '',
            dropOffTime: '',
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: '',
            customer_notes: ''
          });
          setBookingAddress(prefilledData.bookingAddress || '');
          setBookingColor(prefilledData.bookingColor || '#3b82f6');
          setBookingSecondaryColor(prefilledData.bookingSecondaryColor || null);
          
          // Set customer source from prefilled booking data if available
          if (prefilledData.bookingSource && selectedBusiness) {
            try {
              // Find the matching customer source from our business sources
              const customerSources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
              const matchingSource = customerSources.find(source => source.name === prefilledData.bookingSource);
              if (matchingSource) {
                setSelectedCustomerSource(matchingSource);
              }
            } catch (error) {
              console.error('Error loading customer sources for reschedule:', error);
            }
          }
        
        // Extract postal code from booking address if available
        if (prefilledData.bookingAddress) {
          console.log('🔍 Reschedule: Extracting postal code from address:', prefilledData.bookingAddress);
          
          // Enhanced regex patterns for different postal code formats
          const canadianPostalPattern = /[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d/; // Canadian: A1A 1A1
          const usZipPattern = /\d{5}(-\d{4})?/; // US: 12345 or 12345-6789
          
          let postalMatch = prefilledData.bookingAddress.match(canadianPostalPattern);
          if (!postalMatch) {
            postalMatch = prefilledData.bookingAddress.match(usZipPattern);
          }
          
          if (postalMatch) {
            const extractedPostal = postalMatch[0].toUpperCase().replace(/\s+/g, '');
            console.log('✅ Reschedule: Extracted postal code:', extractedPostal);
            setPostalCode(extractedPostal);
          } else {
            console.log('❌ Reschedule: No postal code found in address');
            // As a fallback, try to extract any alphanumeric pattern that might be a postal code
            const fallbackPattern = /[A-Z0-9]{5,7}/gi;
            const fallbackMatch = prefilledData.bookingAddress.match(fallbackPattern);
            if (fallbackMatch && fallbackMatch.length > 0) {
              const fallbackPostal = fallbackMatch[fallbackMatch.length - 1].toUpperCase();
              console.log('🔄 Reschedule: Using fallback postal code:', fallbackPostal);
              setPostalCode(fallbackPostal);
            }
          }
        } else {
          console.log('❌ Reschedule: No booking address provided');
        }
      } else if (prefilledData.isQuickBooking) {
        setTitle('Quick Booking');
        // Set pre-selected resource for quick booking (both paths)
        if (prefilledData.resource) {
          setSelectedResource({
            id: prefilledData.resource.id,
            resource_id: prefilledData.resource.resource_id,
            name: prefilledData.resource.name,
            business_id: selectedBusiness?.id || '',
            mobile_available: true,
            working_hours: {},
            postal_codes: {}
          });
          setSelectedResources([{
            id: prefilledData.resource.id,
            resource_id: prefilledData.resource.resource_id,
            name: prefilledData.resource.name,
            first_name: prefilledData.resource.name.split(' ')[0] || '',
            last_name: prefilledData.resource.name.split(' ').slice(1).join(' ') || '',
          }]);
        }
        // Set pre-selected asset for quick booking
        if (prefilledData.asset) {
          setSelectedAssets([{
            id: prefilledData.asset.asset_id,
            name: prefilledData.asset.name,
          }]);
        }
        // Set pre-selected schedule data
        if (prefilledData.scheduleData) {
          setScheduleData(prefilledData.scheduleData);
        }
      }
      } else if (prefilledData) {
        setTitle('Mobile Booking');
        if (prefilledData.customer?.id) {
          try {
            const fullCustomer = await getCustomer(prefilledData.customer.id);
            setSelectedCustomer(fullCustomer);
          } catch (error) {
            console.error('Error loading customer with vehicles for conversion:', error);
            setSelectedCustomer(prefilledData.customer || null);
          }
        } else {
          setSelectedCustomer(prefilledData.customer || null);
        }
        setSelectedServices((prefilledData.services || []).map((s: any) => ({ ...s, _uid: s._uid || crypto.randomUUID() })));
        setScheduleData(prefilledData.scheduleData || {
          dropOffDate: '',
          dropOffTime: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          customer_notes: ''
        });
        setBookingAddress(prefilledData.bookingAddress || '');
        setBookingColor(prefilledData.bookingColor || '#3b82f6');
        setBookingSecondaryColor(prefilledData.bookingSecondaryColor || null);

        if (prefilledData.bookingSource && selectedBusiness) {
          try {
            const customerSources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
            const bookingSourceName = prefilledData.bookingSource;
            const matchingSource = customerSources.find(
              source => source.name.toLowerCase() === bookingSourceName.toLowerCase()
            );
            if (matchingSource) {
              setSelectedCustomerSource(matchingSource);
            } else {
              setSelectedCustomerSource({
                id: 'project-source',
                business_id: selectedBusiness.id,
                name: bookingSourceName,
                is_active: true,
                created_at: '',
                updated_at: ''
              });
            }
          } catch (error) {
            console.error('Error loading customer sources for conversion:', error);
          }
        } else if (prefilledData.customer) {
          try {
            await detectCustomerSource(prefilledData.customer as Customer);
          } catch (error) {
            console.error('Error detecting customer source for conversion:', error);
          }
        }
      } else if (!isOpen) {
        // Reset state when modal closes
        setTitle('Mobile Booking');
        setSelectedCustomer(null);
        setSelectedServices([]);
        setScheduleData({
          dropOffDate: '',
          dropOffTime: '',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          customer_notes: ''
        });
        setBookingAddress('');
        setBookingColor('#3b82f6');
        setBookingSecondaryColor(null);
        setPostalCode('');
        setError(null);
        
        // Reset membership state
        setMembershipFrequency('weekly');
        setMembershipInterval(1);
        setMembershipEndType('after_occurrences');
        setMembershipOccurrences(12);
        setMembershipDayOfWeek(1);
        setMembershipStartTime('09:00');
        setMembershipAvailabilityResults([]);
        setCheckingAvailability(false);
        setEditingBookingIndex(null);
        setEditingBookingDate('');
        setEditingBookingTime('09:00');
        setIsCalendarPickerOpen(false);
        setResultsUpdateCounter(0);
        setSelectedResource(null);
        setSelectedResources([]);
        setSelectedAssets([]);
        setIsResourcesExpanded(false);
        setIsTitleExpanded(false);
        setIsCustomerSectionExpanded(true);
        setIsServicesSectionExpanded(true);
        setPendingDiscount(null);
        setIsDiscountModalOpen(false);
        setIsSelectResourceModalOpen(false);
        setSelectedCustomerSource(null);
        setIsCustomerSourceSelectionModalOpen(false);
        setIsNotesExpanded(false);
      }
    };
    
    loadRescheduleData();
  }, [isReschedule, prefilledData, isOpen, selectedBusiness?.id]);

  // Load proposal data when converting from a proposal
  useEffect(() => {
    const proposal = convertedProposal || initialProposal;
    if (!proposal || !isOpen) return;

    const loadProposalData = async () => {
      try {
        console.log('[MobileBooking] Loading proposal data:', proposal);
        setTitle(proposal.title || 'Mobile Booking');

        let customerVehicles: CustomerVehicle[] = [];
        if (proposal.customer_id) {
          const customerData = await getCustomer(proposal.customer_id);
          setSelectedCustomer(customerData);
          if (customerData.vehicles && customerData.vehicles.length > 0) {
            customerVehicles = customerData.vehicles;
          } else if (supabase) {
            const { data: vehicles } = await supabase
              .from('customer_vehicles')
              .select('*')
              .eq('customer_id', proposal.customer_id);
            if (vehicles) customerVehicles = vehicles;
          }
          try { await detectCustomerSource(customerData); } catch {}
        }

        const approvedOffer = proposal.offers?.find((o: any) => o.status === 'approved')
          || [...(proposal.offers || [])].sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0))[0];
        if (approvedOffer?.services) {
          const selectedProposalServices = approvedOffer.services.filter((svc: any) => {
            if (proposal.status !== 'approved') return true;
            const isSelectable = svc.isSelectable || svc.is_selectable;
            return !isSelectable || (isSelectable && svc.is_selected);
          });

          const bookingServices: Array<{ _uid?: string; vehicle: CustomerVehicle; service: ExtendedService; priceOption: any; discount_type?: string; discount_value?: number; discount_amount?: number }> = [];
          for (const ps of selectedProposalServices) {
            let vehicleData = customerVehicles.find(v => v.id === ps.vehicle_id);
            if (!vehicleData && ps.vehicle_id && supabase) {
              const { data: dv } = await supabase.from('customer_vehicles').select('*').eq('id', ps.vehicle_id).single();
              if (dv) { vehicleData = dv; customerVehicles.push(dv); }
            }
            if (!vehicleData) {
              vehicleData = {
                id: ps.vehicle_id || `no-vehicle-${Date.now()}`,
                customer_id: proposal.customer_id,
                make: ps.vehicle_make || 'Unknown',
                model: ps.vehicle_model || 'Vehicle',
                year: ps.vehicle_year || new Date().getFullYear(),
                color: ps.vehicle_color || '',
                license_plate: ps.vehicle_license_plate || '',
                vin: ps.vehicle_vin || '',
                mileage: 0, notes: '', is_primary: false, last_service: null,
                created_at: new Date().toISOString(), updated_at: new Date().toISOString()
              } as CustomerVehicle;
            }
            if (supabase) {
              const { data: serviceData, error: serviceError } = await supabase.from('services').select('*').eq('id', ps.service_id).single();
              if (!serviceError && serviceData) {
                const name = ps.name != null ? ps.name : serviceData.name;
                const description = ps.description != null ? ps.description : (serviceData.description ?? '');
                const price = ps.price != null ? ps.price : (serviceData.price_options?.[0]?.price ?? serviceData.price ?? 0);
                const durationMin = ps.duration_minutes != null ? ps.duration_minutes : (serviceData.price_options?.[0]?.duration_minutes ?? serviceData.duration_minutes ?? 60);
                const pricingType = ps.pricing_type || 'flat';
                const pUnitPrice = ps.unit_price ?? null;
                const pDurationPerUnit = ps.duration_per_unit ?? null;
                const pQuantity = ps.quantity ?? 0;

                const priceOptionObj = {
                  id: ps.service_id, service_id: ps.service_id, name: 'Standard',
                  price, duration_minutes: durationMin,
                  pricing_type: pricingType,
                  unit_price: pUnitPrice,
                  duration_per_unit: pDurationPerUnit,
                  quantity: pQuantity,
                  compare_at_price: ps.compare_at_price ?? null
                };
                const service: ExtendedService = {
                  ...serviceData, name, description: description ?? '',
                  tax_rate: ps.tax_rate, tax_exempt: ps.tax_exempt,
                  price_options: [priceOptionObj]
                };
                bookingServices.push({ _uid: crypto.randomUUID(), vehicle: vehicleData, service, priceOption: priceOptionObj, discount_type: ps.discount_type === 'percentage' ? 'percentage' : 'fixed', discount_value: ps.discount_value || 0, discount_amount: ps.discount_amount || 0 });
              }
            }
          }
          if (bookingServices.length > 0) {
            setSelectedServices(bookingServices);
          }
        }
      } catch (err) {
        console.error('[MobileBooking] Error loading proposal data:', err);
      }
    };

    loadProposalData();
  }, [convertedProposal, initialProposal, isOpen]);

  useEffect(() => {
    if (!selectedBusiness?.id || !isOpen) return;

    const loadBookingsByServiceAreaSetting = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'mobile_booking_by_service_area',
            p_default_value: true
          });

        if (!error && data !== null) {
          setBookingsByServiceAreaEnabled(Boolean(data));
        }
      } catch (settingError) {
        console.error('Error loading bookings by service area setting:', settingError);
      }
    };

    loadBookingsByServiceAreaSetting();
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    const handleCalendarSettingChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === 'mobile_booking_by_service_area') {
        setBookingsByServiceAreaEnabled(Boolean(customEvent.detail.value));
      }
    };

    window.addEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
    return () => {
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!bookingsByServiceAreaEnabled) {
      setScheduleData(prev => ({
        ...prev,
        dropOffDate: '',
        dropOffTime: ''
      }));
      setSelectedResource(null);
      setAvailableResources([]);
      setIsResourceSelectorOpen(false);
    }
  }, [bookingsByServiceAreaEnabled]);
  const [blockedSlotsError, setBlockedSlotsError] = useState<string | null>(null);
  const [bufferTime, setBufferTime] = useState<number>(0); // Default buffer time (0 minutes)
  const [bufferTimeLoading, setBufferTimeLoading] = useState(false);

  // Customer Source state
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [isCustomerSourceDropdownOpen, setIsCustomerSourceDropdownOpen] = useState(false);
  const [isCreateSourceModalOpen, setIsCreateSourceModalOpen] = useState(false);
  
  // Referral program state
  const [referralProgramActive, setReferralProgramActive] = useState(false);
  const [referralSourceCustomer, setReferralSourceCustomer] = useState<Customer | null>(null);
  const [isSelectReferralSourceModalOpen, setIsSelectReferralSourceModalOpen] = useState(false);
  
  // Workflow state
  const [servicesWithWorkflows, setServicesWithWorkflows] = useState<Set<string>>(new Set());
  const [workflowEnabled, setWorkflowEnabled] = useState(true); // Default enabled if workflows exist
  const [newSourceName, setNewSourceName] = useState('');
  const [createSourceLoading, setCreateSourceLoading] = useState(false);
  
  // New customer source selection modal state
  const [isCustomerSourceSelectionModalOpen, setIsCustomerSourceSelectionModalOpen] = useState(false);
  // Inline customer source dropdown state
  const [showCustomerSourceDropdown, setShowCustomerSourceDropdown] = useState(false);
  
  // Notes section expanded state
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  
  // Auto-expand notes if there are existing notes
  useEffect(() => {
    if (scheduleData.customer_notes && scheduleData.customer_notes.trim()) {
      setIsNotesExpanded(true);
    }
  }, [scheduleData.customer_notes]);

  // Helper functions for readable date/time formatting
  const formatReadableDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      // Handle different date formats and avoid timezone issues
      let date: Date;
      
      // If it's in YYYY-MM-DD format, parse it as local date to avoid timezone shift
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        date = new Date(dateString);
      }
      
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = dayNames[date.getDay()];
      const monthName = monthNames[date.getMonth()];
      const day = date.getDate();
      
      // Add ordinal suffix (st, nd, rd, th)
      const getOrdinalSuffix = (day: number) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      
      return `${dayName}, ${monthName} ${day}${getOrdinalSuffix(day)}`;
    } catch (error) {
      return dateString; // Fallback to original if parsing fails
    }
  };

  const formatReadableTime = (timeString: string) => {
    if (!timeString) return '';
    try {
      // Parse time string (assuming format like "09:00" or "13:30")
      const [hours, minutes] = timeString.split(':').map(Number);
      
      if (hours === 12) {
        return minutes === 0 ? '12pm' : `12:${minutes.toString().padStart(2, '0')}pm`;
      } else if (hours === 0) {
        return minutes === 0 ? '12am' : `12:${minutes.toString().padStart(2, '0')}am`;
      } else if (hours < 12) {
        return minutes === 0 ? `${hours}am` : `${hours}:${minutes.toString().padStart(2, '0')}am`;
      } else {
        const hour12 = hours - 12;
        return minutes === 0 ? `${hour12}pm` : `${hour12}:${minutes.toString().padStart(2, '0')}pm`;
      }
    } catch (error) {
      return timeString; // Fallback to original if parsing fails
    }
  };

  const handleScheduleChange = (type: 'dropOff' | 'start' | 'end', date: string, time: string) => {
    setScheduleData(prev => {
      const next = {
        ...prev,
        ...(type === 'dropOff' ? { dropOffDate: date, dropOffTime: time } : {}),
        ...(type === 'start' ? { startDate: date, startTime: time } : {}),
        ...(type === 'end' ? { endDate: date, endTime: time } : {})
      };

      if (!bookingsByServiceAreaEnabled) {
        next.dropOffDate = '';
        next.dropOffTime = '';
      }

      return next;
    });
  };
  
  // Ref for customer source dropdown positioning
  const customerSourceButtonRef = useRef<HTMLButtonElement>(null);

      // Removed all persistence systems
  // No need for duplicate modal persistence systems

  // Helper function to save current form state with optional overrides
  const saveMobileBookingProgress = (overrides: Record<string, any> = {}) => {
    const formData = {
      title,
      selectedCustomer,
      selectedServices,
      scheduleData,
      bookingColor,
      bookingSecondaryColor,
      selectedServiceAddons,
      bookingAddress,
      postalCode,
      selectedResource,
      ...overrides // Apply any immediate overrides to handle state timing issues
    };
    console.log('💾 Saving CreateMobileBookingModal progress:', Object.keys(formData));
    // Note: Persistence now handled centrally by usePageLifecycle
  };

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateMobileBookingModal progress...');
      // Note: Persistence now handled centrally by usePageLifecycle
      console.log('📥 Progress loading handled by centralized system');
    }
  }, [isOpen]);

  // Check if assets are enabled for this business
  useEffect(() => {
    if (isOpen && selectedBusiness?.id && supabase) {
      supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'teams',
        p_setting_key: 'assets_enabled',
        p_default_value: false
      }).then(({ data, error }) => {
        if (!error && data !== null) setAssetsEnabled(data);
        else setAssetsEnabled(false);
      }).catch(() => setAssetsEnabled(false));
    }
  }, [isOpen, selectedBusiness?.id]);

  // Calculate total duration in minutes
  const totalDurationMinutes = selectedServices.reduce((total, service) => {
    console.log('🔍 Duration calculation for service:', service.service.name, {
      serviceDuration: service.service.duration_minutes,
      priceOptionDuration: service.priceOption?.duration_minutes,
      addons: selectedServiceAddons[service._uid] || []
    });
    
    // Add service duration - use service.duration_minutes if available, otherwise fall back to priceOption
    let duration = total + (service.service.duration_minutes || service.priceOption?.duration_minutes || 0);
    
    // Add addon durations for this service
    const addons = selectedServiceAddons[service._uid] || [];
    const addonDuration = addons.reduce((addonSum, addon) => addonSum + (addon.duration_minutes || 0), 0);
    duration += addonDuration;
    
    console.log('🔍 Service total duration:', duration, 'minutes');
    return duration;
  }, 0);

  // Add effect to reset schedule when duration changes
  useEffect(() => {
    if (scheduleData.startDate || scheduleData.startTime) {
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
    }
  }, [totalDurationMinutes]);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Add effect to set initial booking address when customer is selected
  useEffect(() => {
    if (selectedCustomer?.address) {
      console.log('Setting booking address from customer:', selectedCustomer.address);
      setBookingAddress(selectedCustomer.address);
      if (selectedCustomer.lat != null && selectedCustomer.lng != null) {
        setBookingLat(selectedCustomer.lat);
        setBookingLng(selectedCustomer.lng);
        console.log('📍 Copied customer coordinates:', { lat: selectedCustomer.lat, lng: selectedCustomer.lng });
      } else {
        setBookingLat(null);
        setBookingLng(null);
      }
    } else {
      console.log('No customer address available');
      setBookingAddress('');
      setBookingLat(null);
      setBookingLng(null);
    }
  }, [selectedCustomer]);

  // Add effect to extract postal code from booking address
  useEffect(() => {
    if (bookingAddress) {
      // Regular expression to match a Canadian postal code (e.g., V3R 1A2 or V3R1A2)
      const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
      const match = bookingAddress.match(postalCodeRegex);
      
      if (match) {
        const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
        console.log('Extracted postal code from address:', extractedPostalCode, 'Using FSA:', extractedPostalCode.substring(0, 3));
        
        // Only update if postal code is empty or different to avoid unnecessary state changes
        if (!postalCode || postalCode !== extractedPostalCode.substring(0, 3)) {
          // Set only the first 3 characters of the postal code (FSA)
          setPostalCode(extractedPostalCode.substring(0, 3));
        }
      } else if (bookingAddress && !postalCode) {
        console.log('No postal code detected in address:', bookingAddress);
      }
    }
  }, [bookingAddress]);

  const savedAddressOptions = useMemo(
    () => buildCustomerSavedAddressOptions(selectedCustomer),
    [selectedCustomer],
  );

  useEffect(() => {
    if (!savedAddressPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (savedAddressButtonRef.current?.contains(t)) return;
      if (savedAddressMenuPortalRef.current?.contains(t)) return;
      setSavedAddressPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [savedAddressPickerOpen]);

  useEffect(() => {
    setSavedAddressPickerOpen(false);
  }, [selectedCustomer?.id]);

  const applyBookingAddressFromSaved = useCallback((address: string, lat: number | null, lng: number | null) => {
    setBookingAddress(address);
    setBookingLat(lat);
    setBookingLng(lng);
    setSavedAddressPickerOpen(false);
    const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
    const match = address.match(postalCodeRegex);
    if (match) {
      const fullPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
      setPostalCode(fullPostalCode.substring(0, 3));
    }
  }, []);

  // Load customer sources and referral program status when modal opens
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadCustomerSources();
      loadReferralProgramStatus();
      // Warm Select Customer so opening it feels instant.
      void prefetchSelectCustomers(selectedBusiness.id);
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadCustomerSources = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
      
      // Default sources that should always be available
      const defaultSources: CustomerSource[] = [
        {
          id: 'repeat-customer',
          business_id: selectedBusiness.id,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        },
        {
          id: 'referral',
          business_id: selectedBusiness.id,
          name: 'Referral',
          is_active: true,
          created_at: '',
          updated_at: ''
        }
      ];
      
      // Merge default sources with database sources, avoiding duplicates
      const allSources = [...defaultSources];
      sources.forEach(source => {
        // Don't add if a default source with the same name exists
        if (!defaultSources.some(ds => ds.name.toLowerCase() === source.name.toLowerCase())) {
          allSources.push(source);
        }
      });
      
      // Sort alphabetically by name
      allSources.sort((a, b) => a.name.localeCompare(b.name));
      
      setCustomerSources(allSources);
    } catch (error) {
      console.error('Error loading customer sources:', error);
    }
  };

  // Load referral program status
  const loadReferralProgramStatus = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('referral_program')
        .select('is_active')
        .eq('business_id', selectedBusiness.id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading referral program status:', error);
        setReferralProgramActive(false);
        return;
      }
      
      const isActive = data?.is_active || false;
      console.log('📊 Referral program status loaded:', { isActive, data });
      setReferralProgramActive(isActive);
    } catch (error) {
      console.error('Error loading referral program status:', error);
      setReferralProgramActive(false);
    }
  };

  const handleCreateCustomerSource = async () => {
    if (!selectedBusiness?.id || !newSourceName.trim()) return;
    
    try {
      setCreateSourceLoading(true);
      const newSource = await CustomerSourcesService.createCustomerSource({
        business_id: selectedBusiness.id,
        name: newSourceName.trim()
      });
      
      // Add to list and select it
      setCustomerSources(prev => [...prev, newSource].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCustomerSource(newSource);
      
      // Close modal and reset
      setIsCreateSourceModalOpen(false);
      setNewSourceName('');
    } catch (error) {
      console.error('Error creating customer source:', error);
      alert(error instanceof Error ? error.message : 'Failed to create customer source');
    } finally {
      setCreateSourceLoading(false);
    }
  };

  // Auto-detect customer source based on repeat status and booking history
  const detectCustomerSource = async (customer: Customer) => {
    if (!selectedBusiness?.id) return;
    
    try {
      // Step 1: Check customer.repeat column first (optimization)
      if (customer.repeat === true) {
        // Customer is already marked as repeat, auto-select repeat source (no modal needed)
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        console.log('✅ Auto-applied repeat customer source (already marked as repeat)');
        return;
      }
      
      // Step 2: If customer.repeat = false, count their total bookings (all statuses)
      if (!supabase) return;
      
      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id);
      
      if (error) {
        console.error('Error counting customer bookings:', error);
        // If error, show dropdown to be safe
        setShowCustomerSourceDropdown(true);
        return;
      }
      
      const totalBookings = bookingsData?.length || 0;
      
      // If customer has 1+ bookings, this will be their 2nd+ booking (repeat)
      if (totalBookings >= 1) {
        setSelectedCustomerSource({ 
          id: 'repeat-customer', 
          business_id: selectedBusiness.id,
          name: 'Repeat Customer', 
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        
        // Update customer's repeat column to true since this will be their 2nd+ booking
        try {
          const { error: updateError } = await supabase
            .from('customers')
            .update({ 
              repeat: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', customer.id);
          
          if (updateError) {
            console.error('Error updating customer repeat status:', updateError);
          } else {
            console.log(`✅ Updated customer ${customer.first_name} ${customer.last_name} to repeat status and auto-applied repeat source`);
            // Update local customer state
            setSelectedCustomer(prev => prev ? { ...prev, repeat: true } : null);
          }
        } catch (updateError) {
          console.error('Error updating customer repeat status:', updateError);
        }
      } else {
        // Customer has 0 bookings, this will be their 1st (new customer) - show inline dropdown
        console.log('🆕 New customer detected - showing customer source dropdown');
        setSelectedCustomerSource(null);
        setShowCustomerSourceDropdown(true);
      }
      
      // Always show the dropdown for all customers, regardless of booking count
      setShowCustomerSourceDropdown(true);
      
    } catch (error) {
      console.error('Error detecting customer source:', error);
      // If error, show dropdown to be safe
      setShowCustomerSourceDropdown(true);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Note: Persistence now handled centrally by usePageLifecycle
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setBookingAddress('');
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);
      setSelectedResources([]);
      setSelectedAssets([]);
      setIsResourcesExpanded(false);
      setIsSelectResourceModalOpen(false);
      setIsUnscheduled(false);
    }
  };

  const handleVehicleCreated = (vehicle: CustomerVehicle) => {
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
    
    // Save progress after vehicle creation
    saveMobileBookingProgress();
    
    // Update customer data to get the new vehicle (async)
    getCustomer(selectedCustomer?.id || '').then(updatedCustomer => {
      setSelectedCustomer(updatedCustomer);
    });
  };

  const handleCheckMembershipAvailability = async () => {
    if (!selectedBusiness?.id || (bookingsByServiceAreaEnabled && !postalCode)) {
      setError('Please ensure booking address is set to check availability');
      return;
    }

    try {
      setCheckingAvailability(true);
      setError(null);
      
      console.log('\n\n🎫 ========== MEMBERSHIP CHECK START ==========');
      console.log('🎫 Settings:', {
        frequency: membershipFrequency,
        interval: membershipInterval,
        occurrences: membershipOccurrences,
        dayOfWeek: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][membershipDayOfWeek],
        startTime: membershipStartTime,
        postalCode
      });

      // Generate occurrence dates (timezone-safe using local dates)
      const occurrences: Array<{ date: string; time: string; available: boolean; conflict?: string }> = [];
      
      // Start with today at noon to avoid timezone issues
      let currentDate = new Date();
      currentDate.setHours(12, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Find the next occurrence of the selected day of week
      // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
      // membershipDayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday (matches JavaScript)
      while (currentDate.getDay() !== membershipDayOfWeek) {
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      console.log('🎫 Found first occurrence:', {
        date: format(currentDate, 'yyyy-MM-dd'),
        dayOfWeek: currentDate.getDay(),
        selectedDayOfWeek: membershipDayOfWeek,
        dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][currentDate.getDay()]
      });
      
      // Check if the first occurrence is today and if the time has already passed
      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      const isToday = currentDateOnly.getTime() === today.getTime();
      
      if (isToday) {
        // Parse the start time
        const [startHour, startMinute] = membershipStartTime.split(':').map(Number);
        const now = new Date();
        // Create start time in local timezone using the current date
        const startTimeToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute, 0, 0);
        
        // If the start time has already passed today, move to the next occurrence of this day
        // (NOT the next interval - just the next occurrence of the day)
        if (now >= startTimeToday) {
          console.log('🎫 First occurrence is today but time has passed, moving to next occurrence of this day');
          switch (membershipFrequency) {
            case 'daily':
              // Next day (not next interval)
              currentDate.setDate(currentDate.getDate() + 1);
              break;
            case 'weekly':
              // Next week (same day next week, not next interval)
              currentDate.setDate(currentDate.getDate() + 7);
              break;
            case 'monthly':
              // Next month (same day next month, not next interval)
              currentDate.setMonth(currentDate.getMonth() + 1);
              break;
          }
        }
      }
      
      // Generate all occurrence dates
      // First booking is on the base date (next available day)
      // Subsequent bookings are spaced by the interval from the FIRST booking
      for (let i = 0; i < membershipOccurrences; i++) {
        const occurrenceDate = new Date(currentDate);
        
        // Add interval based on frequency
        // For i=0: use base date (no offset)
        // For i=1: add 1 interval from base
        // For i=2: add 2 intervals from base
        // etc.
        if (i > 0) {
          switch (membershipFrequency) {
            case 'daily':
              // Add i intervals of days
              occurrenceDate.setDate(occurrenceDate.getDate() + (membershipInterval * i));
              break;
            case 'weekly':
              // Add i intervals of weeks (each interval is membershipInterval weeks)
              occurrenceDate.setDate(occurrenceDate.getDate() + (membershipInterval * 7 * i));
              break;
            case 'monthly':
              // Add i intervals of months
              occurrenceDate.setMonth(occurrenceDate.getMonth() + (membershipInterval * i));
              break;
          }
        }
        
        console.log(`🎫 Booking ${i + 1}:`, {
          date: format(occurrenceDate, 'yyyy-MM-dd'),
          dayOfWeek: occurrenceDate.getDay(),
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][occurrenceDate.getDay()],
          interval: i,
          calculation: i === 0 ? 'base date' : `base + ${membershipInterval * i} ${membershipFrequency === 'daily' ? 'days' : membershipFrequency === 'weekly' ? 'weeks' : 'months'}`
        });
        
        // Use format instead of toISOString to avoid timezone shift
        const dateStr = format(occurrenceDate, 'yyyy-MM-dd');
        
        // Verify the day of week matches what we expect
        const dayOfWeekFromDate = occurrenceDate.getDay();
        const dayNameFromDate = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeekFromDate];
        
        if (i === 0) {
          console.log('🎫 First booking date calculation:', {
            dateStr,
            dayOfWeek: dayOfWeekFromDate,
            expectedDayOfWeek: membershipDayOfWeek,
            dayName: dayNameFromDate,
            matches: dayOfWeekFromDate === membershipDayOfWeek
          });
        }
        
        // Check availability for this occurrence
        // Calculate end time based on service duration
        const serviceDuration = selectedServices[0]?.priceOption?.duration_minutes || 0;
        const addons = selectedServiceAddons[selectedServices[0]?._uid] || [];
        const addonDuration = addons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
        const totalDuration = serviceDuration + addonDuration + bufferTime;
        
        // Create date in local timezone to avoid timezone conversion issues
        // Parse the date string and time separately
        const [year, month, day] = dateStr.split('-').map(Number);
        const [startHour, startMinute] = membershipStartTime.split(':').map(Number);
        
        // Create date object in local timezone
        const startDateTime = new Date(year, month - 1, day, startHour, startMinute, 0, 0);
        const endDateTime = new Date(startDateTime.getTime() + totalDuration * 60000);
        const endTime = endDateTime.toTimeString().slice(0, 5);
        
        // Check if this occurrence is in the past
        const now = new Date();
        if (startDateTime < now) {
          occurrences.push({
            date: dateStr,
            time: membershipStartTime,
            available: false,
            conflict: 'This time is in the past',
            resource: undefined
          } as any);
          continue; // Skip to next occurrence
        }
        
        // Check availability using same logic as calendar picker
        let available = true;
        let conflictReason = '';
        let resourceInfo: { id: string; name: string } | undefined = undefined;
        
        try {
          // Check if we have resources available for this postal code and time
          const availableResourcesForSlot = mobileAvailability.filter(resource => {
            // Check working hours first to get day of week
            const dayOfWeek = format(occurrenceDate, 'EEEE').toLowerCase();
            
            // Check postal code for this specific day
            let postalCodes: any = resource.postal_codes;
            if (typeof postalCodes === 'string') {
              postalCodes = JSON.parse(postalCodes);
            }
            
            // Get postal codes for the specific day
            const dayPostalCodes = postalCodes?.[dayOfWeek];
            
            if (!Array.isArray(dayPostalCodes) || !dayPostalCodes.includes(postalCode)) {
              return false;
            }
            
            let workingHours = resource.working_hours;
            if (typeof workingHours === 'string') {
              workingHours = JSON.parse(workingHours);
            }
            
            const daySchedule = workingHours?.[dayOfWeek];
            
            if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
              return false;
            }
            
            // Check if time fits within shifts
            const [startHour, startMinute] = membershipStartTime.split(':').map(Number);
            const bookingStartMinutes = startHour * 60 + startMinute;
            const bookingEndMinutes = bookingStartMinutes + totalDuration;
            
            return daySchedule.shifts.some(shift => {
              const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
              const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;
              const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
              const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
              
              return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
            });
          });
          
          if (availableResourcesForSlot.length === 0) {
            available = false;
            conflictReason = 'No resources available for this time slot';
          }
          
          // Get resource info if available (assign to outer variable)
          if (availableResourcesForSlot.length > 0) {
            const firstResource = availableResourcesForSlot[0];
            
            console.log(`🎫 DEBUG Resource lookup for ${dateStr}:`, {
              firstResource,
              resourceNamesMap: resourceNames,
              lookupById: resourceNames[firstResource.id],
              lookupByResourceId: resourceNames[firstResource.resource_id]
            });
            
            const resourceName = resourceNames[firstResource.id] || 
                                resourceNames[firstResource.resource_id] || 
                                `Resource ${firstResource.resource_id}`;
            
            resourceInfo = {
              id: firstResource.resource_id,
              name: resourceName
            };
            
            console.log(`🎫 ✅ Assigned resource for ${dateStr}:`, resourceInfo);
          } else {
            console.log(`🎫 ❌ No available resources found for ${dateStr}`);
          }
          
          // TODO: Check for booking conflicts with existing bookings
          // This would require querying the bookings table for each date/time
          
        } catch (err) {
          console.error('Error checking availability for occurrence:', err);
          available = false;
          conflictReason = 'Error checking availability';
        }
        
        occurrences.push({
          date: dateStr,
          time: membershipStartTime,
          available,
          conflict: available ? undefined : conflictReason,
          resource: resourceInfo
        } as any);
      }
      
      console.log(`🎫 ✅ Check complete: ${occurrences.filter(o => o.available).length}/${occurrences.length} available`);
      console.log('🎫 ========== MEMBERSHIP CHECK END ==========\n\n');
      setMembershipAvailabilityResults(occurrences);
      
    } catch (err) {
      console.error('Error checking membership availability:', err);
      setError('Failed to check availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validation checks...
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      if (!selectedBusiness) {
        throw new Error('Please select a business');
      }

      if (selectedServices.length === 0) {
        throw new Error('Please select at least one service');
      }

      if (totalDurationMinutes < 30) {
        throw new Error('Total booking duration must be at least 30 minutes. Please check your service durations.');
      }

      // Check if this is a membership booking
      const isMembershipBooking = false /* memberships retired — recurring bookings replace them */;

      // Field-quote link gate: before creating, surface likely field quotes for
      // this customer/opportunity and let the user confirm/pick. Skipped when the
      // origin is already known or for membership bookings (different create path).
      if (
        resolvedFieldQuoteIdsRef.current === null &&
        !initialProposal &&
        !initialFieldQuoteId &&
        !isMembershipBooking
      ) {
        const candidates = await resolveCandidateFieldQuotes({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer.id,
          opportunityId: initialOpportunityId ?? null,
          projectId: initialProjectId ?? null,
        });
        if (candidates.length > 0) {
          setFqCandidates(candidates);
          setFqSelectedIds([candidates[0].id]);
          setFqPickerOpen(true);
          setLoading(false);
          return;
        }
        resolvedFieldQuoteIdsRef.current = [];
      }

      const isSelectedResourceAnAsset = bookingsByServiceAreaEnabled && (selectedResource as any)?._isAsset === true;
      const primaryResourceId = bookingsByServiceAreaEnabled
        ? (isSelectedResourceAnAsset ? undefined : selectedResource?.resource_id)
        : selectedResources[0]?.id;

      const primaryResourceDisplayName = bookingsByServiceAreaEnabled
        ? (selectedResource
          ? (resourceNames[selectedResource.resource_id] || selectedResource.name || (isSelectedResourceAnAsset ? 'Asset' : 'Mobile Technician'))
          : 'Mobile Technician')
        : (selectedResources[0]
          ? `${selectedResources[0].first_name || ''} ${selectedResources[0].last_name || ''}`.trim()
          : 'Resource');
      
      // For non-membership bookings, require a selected resource or asset
      // (skipped for unscheduled bookings — resources are assigned when scheduled later).
      if (!isMembershipBooking && !isUnscheduled) {
        if (bookingsByServiceAreaEnabled) {
          if (!selectedResource && selectedAssets.length === 0) {
            throw new Error('Please select a resource or asset for this booking');
          }

          if (selectedResource && !selectedResource?.resource_id) {
            throw new Error('Invalid resource selected');
          }
        } else {
          if (selectedResources.length === 0 && selectedAssets.length === 0) {
            throw new Error('Please select at least one resource or asset for this booking');
          }
          if (!primaryResourceId && selectedAssets.length === 0) {
            throw new Error('Invalid resource selection');
          }
        }
      }
      
      if (isMembershipBooking) {
        console.log('🎫 Detected membership booking, using membership creation flow');
        
        // Validate membership-specific requirements
        if (membershipAvailabilityResults.length === 0) {
          throw new Error('Please check availability first');
        }
        
        if (!selectedCustomerSource) {
          throw new Error('Please select a customer source');
        }
        
        // Create membership with all bookings
        const firstService = selectedServices[0];
        await createMembership({
          business_id: selectedBusiness.id,
          customer_id: selectedCustomer.id,
          service_id: firstService.service.id,
          vehicle_id: firstService.vehicle.id,
          service_name: firstService.service.name,
          service_description: firstService.service.description || undefined,
          price: firstService.priceOption.price,
          duration_minutes: firstService.priceOption.duration_minutes,
          tax_rate: firstService.service.tax_rate,
          tax_exempt: firstService.service.tax_exempt,
          frequency: membershipFrequency,
          interval: membershipInterval,
          day_of_week: membershipDayOfWeek,
          start_time: membershipStartTime,
          end_type: membershipEndType,
          total_occurrences: membershipOccurrences,
          title: title || `${firstService.service.name} - Membership`,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || undefined,
          booking_address: bookingAddress,
          booking_lat: bookingLat,
          booking_lng: bookingLng,
          customer_notes: scheduleData.customer_notes,
          buffer_time: bufferTime,
          resource_id: primaryResourceId, // Optional - resources come from availability results
          created_by: currentResource?.id || 'admin',
          availability_results: membershipAvailabilityResults
        });
        
        console.log('🎫 🎉 Membership created successfully!');
        
        // Close modal and trigger success callback
        if (onSuccess) onSuccess();
        onClose();
        return;
      }

      // For quick booking, use pre-filled schedule data; otherwise use selected schedule data
      const bookingStartDate = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startDate : scheduleData?.startDate;
      const bookingStartTime = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startTime : scheduleData?.startTime;
      
      if (!isUnscheduled && (!bookingStartDate || !bookingStartTime)) {
        throw new Error('Please select a date and time for the booking');
      }
      
      // Determine end date/time:
      // When bookingsByServiceArea is OFF, the user manually sets end date/time via CreateScheduleEditor.
      // Use their values if present; otherwise fall back to calculating from service duration.
      const userEndDate = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endDate : scheduleData?.endDate;
      const userEndTime = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endTime : scheduleData?.endTime;

      let bookingEndDate: string;
      let bookingEndTime: string;

      if (isUnscheduled) {
        // No date yet — end date/time are left empty and nulled in the payload.
        bookingEndDate = '';
        bookingEndTime = '';
      } else if (!bookingsByServiceAreaEnabled && userEndDate && userEndTime) {
        bookingEndDate = userEndDate;
        bookingEndTime = userEndTime;
      } else {
        const startDateTime = new Date(`${bookingStartDate}T${bookingStartTime}`);
        const endDateTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60000);
        bookingEndDate = format(endDateTime, 'yyyy-MM-dd');
        bookingEndTime = format(endDateTime, 'HH:mm');
      }

      // For service-area mode (auto-calculated), prevent multi-day bookings
      if (!isUnscheduled && bookingsByServiceAreaEnabled && bookingEndDate !== bookingStartDate) {
        throw new Error('Mobile bookings cannot span multiple days. Please select a shorter service duration or an earlier start time.');
      }
      
      console.log('📅 Booking time calculation:', {
        start: `${bookingStartDate} ${bookingStartTime}`,
        serviceDuration: totalDurationMinutes,
        bufferTime: bufferTime,
        end: `${bookingEndDate} ${bookingEndTime}`,
        manualEndTime: !bookingsByServiceAreaEnabled && userEndDate && userEndTime ? true : false,
        note: 'End time is service duration only. Buffer block created separately.'
      });

      if (!selectedCustomerSource) {
        throw new Error('Please select a customer source');
      }
      
      // Log the buffer time being used
      console.log(`Creating mobile booking with buffer time: ${bufferTime} minutes`);

      // Calculate customer repeat status and source for tracking
      const totalBookings = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', selectedCustomer.id)
        .eq('business_id', selectedBusiness.id);
      
      const customerRepeatStatus = selectedCustomer.repeat === true ? 'repeat' :
        ((totalBookings.data?.length || 0) >= 1 ? 'repeat' : 'new');
      const customerSourceName = selectedCustomerSource ? selectedCustomerSource.name : undefined;

      // Prepare workflow data for services that have workflows enabled (only if power-up is enabled)
      const servicesWithWorkflowData = await Promise.all(
        selectedServices.map(async (service) => {
          const hasWorkflow = workflowsPowerUpEnabled && servicesWithWorkflows.has(service.service.id);
          let workflowData = null;

          if (hasWorkflow && workflowEnabled && primaryResourceId) {
            try {
              console.log('📋 Preparing workflow data for mobile booking service:', service.service.name);
              
              // Get the workflow template and steps
              const workflow = await getWorkflowByServiceId(service.service.id);
              if (workflow) {
                const workflowSteps = await getWorkflowSteps(workflow.id);
                
                // Fetch the resource's profile data including avatar
                const { data: resourceProfile } = await supabase
                  .from('resources')
                  .select('avatar_url, first_name, last_name')
                  .eq('id', primaryResourceId)
                  .single();
                
                const resourceName = resourceProfile 
                  ? `${resourceProfile.first_name} ${resourceProfile.last_name}`.trim()
                  : primaryResourceDisplayName || 'Mobile Technician';
                
                // Create workflow data with auto-assigned resource
                workflowData = workflowSteps.map((step, index) => ({
                  workflow_step_library_id: step.workflow_step_library_id || step.id,
                  title: step.title,
                  description: step.description || '',
                  order_index: step.order_index || index + 1,
                  status: 'pending',
                  completed_at: null,
                  photos_enabled: step.photos_enabled || false,
                  assigned_resources: [{
                    id: primaryResourceId,
                    name: resourceName,
                    avatar_url: resourceProfile?.avatar_url || null
                  }]
                }));

                console.log(`✅ Workflow data prepared for mobile booking service ${service.service.name}:`, workflowData);
              }
            } catch (workflowError) {
              console.error('Error preparing workflow for mobile booking:', workflowError);
              // Don't fail the entire booking creation if workflow fails
            }
          }

          return { ...service, workflowData };
        })
      );

      // Build asset IDs: include asset from service-area auto-selection + manually added assets
      const allAssetIds: string[] = [
        ...(isSelectedResourceAnAsset && selectedResource ? [selectedResource.resource_id] : []),
        ...selectedAssets.map(a => a.id)
      ];

      // Geocode if coordinates are missing but address exists
      let finalLat = bookingLat;
      let finalLng = bookingLng;
      if ((!finalLat || !finalLng) && bookingAddress?.trim() && window.google?.maps) {
        try {
          console.log('📍 Geocoding address before booking creation:', bookingAddress);
          const geocoder = new google.maps.Geocoder();
          const result = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
            geocoder.geocode({ address: bookingAddress }, (results, status) => {
              if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                resolve(results[0]);
              } else {
                resolve(null);
              }
            });
          });
          if (result) {
            finalLat = result.geometry.location.lat();
            finalLng = result.geometry.location.lng();
            setBookingLat(finalLat);
            setBookingLng(finalLng);
            console.log('📍 Geocoded coordinates:', { lat: finalLat, lng: finalLng });
          }
        } catch (err) {
          console.error('📍 Geocoding failed:', err);
        }
      }

      // Create booking params object
      const bookingParams = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        resource_id: primaryResourceId || undefined,
        resource_ids: bookingsByServiceAreaEnabled ? undefined : (selectedResources.length > 0 ? selectedResources.map(resource => resource.id) : undefined),
        asset_ids: allAssetIds.length > 0 ? allAssetIds : undefined,
        title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        start_date: isUnscheduled ? undefined : bookingStartDate,
        start_time: isUnscheduled ? undefined : bookingStartTime,
        end_date: isUnscheduled ? undefined : bookingEndDate,
        end_time: isUnscheduled ? undefined : bookingEndTime,
        buffer_time: isUnscheduled ? 0 : bufferTime,
        status: 'scheduled',
        is_unscheduled: isUnscheduled,
        notes: `Created with ${selectedServices.length} services`,
        customer_notes: scheduleData.customer_notes || "",
        color: bookingColor,
        secondary_color: bookingSecondaryColor || undefined,
        booking_address: bookingAddress,
        booking_lat: finalLat,
        booking_lng: finalLng,
        customer_repeat_status: customerRepeatStatus,
        source: customerSourceName,
        referral_source_customer_id: referralSourceCustomer?.id || undefined,
        project_id: initialProjectId || undefined,
        pipeline_opportunities_id: initialOpportunityId || (initialProposal as any)?.pipeline_opportunities_id || undefined,
        source_proposal_id: (() => {
          const linked = convertedProposal || initialProposal;
          return (linked && !(linked as any)?.isProject) ? linked.id : null;
        })(),
        automations_disabled: removeFromAutomations,
        field_quote_ids: resolvedFieldQuoteIdsRef.current && resolvedFieldQuoteIdsRef.current.length > 0
          ? resolvedFieldQuoteIdsRef.current
          : undefined,
        created_by: currentResource?.id || 'admin',
        vehicles: Object.entries(servicesByVehicle).map(([vehicleId, services]) => ({
          vehicle_id: isHomeServices || vehicleId === 'no-vehicle' ? null : vehicleId,
          services: services.map(service => {
            const addons = selectedServiceAddons[service._uid] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : 5;
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            
            // Find the corresponding service with workflow data
            const serviceWithWorkflow = servicesWithWorkflowData.find(s => s.service.id === service.service.id);
            
            return {
              service_id: service.service.id,
              name: service.service.name,
              description: service.service.description || '',
              price: servicePrice,
              discount_type: (service as any).discount_type === 'percentage' ? 'percentage' : 'fixed',
              discount_value: (service as any).discount_value || 0,
              discount_amount: (service as any).discount_amount || 0,
              tax_exempt: service.service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmount,
              total_amount: taxableAmount + taxAmount,
              duration_minutes: service.priceOption.duration_minutes,
              workflow_steps: serviceWithWorkflow?.workflowData || null,
              pricing_type: service.priceOption.pricing_type || 'flat',
              unit_price: service.priceOption.unit_price ?? null,
              duration_per_unit: service.priceOption.duration_per_unit ?? null,
              quantity: service.priceOption.quantity ?? 0,
              compare_at_price: service.priceOption.compare_at_price ?? null
            };
          })
        }))
      };
      
      console.log('Creating mobile booking with params:', {
        ...bookingParams,
        buffer_time: bookingParams.buffer_time // Explicitly log the buffer time
      });

      // Handle rescheduling - cancel automations, soft delete booking, hard delete mobile_bookings
      if (isReschedule && existingBookingId) {
        console.log('[MOBILE RESCHEDULE] 🔄 Starting reschedule process for booking:', existingBookingId);
        
        try {
          // 🚫 Step 1: Cancel existing automations FIRST
          if (selectedBusiness?.id) {
            try {
              console.log('[MOBILE RESCHEDULE] 🚫 Cancelling existing automations for booking:', existingBookingId);
              const cancellationResult = await cancelBookingAutomations(
                existingBookingId,
                'mobile_booking_rescheduled',
                selectedBusiness.id
              );
              
              if (cancellationResult.success) {
                console.log(`[MOBILE RESCHEDULE] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs`);
              } else {
                console.warn(`[MOBILE RESCHEDULE] ⚠️ Automation cancellation had issues:`, cancellationResult.errors);
              }
            } catch (automationError) {
              console.error('[MOBILE RESCHEDULE] ❌ Error cancelling automations:', automationError);
              // Don't fail the reschedule if automation cancellation fails
            }
          }
          
          // 🗑️ Step 2: Soft delete the booking (preserve for automation system)
          console.log('[MOBILE RESCHEDULE] 🗑️ Soft deleting booking entry:', existingBookingId);
          const { error: softDeleteError } = await supabase
            .from('bookings')
            .update({ 
              is_deleted: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingBookingId);
            
          if (softDeleteError) {
            console.error('Error soft deleting existing booking:', softDeleteError);
            throw new Error('Failed to soft delete existing booking');
          }
          
          // 💥 Step 3: Hard delete mobile_bookings entries (for resource scheduling)
          console.log('[MOBILE RESCHEDULE] 💥 Hard deleting mobile_bookings entries for booking:', existingBookingId);
          const { error: hardDeleteError } = await supabase
            .from('mobile_bookings')
            .delete()
            .eq('booking_id', existingBookingId);
            
          if (hardDeleteError) {
            console.error('Error hard deleting mobile_bookings entries:', hardDeleteError);
            throw new Error('Failed to hard delete mobile_bookings entries');
          }
          
          console.log('[MOBILE RESCHEDULE] ✅ Successfully completed reschedule cleanup (automations cancelled, booking soft deleted, mobile_bookings hard deleted)');
        } catch (deleteError) {
          console.error('Error during mobile reschedule cleanup:', deleteError);
          throw new Error('Failed to cleanup existing booking for mobile rescheduling');
        }
      }

      // Create the booking
      const result = await createMobileBooking(bookingParams);

      if (!result || !result.id) throw new Error('No booking data returned');

      // Link the booking to an originating field quote (e.g. "Convert to booking")
      if (initialFieldQuoteId) {
        await linkFieldQuotesToBooking(result.id, [initialFieldQuoteId], selectedBusiness?.id || '');
      }

      const bookingProjectId = (result as any)?.project_id || initialProjectId;

      if (bookingProjectId) {
        console.log('🔄 Handling project to booking conversion (mobile):', {
          projectId: bookingProjectId,
          bookingId: result.id
        });

        try {
          const { error: projectUpdateError } = await supabase
            .from('projects')
            .update({ 
              booking_id: result.id,
              status: 'Converted to booking'
            })
            .eq('id', bookingProjectId);

          if (projectUpdateError) {
            console.error('❌ Error updating project with booking_id:', projectUpdateError);
          } else {
            console.log('✅ Updated project with booking_id:', bookingProjectId);
          }

          const { data: projectPayments, error: paymentsError } = await supabase
            .from('payments')
            .select('*')
            .eq('project_id', bookingProjectId);

          if (paymentsError) {
            console.error('❌ Error fetching project payments:', paymentsError);
          } else if (projectPayments && projectPayments.length > 0) {
            console.log(`📦 Found ${projectPayments.length} payment(s) to transfer to booking ${result.id}`);

            for (const payment of projectPayments) {
              const updateData: any = {
                booking_id: result.id,
              };

              if (payment.payment_type) {
                updateData.payment_type = payment.payment_type;
              }

              const { error: updatePaymentError } = await supabase
                .from('payments')
                .update(updateData)
                .eq('id', payment.id);

              if (updatePaymentError) {
                console.error(`❌ Error updating payment ${payment.id}:`, updatePaymentError);
              } else {
                console.log(`✅ Linked payment ${payment.id} to booking ${result.id}`);
              }
            }
          }

          const { data: projectMaterials, error: materialsError } = await supabase
            .from('booking_materials')
            .select('id, item_id, booking_id')
            .eq('project_id', bookingProjectId)
            .is('booking_id', null);

          if (materialsError) {
            console.error('❌ Error fetching project materials:', materialsError);
          } else if (projectMaterials && projectMaterials.length > 0) {
            console.log(`📦 Found ${projectMaterials.length} material(s) to link to booking ${result.id}`);

            for (const material of projectMaterials) {
              const { error: updateMaterialError } = await supabase
                .from('booking_materials')
                .update({ booking_id: result.id })
                .eq('id', material.id);

              if (updateMaterialError) {
                console.error(`❌ Error updating material ${material.id}:`, updateMaterialError);
              } else {
                console.log(`✅ Linked material ${material.id} to booking ${result.id}`);

                if (material.item_id) {
                  const { data: stockTransactions, error: stockError } = await supabase
                    .from('stock_transactions')
                    .select('id')
                    .eq('item_id', material.item_id)
                    .eq('type', 'sale')
                    .is('booking_id', null)
                    .like('notes', `%project: ${bookingProjectId}%`);

                  if (!stockError && stockTransactions && stockTransactions.length > 0) {
                    const { error: updateStockError } = await supabase
                      .from('stock_transactions')
                      .update({ booking_id: result.id })
                      .in('id', stockTransactions.map(t => t.id));

                    if (updateStockError) {
                      console.error('❌ Error updating stock transactions:', updateStockError);
                    } else {
                      console.log(`✅ Linked stock transactions to booking ${result.id}`);
                    }
                  }
                }
              }
            }
          }

          // Transfer project expenses to booking (update expenses to have both project_id and booking_id)
          const { data: projectExpenses, error: expensesError } = await supabase
            .from('expenses')
            .select('id')
            .eq('project_id', bookingProjectId)
            .is('booking_id', null); // Only get expenses that aren't already linked to a booking

          if (expensesError) {
            console.error('❌ Error fetching project expenses:', expensesError);
          } else if (projectExpenses && projectExpenses.length > 0) {
            console.log(`💰 Found ${projectExpenses.length} expense(s) to link to booking ${result.id}`);

            for (const expense of projectExpenses) {
              // Update expense to have both project_id and booking_id
              const { error: updateExpenseError } = await supabase
                .from('expenses')
                .update({ booking_id: result.id })
                .eq('id', expense.id);

              if (updateExpenseError) {
                console.error(`❌ Error updating expense ${expense.id}:`, updateExpenseError);
              } else {
                console.log(`✅ Linked expense ${expense.id} to booking ${result.id}`);
              }
            }
          }
        } catch (linkError) {
          console.error('❌ Error handling mobile project conversion:', linkError);
        }
      }

      // Apply referral discount if conditions are met
      if (
        referralSourceCustomer &&
        selectedCustomerSource?.name?.toLowerCase().includes('refer') &&
        selectedCustomer &&
        selectedCustomer.repeat !== true // Customer is new (not explicitly repeat)
      ) {
        try {
          console.log('🎁 Applying referral discount to mobile booking:', result.id);
          const discountResult = await applyReferralDiscount(result.id, selectedBusiness.id);
          if (discountResult.success) {
            console.log('✅ Referral discount applied successfully');
          } else {
            console.warn('⚠️ Failed to apply referral discount:', discountResult.error);
            // Don't throw - booking is already created, discount is supplementary
          }
        } catch (discountError) {
          console.error('❌ Error applying referral discount:', discountError);
          // Don't throw - booking is already created, discount is supplementary
        }
      }

      // Apply pending discount if set
      if (pendingDiscount && result.id) {
        try {
          await applyDiscount(result.id, pendingDiscount.title, pendingDiscount.type, pendingDiscount.amount);
          console.log('✅ Pending discount applied:', pendingDiscount);
        } catch (discountErr) {
          console.error('Error applying discount:', discountErr);
        }
      }

      // Insert add-ons for each service (workflow data is now handled during
      // booking creation). Shared with recurring-series children.
      const applyAddonsToBooking = async (bookingId: string) => {
        for (const serviceData of selectedServices) {
          const addons = selectedServiceAddons[serviceData._uid] || [];

          if (addons.length > 0) {
            const { data: serviceResult } = await supabase
              .from('booking_services')
              .select('id')
              .eq('booking_id', bookingId)
              .eq('service_id', serviceData.service.id)
              .single();

            if (serviceResult) {
              // Insert add-ons
              const { error: addonsError } = await supabase
                .from('booking_service_addons')
                .insert(
                  addons.map(addon => ({
                    booking_service_id: serviceResult.id,
                    addon_id: addon.id,
                    price: addon.price
                  }))
                );

              if (addonsError) throw addonsError;
            }
          }
        }
      };
      await applyAddonsToBooking(result.id);

      // Recurring booking series — fan out the remaining occurrences at the
      // same time-of-day for the chosen length (e.g. monthly for 1 year).
      if (isRecurring && !isUnscheduled && !isReschedule && bookingStartDate && bookingStartTime) {
        try {
          const seriesResult = await createBookingSeries({
            businessId: selectedBusiness.id,
            customerId: selectedCustomer.id,
            firstBookingId: result.id,
            baseParams: bookingParams,
            frequency: recurringFrequency,
            lengthMonths: recurringLengthMonths,
            isMobile: true,
          });
          if (seriesResult) {
            const dayDelta = (from: string, to: string) =>
              Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);
            const shiftYmd = (ymd: string | undefined | null, days: number): string | undefined => {
              if (!ymd) return undefined;
              const [y, m, d] = ymd.split('-').map(Number);
              const dt = new Date(y, m - 1, d + days);
              return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
            };

            for (const child of seriesResult.children) {
              try {
                await applyAddonsToBooking(child.id);
              } catch (extraErr) {
                console.error('Series child add-ons failed:', extraErr);
              }
              // Children fire 'created' with series_child: reminder waits still
              // schedule per visit, but instant confirmations are skipped
              // (handled in execute-automation-step) — one confirmation total.
              if (!removeFromAutomations) {
                const delta = dayDelta(bookingStartDate, child.start_date);
                triggerBookingAutomations(selectedBusiness.id, 'created', {
                  booking_id: child.id,
                  series_child: true,
                  business_id: selectedBusiness.id,
                  title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                  customer: {
                    id: selectedCustomer.id,
                    name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                    full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
                    first_name: selectedCustomer.first_name,
                    last_name: selectedCustomer.last_name,
                    email: selectedCustomer.email,
                    phone: selectedCustomer.phone
                  },
                  schedule: {
                    start_date: child.start_date,
                    start_time: bookingStartTime,
                    end_date: shiftYmd(bookingEndDate, delta),
                    end_time: bookingEndTime,
                    drop_off_date: child.start_date,
                    drop_off_time: bookingStartTime
                  },
                  services: selectedServices.map(service => ({
                    id: service.service.id,
                    name: service.service.name,
                    price: service.priceOption.price,
                    duration_minutes: service.priceOption.duration_minutes
                  })),
                  booking_address: bookingAddress,
                  status: 'scheduled'
                }).catch(err => console.error('Series child automations failed:', err));
              }
            }

            const fmtDate = (ymd: string) =>
              new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            toast.success(
              `Recurring booking: ${seriesResult.totalVisits} visits scheduled through ${fmtDate(seriesResult.lastDate)}.` +
              (seriesResult.conflictDates.length > 0
                ? ` Heads up — double-booked dates: ${seriesResult.conflictDates.map(fmtDate).join(', ')}.`
                : '') +
              (seriesResult.failedDates.length > 0
                ? ` Could not create: ${seriesResult.failedDates.map(fmtDate).join(', ')}.`
                : ''),
              { duration: 8000 }
            );
          }
        } catch (seriesError) {
          console.error('Failed to create recurring series:', seriesError);
          toast.error('The booking was created, but the repeat visits could not be scheduled.');
        }
      }

      // Update proposal status to 'booked' if this was created from a proposal
      const linkedProposal = convertedProposal || initialProposal;
      if (linkedProposal?.id) {
        try {
          const { error: proposalUpdateError } = await supabase
            .from('proposals')
            .update({ status: 'booked', booking_id: result.id })
            .eq('id', linkedProposal.id);

          if (proposalUpdateError) {
            console.error('❌ Failed to update proposal status to booked:', proposalUpdateError);
          } else {
            console.log('✅ Updated proposal status to booked:', linkedProposal.id);
          }

          // Link the new booking to the field quote that originated this proposal
          if (result?.id) {
            try {
              const { data: proposalRow } = await supabase
                .from('proposals')
                .select('field_quote_id')
                .eq('id', linkedProposal.id)
                .maybeSingle();
              if (proposalRow?.field_quote_id) {
                await linkFieldQuotesToBooking(result.id, [proposalRow.field_quote_id], selectedBusiness?.id || '');
              }
            } catch (linkErr) {
              console.error('❌ Error linking field quote to mobile booking:', linkErr);
            }
          }

          // Link proposal payments to the new booking
          const { data: proposalPayments, error: paymentsError } = await supabase
            .from('payments')
            .select('*')
            .eq('proposal_id', linkedProposal.id);

          if (!paymentsError && proposalPayments && proposalPayments.length > 0) {
            for (const payment of proposalPayments) {
              const { error: updatePaymentError } = await supabase
                .from('payments')
                .update({ booking_id: result.id })
                .eq('id', payment.id);

              if (updatePaymentError) {
                console.error(`❌ Error linking payment ${payment.id} to booking:`, updatePaymentError);
              } else {
                console.log(`✅ Linked payment ${payment.id} to mobile booking ${result.id}`);
              }
            }
          }
        } catch (proposalError) {
          console.error('❌ Error updating proposal after mobile booking creation:', proposalError);
        }
      }

      // Reset all state before closing
      setTitle('Mobile Booking');
      setSelectedCustomer(null);
      setCurrentVehicle(null);
      setSelectedServices([]);
      setSelectedServiceAddons({});
      setConvertedProposal(null);
      setExistingProposals([]);
      setIsExistingProposalsModalOpen(false);
      setConvertingExistingProposal(false);
      setBookingAddress('');
      setPostalCode('');
      setSelectedResource(null);
      setScheduleData({
        dropOffDate: '',
        dropOffTime: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        customer_notes: ''
      });
      setError(null);
      
      // Reset workflow state
      setServicesWithWorkflows(new Set());
      setWorkflowEnabled(true);

      // Clear saved modal state
              // Removed persistence - no longer needed

      // Trigger automations for mobile booking creation
      try {
        console.log('🤖 MOBILE BOOKING: Triggering booking automations for created event...');
        
        // Build comprehensive booking data for automation (same structure as CreateBookingModal)
      const dropOffDateValue = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.dropOffDate : scheduleData.dropOffDate;
      const dropOffTimeValue = prefilledData?.isQuickBooking ? prefilledData.scheduleData?.dropOffTime : scheduleData.dropOffTime;

      const realBookingData = {
          booking_id: result.id, // ✅ Use UUID for automation system lookup
          booking_number: result.booking_number,
          business_id: selectedBusiness.id, // Add business_id for consistency
          title: title || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
          grand_total: selectedServices.reduce((total, service) => {
            const addons = selectedServiceAddons[service._uid] || [];
            const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
            const servicePrice = service.priceOption.price;
            const taxRate = service.service.tax_exempt ? 0 : (service.service.tax_rate || 5);
            const taxableAmount = servicePrice + addonTotal;
            const taxAmount = service.service.tax_exempt ? 0 : Math.round((taxableAmount * taxRate / 100) * 100) / 100;
            return total + taxableAmount + taxAmount;
          }, 0),
          
          // Customer information
          customer: {
            id: selectedCustomer.id,
            name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            full_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
            first_name: selectedCustomer.first_name,
            last_name: selectedCustomer.last_name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          },
          
          // Schedule information
          schedule: {
            start_date: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startDate : scheduleData.startDate,
            start_time: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.startTime : scheduleData.startTime,
            end_date: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endDate : scheduleData.endDate,
            end_time: prefilledData?.isQuickBooking ? prefilledData.scheduleData?.endTime : scheduleData.endTime,
            drop_off_date: bookingsByServiceAreaEnabled ? dropOffDateValue : null,
            drop_off_time: bookingsByServiceAreaEnabled ? dropOffTimeValue : null
          },
          
          // Services information
          services: selectedServices.map(service => ({
            id: service.service.id,
            name: service.service.name,
            price: service.priceOption.price,
            duration_minutes: service.priceOption.duration_minutes,
            vehicle: {
              id: service.vehicle.id,
              make: service.vehicle.make,
              model: service.vehicle.model,
              year: service.vehicle.year,
              color: service.vehicle.color
            },
            addons: selectedServiceAddons[service._uid] || []
          })),
          
          // Resource assigned
          resources: bookingsByServiceAreaEnabled
            ? (selectedResource ? [{
                id: selectedResource.resource_id,
                name: resourceNames[selectedResource.resource_id] || selectedResource.name || 'Mobile Technician',
                phone: '', // Mobile resource phone not available in this context
                role: 'mobile_technician'
              }] : [])
            : selectedResources.map(resource => ({
                id: resource.id,
                name: `${resource.first_name || ''} ${resource.last_name || ''}`.trim() || resource.email || 'Resource',
                phone: resource.phone || '',
                role: resource.role || 'mobile_technician'
              })),
          
          // Vehicle summary for easy access
          vehicles: (() => {
            const vehicles = selectedServices.map(service => ({
              make: service.vehicle.make || '',
              model: service.vehicle.model || '',
              year: service.vehicle.year || 0,
              color: service.vehicle.color || '',
              display: `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            }));
            
            // Remove duplicates based on make/model/year combination
            const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
              index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
            );
            
            return uniqueVehicles;
          })(),
          
          // Additional booking details
          notes: scheduleData.customer_notes || null,
          booking_address: bookingAddress || null,
          color: bookingColor,
          secondary_color: bookingSecondaryColor || null,
          created_at: new Date().toISOString(),
          status: 'scheduled',
          mobile_booking: true,
          source: isReschedule ? 'mobile_reschedule_action' : 'mobile_booking_creation'
        };

        console.log('📋 Mobile booking data:', realBookingData);
        
        // Trigger automation (non-blocking). Unscheduled bookings skip the
        // "created" trigger — it fires later when the booking gets a date.
        const eventType = isReschedule ? 'created' : 'created'; // Both use 'created' but source differs
        const logPrefix = isReschedule ? 'MOBILE RESCHEDULE' : 'MOBILE BOOKING';
        
        if (isUnscheduled) {
          console.log(`🤖 ${logPrefix}: Unscheduled booking — skipping created automations`);
        } else {
          triggerBookingAutomations(selectedBusiness.id, eventType, realBookingData)
            .then(() => console.log(`🤖 ${logPrefix}: Booking automations completed successfully`))
            .catch(automationError => console.error(`❌ ${logPrefix}: Error triggering booking automations:`, automationError));
          
          console.log(`🤖 ${logPrefix}: Booking automation trigger initiated`);
        }
      } catch (automationError) {
        console.error('❌ MOBILE BOOKING: Failed to trigger booking automations:', automationError);
        // We don't throw here to avoid blocking the booking creation flow
      }

      // Note: Persistence now handled centrally by usePageLifecycle
              console.log('🗑️ Mobile booking created successfully');

      // Call success callback and close modal
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (service: ExtendedService, priceOption: any) => {
    const vehicleToUse = currentVehicle || (isHomeServices ? buildHomeServicesVehicle(selectedCustomer?.id) : null);
    if (!vehicleToUse) return;

    const pricingType = priceOption.pricing_type || 'flat';
    const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';

    const newService = {
      _uid: crypto.randomUUID(),
      vehicle: vehicleToUse,
      service,
      priceOption: {
        ...priceOption,
        price: isPerUnit ? 0 : priceOption.price,
        duration_minutes: isPerUnit ? 0 : priceOption.duration_minutes,
        quantity: isPerUnit ? 0 : undefined,
      }
    };

    const newSelectedServices = [...selectedServices, newService];
    console.log('📱 Service added in CreateMobileBookingModal:', service.name);
    setSelectedServices(newSelectedServices);
    
    // Check if this service has a workflow template
    await checkServiceForWorkflow(service.id);
    
    // Save progress immediately with the new services array
    saveMobileBookingProgress({ selectedServices: newSelectedServices });
    
    // Check if service has add-ons, cache result, and only show modal if it does
    try {
      const addons = await getServiceAddons(service.id);
      const hasAddons = !!(addons && addons.length > 0);
      setServiceHasAddons(prev => ({ ...prev, [service.id]: hasAddons }));
      if (hasAddons) {
        setCurrentService(service);
        setCurrentServiceUid(newService._uid);
        setIsSelectAddonsModalOpen(true);
      }
    } catch (err) {
      console.error('Error checking service add-ons:', err);
    }
  };

  // Function to check if a service has a workflow template
  const checkServiceForWorkflow = async (serviceId: string) => {
    // Only check for workflows if the power-up is enabled
    if (!workflowsPowerUpEnabled) {
      return;
    }
    
    try {
      const workflow = await getWorkflowByServiceId(serviceId);
      if (workflow) {
        console.log('📋 Found workflow template for service:', serviceId, workflow.name);
        setServicesWithWorkflows(prev => new Set(prev.add(serviceId)));
      }
    } catch (error) {
      console.log('📋 No workflow template found for service:', serviceId);
    }
  };

  const handleAddAddons = (addons: ServiceAddon[]) => {
    if (!currentService || !currentServiceUid) return;
    
    const newAddons = {
      ...selectedServiceAddons,
      [currentServiceUid]: addons
    };
    
    console.log('📱 Add-ons added in CreateMobileBookingModal for service:', currentService.name);
    setSelectedServiceAddons(newAddons);
    
    // Save progress immediately with the new addons
    saveMobileBookingProgress({ selectedServiceAddons: newAddons });
  };

  const buildHomeServicesVehicle = (customerId?: string): CustomerVehicle => ({
    id: 'no-vehicle',
    customer_id: customerId || '',
    make: '',
    model: '',
    year: 0,
    vin: null,
    color: null,
    license_plate: null,
    mileage: null,
    notes: null,
    is_primary: false,
    last_service: null,
    created_at: '',
    updated_at: ''
  });

  // Group services by vehicle
  const servicesByVehicle = selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
    const vehicleId = service.vehicle?.id || 'no-vehicle';
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  const [vehicleIdToSwap, setVehicleIdToSwap] = useState<string | null>(null);

  const handleSelectVehicle = (vehicle: CustomerVehicle) => {
    if (vehicleIdToSwap) {
      setSelectedServices(prev => prev.map(s =>
        s.vehicle?.id === vehicleIdToSwap ? { ...s, vehicle } : s
      ));
      setVehicleIdToSwap(null);
      setIsSelectVehicleModalOpen(false);
      return;
    }

    const existingVehicleServices = selectedServices.find(s => s.vehicle.id === vehicle.id);
    const vehicleToSet = existingVehicleServices ? existingVehicleServices.vehicle : vehicle;
    
    setCurrentVehicle(vehicleToSet);
    setIsSelectServiceModalOpen(true);
    setIsSelectVehicleModalOpen(false);
    
    saveMobileBookingProgress({ currentVehicle: vehicleToSet });
  };

  const handleRemoveService = (uid: string) => {
    setSelectedServices(prev => prev.filter(s => s._uid !== uid));
    
    // Save progress after removing service
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  const handleRemoveAddon = (uid: string, addonId: string) => {
    setSelectedServiceAddons(prev => ({
      ...prev,
      [uid]: prev[uid]?.filter(addon => addon.id !== addonId) || []
    }));
    
    // Save progress after removing addon
    setTimeout(() => saveMobileBookingProgress(), 100);
  };

  const handleResourceAdd = (resource: any) => {
    if (!selectedResources.some(r => r.id === resource.id)) {
      const newResources = [...selectedResources, resource];
      setSelectedResources(newResources);
      saveMobileBookingProgress({ selectedResources: newResources });
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleResourceRemove = (resourceId: string) => {
    const newResources = selectedResources.filter(r => r.id !== resourceId);
    setSelectedResources(newResources);
    saveMobileBookingProgress({ selectedResources: newResources });
  };

  const handleAssetAdd = (asset: any) => {
    if (!selectedAssets.some(a => a.id === asset.id)) {
      setSelectedAssets(prev => [...prev, asset]);
    }
    setIsSelectResourceModalOpen(false);
  };

  const handleAssetRemove = (assetId: string) => {
    setSelectedAssets(prev => prev.filter(a => a.id !== assetId));
  };

  // Fetch mobile availability data
  const fetchMobileAvailability = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setAvailabilityLoading(true);
      setAvailabilityError(null);

      const { data, error } = await supabase
        .from('mobile_availability')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_available', true);

      if (error) throw error;

      let combinedAvailability = data || [];

      // Also fetch asset availability if assets are enabled
      if (assetsEnabled) {
        try {
          const { data: assetAvailData, error: assetAvailError } = await supabase
            .from('mobile_asset_availability')
            .select('id, asset_id, business_id, mobile_available, working_hours, postal_codes, created_at, updated_at')
            .eq('business_id', selectedBusiness.id)
            .eq('mobile_available', true);

          if (!assetAvailError && assetAvailData && assetAvailData.length > 0) {
            const assetEntries = assetAvailData.map(record => ({
              id: record.id,
              resource_id: record.asset_id,
              business_id: record.business_id,
              mobile_available: record.mobile_available,
              working_hours: record.working_hours,
              postal_codes: record.postal_codes || {},
              created_at: record.created_at,
              updated_at: record.updated_at,
              _isAsset: true,
            }));
            combinedAvailability = [...combinedAvailability, ...assetEntries];
            console.log('📦 Also fetched asset availability:', assetEntries.length);
          }
        } catch {
          console.error('Error fetching asset availability (non-critical)');
        }
      }

      console.log('Fetched mobile availability:', combinedAvailability.length);
      setMobileAvailability(combinedAvailability);
    } catch (err) {
      console.error('Error fetching mobile availability:', err);
      setAvailabilityError(err instanceof Error ? err.message : 'Failed to fetch mobile availability');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Fetch buffer time from online_booking_settings
  const fetchBufferTime = async () => {
    if (!supabase || !selectedBusiness?.id) return;
    
    try {
      setBufferTimeLoading(true);
      
      const { data: defPortal } = await supabase
        .from('online_booking_portals')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('is_default', true)
        .maybeSingle();
      if (!defPortal?.id) {
        setBufferTime(0);
        return;
      }

      const { data, error } = await supabase
        .from('online_booking_settings')
        .select('buffer_time')
        .eq('portal_id', defPortal.id)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, keeping default value
          console.log('No buffer time setting found, using 0 minutes');
          setBufferTime(0);
        } else {
          console.error('Error fetching buffer time:', error);
        }
        return;
      }
      
      if (data?.buffer_time !== undefined && data?.buffer_time !== null) {
        console.log(`Setting buffer time to ${data.buffer_time} minutes from database`);
        setBufferTime(data.buffer_time);
      } else {
        setBufferTime(0);
      }
    } catch (error) {
      console.error('Error fetching buffer time:', error);
    } finally {
      setBufferTimeLoading(false);
    }
  };

  // Fetch availability data when modal opens or assets setting changes
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchMobileAvailability();
      fetchBufferTime();
    }
  }, [isOpen, selectedBusiness?.id, assetsEnabled]);

  // Helper function to merge overlapping time ranges
  const mergeTimeRanges = (ranges: Array<{ startMinutes: number; endMinutes: number }>): Array<{ startMinutes: number; endMinutes: number }> => {
    if (ranges.length === 0) return [];
    
    // Sort by start time
    const sorted = [...ranges].sort((a, b) => a.startMinutes - b.startMinutes);
    const merged: Array<{ startMinutes: number; endMinutes: number }> = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];
      
      // If current overlaps with or touches last, merge them
      if (current.startMinutes <= last.endMinutes) {
        last.endMinutes = Math.max(last.endMinutes, current.endMinutes);
      } else {
        merged.push(current);
      }
    }
    
    return merged;
  };

  // Helper function to convert time range to 30-minute slots
  const timeRangeToSlots = (range: { startMinutes: number; endMinutes: number }): string[] => {
    const slots: string[] = [];
    
    // Round start down to nearest 30-minute mark
    const startSlot = Math.floor(range.startMinutes / 30) * 30;
    // Round end up to nearest 30-minute mark
    const endSlot = Math.ceil(range.endMinutes / 30) * 30;
    
    for (let minutes = startSlot; minutes < endSlot; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
    
    return slots;
  };

  // Helper function to split multi-day booking into per-day entries
  const splitMultiDayBooking = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): Array<{ date: string; startMinutes: number; endMinutes: number }> => {
    console.log(`🔪 splitMultiDayBooking called:`, { startDate, startTime, endDate, endTime });
    const entries: Array<{ date: string; startMinutes: number; endMinutes: number }> = [];
    
    if (startDate === endDate) {
      // Single-day booking
      console.log(`  → Single-day booking on ${startDate}`);
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      entries.push({
        date: startDate,
        startMinutes: startHour * 60 + startMinute,
        endMinutes: endHour * 60 + endMinute
      });
    } else {
      // Multi-day booking
      console.log(`  → Multi-day booking from ${startDate} to ${endDate}`);
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      console.log(`     Start Date object: ${start.toISOString()}`);
      console.log(`     End Date object: ${end.toISOString()}`);
      
      // Parse start and end times
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;
      
      // First day: from start_time to end of day
      console.log(`     Adding first day: ${startDate} (${startTimeMinutes} → 1440)`);
      entries.push({
        date: startDate,
        startMinutes: startTimeMinutes,
        endMinutes: 1440 // 24:00 (end of day)
      });
      
      // Middle days: entire day (if any)
      const current = new Date(start);
      current.setDate(current.getDate() + 1);
      console.log(`     Starting middle days loop from ${format(current, 'yyyy-MM-dd')}`);
      
      while (current < end) {
        const dateStr = format(current, 'yyyy-MM-dd');
        console.log(`       Adding middle day: ${dateStr} (0 → 1440)`);
        entries.push({
          date: dateStr,
          startMinutes: 0, // Start of day
          endMinutes: 1440 // End of day
        });
        current.setDate(current.getDate() + 1);
      }
      console.log(`     Ended middle days loop at ${format(current, 'yyyy-MM-dd')}`);
      
      // Last day: from start of day to end_time
      console.log(`     Adding last day: ${endDate} (0 → ${endTimeMinutes})`);
      entries.push({
        date: endDate,
        startMinutes: 0, // Start of day
        endMinutes: endTimeMinutes
      });
    }
    
    console.log(`  ✂️ Split result: ${entries.length} day entries`);
    entries.forEach(e => console.log(`     - ${e.date}: ${e.startMinutes} → ${e.endMinutes}`));
    
    return entries;
  };

  // Function to fetch blocked time slots
  const fetchBlockedTimeSlots = async () => {
    if (!supabase) {
      setBlockedSlotsError('Database connection not available');
      setBlockedSlotsLoading(false);
      return;
    }

    try {
      setBlockedSlotsLoading(true);
      setBlockedSlotsError(null);

      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const todayDateStr = format(now, 'yyyy-MM-dd');

      // Fetch mobile_bookings entries (resource-based mobile bookings only)
      const { data: mobileBookings, error: mobileError } = await supabase
        .from('mobile_bookings')
        .select('*')
        .eq('business_id', selectedBusiness?.id)
        .gte('start_date', todayDateStr)
        .order('start_date', { ascending: true });

      if (mobileError) throw mobileError;

      // Fetch all bookings for this business (both mobile and location)
      const { data: allBookingsData, error: allBookingsError } = await supabase
        .from('bookings')
        .select('id, start_date, start_time, end_date, end_time, status, mobile_booking, is_deleted, business_id')
        .eq('business_id', selectedBusiness?.id)
        .neq('is_deleted', true);

      if (allBookingsError) throw allBookingsError;

      // Include ALL bookings (both mobile and location) so asset-only mobile bookings
      // are properly blocked. Duplicate resource blocking from mobile_bookings is harmless
      // since ranges are merged.
      const regularBookingsData = (allBookingsData || []).filter((b: any) => {
        if (!b.start_date) return false;
        const endDate = b.end_date || b.start_date;
        return endDate >= todayDateStr;
      });

      // Now fetch resources and assets for these bookings
      let regularBookings: any[] = [];
      if (regularBookingsData && regularBookingsData.length > 0) {
        const bookingIds = regularBookingsData.map(b => b.id);
        
        const { data: bookingResources, error: resourcesError } = await supabase
          .from('booking_resources')
          .select('booking_id, resource_id, start_date, end_date')
          .in('booking_id', bookingIds);

        let bookingAssets: any[] = [];
        try {
          const { data: baData, error: baError } = await supabase
            .from('booking_assets')
            .select('booking_id, asset_id, start_date, end_date')
            .in('booking_id', bookingIds);
          if (!baError && baData) bookingAssets = baData;
        } catch { /* non-critical */ }
        
        if (resourcesError) {
          console.error('Error fetching booking resources:', resourcesError);
        } else {
          regularBookings = regularBookingsData.map(booking => ({
            ...booking,
            resources: bookingResources?.filter((br: any) => br.booking_id === booking.id) || [],
            assets: bookingAssets.filter((ba: any) => ba.booking_id === booking.id) || []
          }));
        }
      }

      // Fetch calendar tasks (they block time for assigned resources/assets)
      let calendarTasks: any[] = [];
      try {
        const { data: tasksData, error: tasksError } = await supabase
          .from('calendar_tasks')
          .select('id, start_date, start_time, end_date, end_time, status, is_deleted')
          .eq('business_id', selectedBusiness?.id)
          .eq('is_deleted', false)
          .neq('status', 'completed')
          .gte('end_date', todayDateStr);

        if (!tasksError && tasksData && tasksData.length > 0) {
          const taskIds = tasksData.map(t => t.id);

          const [taskResourcesRes, taskAssetsRes] = await Promise.all([
            supabase.from('calendar_task_resources').select('task_id, resource_id').in('task_id', taskIds),
            supabase.from('calendar_task_assets').select('task_id, asset_id').in('task_id', taskIds)
          ]);

          calendarTasks = tasksData.map(task => ({
            ...task,
            resources: taskResourcesRes.data?.filter((tr: any) => tr.task_id === task.id) || [],
            assets: taskAssetsRes.data?.filter((ta: any) => ta.task_id === task.id) || []
          }));
        }
      } catch { /* non-critical */ }

      // Process the bookings into blocked time slots
      const blockedRangesByResource: Record<string, Record<string, Array<{ startMinutes: number; endMinutes: number }>>> = {};

      // Process mobile bookings (resource and asset entries from mobile_bookings table)
      mobileBookings?.forEach(booking => {
        const { resource_id, asset_id, start_date, start_time, end_time } = booking;
        const ownerId = resource_id || asset_id;
        if (!ownerId) return;

        if (!blockedRangesByResource[ownerId]) {
          blockedRangesByResource[ownerId] = {};
        }
        if (!blockedRangesByResource[ownerId][start_date]) {
          blockedRangesByResource[ownerId][start_date] = [];
        }

        const [startHour, startMinute] = start_time.split(':').map(Number);
        const [endHour, endMinute] = end_time.split(':').map(Number);

        blockedRangesByResource[ownerId][start_date].push({
          startMinutes: startHour * 60 + startMinute,
          endMinutes: endHour * 60 + endMinute
        });
      });

      // Process all bookings (both mobile and location) into blocked time slots
      regularBookings?.forEach((booking) => {
        if (!booking.start_date || !booking.start_time) return;
        
        const endDate = booking.end_date || booking.start_date;
        const endTime = booking.end_time || booking.start_time;

        const dayEntries = splitMultiDayBooking(
          booking.start_date,
          booking.start_time,
          endDate,
          endTime
        );

        const mobileResourceIds = mobileAvailability.map(ma => ma.resource_id);

        // Block time for assigned resources
        const resources = booking.resources || [];
        resources.forEach((resourceAssignment: any) => {
          const resourceId = resourceAssignment.resource_id;
          if (!resourceId || !mobileResourceIds.includes(resourceId)) return;

          if (!blockedRangesByResource[resourceId]) {
            blockedRangesByResource[resourceId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[resourceId][entry.date]) {
              blockedRangesByResource[resourceId][entry.date] = [];
            }
            blockedRangesByResource[resourceId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });

        // Block time for assigned assets using the booking's day entries
        const assetAssignments = booking.assets || [];
        assetAssignments.forEach((ba: any) => {
          const assetId = ba.asset_id;
          if (!assetId) return;

          if (!blockedRangesByResource[assetId]) {
            blockedRangesByResource[assetId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[assetId][entry.date]) {
              blockedRangesByResource[assetId][entry.date] = [];
            }
            blockedRangesByResource[assetId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });
      });

      // Process calendar tasks into blocked time slots
      calendarTasks.forEach((task: any) => {
        if (!task.start_date || !task.start_time) return;

        const endDate = task.end_date || task.start_date;
        const endTime = task.end_time || task.start_time;

        const dayEntries = splitMultiDayBooking(
          task.start_date,
          task.start_time,
          endDate,
          endTime
        );

        // Block time for assigned resources
        (task.resources || []).forEach((tr: any) => {
          const resourceId = tr.resource_id;
          if (!resourceId) return;

          if (!blockedRangesByResource[resourceId]) {
            blockedRangesByResource[resourceId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[resourceId][entry.date]) {
              blockedRangesByResource[resourceId][entry.date] = [];
            }
            blockedRangesByResource[resourceId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });

        // Block time for assigned assets
        (task.assets || []).forEach((ta: any) => {
          const assetId = ta.asset_id;
          if (!assetId) return;

          if (!blockedRangesByResource[assetId]) {
            blockedRangesByResource[assetId] = {};
          }

          dayEntries.forEach(entry => {
            if (!blockedRangesByResource[assetId][entry.date]) {
              blockedRangesByResource[assetId][entry.date] = [];
            }
            blockedRangesByResource[assetId][entry.date].push({
              startMinutes: entry.startMinutes,
              endMinutes: entry.endMinutes
            });
          });
        });
      });

      // Merge overlapping ranges and convert to blocked slots
      const blocked: BlockedTimeSlots = {};

      Object.keys(blockedRangesByResource).forEach(resourceId => {
        blocked[resourceId] = [];
        
        Object.keys(blockedRangesByResource[resourceId]).forEach(date => {
          const ranges = blockedRangesByResource[resourceId][date];
          
          // Merge overlapping ranges
          const mergedRanges = mergeTimeRanges(ranges);
          
          // Convert merged ranges to 30-minute slots
          const allSlots: string[] = [];
          mergedRanges.forEach(range => {
            const slots = timeRangeToSlots(range);
            slots.forEach(slot => {
              if (!allSlots.includes(slot)) {
                allSlots.push(slot);
          }
            });
          });
          
          // Sort slots
          allSlots.sort();
          
          blocked[resourceId].push({
            date,
            blockedSlots: allSlots
          });
        });
        
        // Sort by date
        blocked[resourceId].sort((a, b) => a.date.localeCompare(b.date));
      });

      setBlockedTimeSlots(blocked);
    } catch (error) {
      console.error('Error fetching blocked time slots:', error);
      setBlockedSlotsError(error instanceof Error ? error.message : 'Error fetching blocked time slots');
    } finally {
      setBlockedSlotsLoading(false);
    }
  };

  // Fetch blocked time slots when modal opens or business changes
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      fetchBlockedTimeSlots();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Update the isTimeSlotAvailable function to be more efficient
  const isTimeSlotAvailable = (date: Date, timeString: string) => {
    // If we're still loading blocked slots, return false
    if (blockedSlotsLoading) return false;

    // Check if this time slot would result in a multi-day booking
    const startDateTime = new Date(`${format(date, 'yyyy-MM-dd')}T${timeString}`);
    const endDateTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60000);
    const startDate = format(startDateTime, 'yyyy-MM-dd');
    const endDate = format(endDateTime, 'yyyy-MM-dd');
    
    // Prevent multi-day bookings for mobile bookings
    if (startDate !== endDate) {
      return false;
    }

    // Get available resources for this time slot
    const availableResources = getAvailableResourcesForTimeSlot(date, timeString);
    
    // If there are no available resources, the time slot is not available
    return availableResources.length > 0;
  };

  // Update the isDayAvailable function with better debugging for reschedule mode
  const isDayAvailable = (date: Date) => {
    // For non-reschedule mode, require postal code to be set
    if (!isReschedule && (!postalCode || postalCode.trim() === '')) {
      return false;
    }
    
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dayOfWeek = format(localDate, 'EEEE').toLowerCase();
    
    // Debug logging for reschedule mode
    if (isReschedule) {
      console.log('🔍 Checking day availability for reschedule:', {
        date: format(date, 'yyyy-MM-dd'),
        dayOfWeek,
        postalCode,
        mobileAvailabilityCount: mobileAvailability.length
      });
    }
    
    // For reschedule mode, first try normal postal code checking
    let hasAvailableResource = mobileAvailability.some(resource => {
      try {
        // Only check postal code availability if a postal code is provided
        if (postalCode && postalCode.trim() !== '') {
          let postalCodes = resource.postal_codes;
          if (typeof postalCodes === 'string') {
            try {
              postalCodes = JSON.parse(postalCodes);
            } catch (err) {
              if (isReschedule) console.log('❌ Failed to parse postal codes for resource:', resource.id);
              return false;
            }
          }
          
          const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
          const enteredFSA = postalCode.toUpperCase().replace(/\s+/g, '').substring(0, 3);
          
          if (isReschedule) {
            console.log('📮 Postal code check:', {
              resourceId: resource.id,
              dayOfWeek,
              enteredFSA,
              availablePostalCodes,
              availablePostalCodesCount: availablePostalCodes.length,
              samplePostalCodes: availablePostalCodes.slice(0, 5)
            });
          }
          
          const matchesFSA = availablePostalCodes.some((pc: string) => {
            const pcFSA = pc.toUpperCase().replace(/\s+/g, '').substring(0, 3);
            return pcFSA === enteredFSA;
          });
          
          if (!matchesFSA) {
            return false;
          }
        }
        
        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          try {
            workingHours = JSON.parse(workingHours);
          } catch (err) {
            if (isReschedule) console.log('❌ Failed to parse working hours for resource:', resource.id);
            return false;
          }
        }
        
        const daySchedule = workingHours?.[dayOfWeek];
        const hasWorkingHours = daySchedule?.enabled && daySchedule?.shifts?.length > 0;
        
        if (isReschedule) {
          console.log('⏰ Working hours check:', {
            resourceId: resource.id,
            dayOfWeek,
            hasSchedule: !!daySchedule,
            enabled: daySchedule?.enabled,
            shiftsCount: daySchedule?.shifts?.length || 0,
            hasWorkingHours
          });
        }

        return hasWorkingHours;
      } catch (err) {
        console.error('Error checking day availability:', err);
        return false;
      }
    });
    
    // Fallback for reschedule mode: if no resources match the postal code,
    // check if any resources have working hours (ignore postal code restriction)
    if (!hasAvailableResource && isReschedule && postalCode && postalCode.trim() !== '') {
      console.log('🔄 Reschedule fallback: Checking availability without postal code restriction');
      
      hasAvailableResource = mobileAvailability.some(resource => {
        try {
          // Check working hours only (skip postal code check)
          let workingHours = resource.working_hours;
          if (typeof workingHours === 'string') {
            try {
              workingHours = JSON.parse(workingHours);
            } catch (err) {
              return false;
            }
          }
          
          const daySchedule = workingHours?.[dayOfWeek];
          const hasWorkingHours = daySchedule?.enabled && daySchedule?.shifts?.length > 0;
          
          if (hasWorkingHours && isReschedule) {
            console.log('✅ Reschedule fallback: Found resource with working hours:', {
              resourceId: resource.id,
              dayOfWeek,
              shiftsCount: daySchedule?.shifts?.length || 0
            });
          }

          return hasWorkingHours;
        } catch (err) {
          console.error('Error in reschedule fallback check:', err);
          return false;
        }
      });
      
      if (hasAvailableResource) {
        console.log('✅ Reschedule fallback successful: Using working hours only');
      }
    }
    
    if (isReschedule) {
      console.log('✅ Final day availability result:', {
        date: format(date, 'yyyy-MM-dd'),
        hasAvailableResource,
        usedFallback: !hasAvailableResource && isReschedule
      });
    }
    
    return hasAvailableResource;
  };

  // Update the hasTimePassed function to remove verbose logging
  const hasTimePassed = (date: Date | string, timeString: string): boolean => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const checkDate = typeof date === 'string' ? new Date(date + 'T12:00:00Z') : new Date(date);

    // Compare dates in UTC to avoid timezone issues
    const nowUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const checkUTC = Date.UTC(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());

    // For future dates, only check if the time is within business hours (9am-5pm)
    if (checkUTC > nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      return slotMinutes < (9 * 60) || slotMinutes > (17 * 60);
    }

    // For today, check if the time has passed
    if (checkUTC === nowUTC) {
      const slotMinutes = hours * 60 + minutes;
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
      return currentTimeMinutes >= slotMinutes;
    }

    // Past dates are always considered passed
    return true;
  };

  // Update the handleDateSelect function with better logging
  const handleDateSelect = async (day: number) => {
    try {
      // Create date at noon UTC to avoid any timezone issues
      const selectedDate = new Date(Date.UTC(
        currentMonth.getUTCFullYear(),
        currentMonth.getUTCMonth(),
        day,
        12, // Set to noon UTC
        0,
        0
      ));
      
      // Format date as YYYY-MM-DD using UTC date
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Fetch raw booking data for this date to show in logs
      if (supabase && selectedBusiness?.id) {
        console.log('\n' + '🔎 FETCHING RAW BOOKING DATA FOR ' + formattedDate);
        const { data: rawBookings, error } = await supabase
          .from('bookings')
          .select('id, start_date, start_time, end_date, end_time, status, mobile_booking, is_deleted')
          .eq('business_id', selectedBusiness.id)
          .or(`start_date.eq.${formattedDate},end_date.eq.${formattedDate}`)
          .limit(10);
        
        if (!error && rawBookings && rawBookings.length > 0) {
          console.log(`📋 Found ${rawBookings.length} bookings that START or END on ${formattedDate}:`);
          rawBookings.forEach((b: any) => {
            console.log(`  - ${b.id}:`);
            console.log(`    ${b.start_date} ${b.start_time} → ${b.end_date || b.start_date} ${b.end_time}`);
            console.log(`    mobile_booking: ${b.mobile_booking}, is_deleted: ${b.is_deleted}, status: ${b.status}`);
          });
        } else if (!error) {
          console.log(`❌ No bookings found that START or END on ${formattedDate}`);
        }
        console.log('');
      }
      
      // ==========================================
      // DEBUG: DATE SELECTED - SHOW ALL RELEVANT DATA
      // ==========================================
      console.log('\n' + '█'.repeat(80));
      console.log('📅 DATE SELECTED ON CALENDAR PICKER: ' + formattedDate);
      console.log('█'.repeat(80));
      console.log('Selected Date (ISO):', selectedDate.toISOString());
      console.log('Selected Date (local string):', formattedDate);
      console.log('');
      
      // Show what we have in state
      console.log('📊 CURRENT STATE:');
      console.log('  Mobile-available resources:', mobileAvailability.length);
      mobileAvailability.forEach(ma => {
        console.log(`    - ${ma.resource_id} (ID: ${ma.id})`);
      });
      console.log('');
      
      // Show blocked time slots summary
      console.log('🔒 BLOCKED TIME SLOTS IN MEMORY:');
      if (blockedTimeSlots && Object.keys(blockedTimeSlots).length > 0) {
        Object.keys(blockedTimeSlots).forEach(resourceId => {
          const dates = blockedTimeSlots[resourceId] || [];
          console.log(`  Resource ${resourceId}:`);
          dates.forEach(dateEntry => {
            const isSelectedDate = dateEntry.date === formattedDate;
            console.log(`    ${isSelectedDate ? '👉' : '  '} ${dateEntry.date}: ${dateEntry.blockedSlots.length} blocked slots`);
            if (isSelectedDate && dateEntry.blockedSlots.length > 0) {
              console.log(`       Slots: ${dateEntry.blockedSlots.slice(0, 10).join(', ')}${dateEntry.blockedSlots.length > 10 ? '...' : ''}`);
            }
          });
        });
      } else {
        console.log('  No blocked slots in memory');
      }
      console.log('');
      
      // Check if we have blocked time slots loaded and if any bookings exist for this date
      if (blockedTimeSlots) {
        const resourceIds = Object.keys(blockedTimeSlots);
        console.log(`🔍 CHECKING ${resourceIds.length} RESOURCES FOR BLOCKED SLOTS ON ${formattedDate}:`);
        console.log('');
        
        let bookingsFoundForDate = false;
        
        resourceIds.forEach(resourceId => {
          const allDatesForResource = blockedTimeSlots[resourceId] || [];
          const dateEntry = allDatesForResource.find(entry => entry.date === formattedDate);
          
          if (dateEntry && dateEntry.blockedSlots.length > 0) {
            bookingsFoundForDate = true;
            console.log(`✅ Resource ${resourceId}:`);
            console.log(`   Blocked slots on ${formattedDate}: ${dateEntry.blockedSlots.length}`);
            console.log(`   Time slots: ${dateEntry.blockedSlots.slice(0, 15).join(', ')}${dateEntry.blockedSlots.length > 15 ? '...' : ''}`);
            console.log('');
          } else {
            console.log(`➖ Resource ${resourceId}: No blocked slots on ${formattedDate}`);
            if (allDatesForResource.length > 0) {
              console.log(`   (Has blocked slots on other dates: ${allDatesForResource.map(d => d.date).join(', ')})`);
            }
            console.log('');
          }
        });
        
        if (!bookingsFoundForDate) {
          console.log('');
          console.log('❌❌❌ NO BLOCKED SLOTS FOUND FOR ' + formattedDate + ' ❌❌❌');
          console.log('This means one of:');
          console.log('  1. No mobile bookings exist for this date');
          console.log('  2. No regular bookings exist for this date');
          console.log('  3. Regular bookings exist but have no mobile-available resources assigned');
          console.log('  4. The dates were processed incorrectly (timezone issue)');
          console.log('');
          console.log('💡 TO DEBUG: Scroll up to find "🔪 splitMultiDayBooking" logs');
          console.log('   These show exactly what dates were created for each booking');
          console.log('');
        }
      } else {
        console.log('\n⚠️ Blocked time slots not yet loaded');
      }
      console.log('█'.repeat(80) + '\n');
      // ==========================================

      // Reset all time-related fields when selecting a new date
      setScheduleData(prev => ({
        ...prev,
        dropOffDate: formattedDate,
        startDate: formattedDate,
        endDate: formattedDate,
        // Reset time fields
        dropOffTime: '',
        startTime: '',
        endTime: ''
      }));

      // Also reset the selected resource since it's tied to a specific time slot
      setSelectedResource(null);
    } catch (err) {
      console.error('Error selecting date:', err);
      setError('Failed to select date. Please try again.');
    }
  };

  // Update the renderCalendar function to check postal code for day availability
  const renderCalendar = () => {
    const days = getDaysInMonthArray(currentMonth);
    const dayHeaders = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();

    // Toggle to create the booking without a date (unscheduled "limbo" state).
    const unscheduledToggle = (
      <button
        type="button"
        onClick={() => {
          setIsUnscheduled((prev) => {
            const next = !prev;
            if (next) {
              setScheduleData((sd) => ({
                ...sd,
                dropOffDate: '',
                dropOffTime: '',
                startDate: '',
                startTime: '',
                endDate: '',
                endTime: '',
              }));
            }
            return next;
          });
        }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left active:scale-[0.98] touch-manipulation mb-4 ${
          isUnscheduled
            ? 'border-blue-500 bg-blue-50 text-blue-700'
            : 'border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
        }`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isUnscheduled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
          <CalendarClock className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">
            {isUnscheduled ? 'Unscheduled — no date set' : 'Set as unscheduled'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {isUnscheduled
              ? 'This booking will be saved without a date.'
              : "Don't have a date yet? Save it to the unscheduled list."}
          </div>
        </div>
        <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ${isUnscheduled ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
          {isUnscheduled ? <Check className="h-3 w-3 text-white" /> : null}
        </div>
      </button>
    );

    if (isUnscheduled) {
      return (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700 flex items-center">
              <Clock className="h-4 w-4 mr-2 text-blue-600" />
              Schedule
            </h3>
          </div>
          {unscheduledToggle}
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-blue-600" />
            Schedule
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            >
              <ChevronDown className="w-4 h-4 transform rotate-90 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            >
              <ChevronDown className="w-4 h-4 transform -rotate-90 text-gray-600" />
            </button>
          </div>
        </div>

        {unscheduledToggle}
        
        {/* Show message if booking address is missing */}
        {(!bookingAddress || bookingAddress.trim() === '') && !isReschedule && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-700 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2" />
              Please add a booking address to see available dates
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-7 gap-1 mb-3">
          {dayHeaders.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-blue-600 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            if (!day) return <div key={index} className="aspect-square" />;
            
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateStr = format(date, 'yyyy-MM-dd');
            const isSelected = dateStr === scheduleData.startDate;
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const hasAvailability = isDayAvailable(date);

            return (
              <button
                key={index}
                type="button"
                disabled={isPast || !hasAvailability}
                onClick={() => !isPast && hasAvailability && handleDateSelect(day)}
                className={`
                  aspect-square rounded-lg text-center relative transition-colors font-medium text-sm border
                  ${isPast || !hasAvailability 
                    ? 'cursor-not-allowed opacity-50 bg-gray-100 border-gray-200 text-gray-400' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                  }
                  ${isSelected 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : ''
                  }
                  ${date.getDate() === today.getDate() && 
                    date.getMonth() === today.getMonth() && 
                    date.getFullYear() === today.getFullYear() 
                    ? 'font-bold ring-2 ring-blue-200' 
                    : ''}
                `}
                style={{ 
                  touchAction: 'manipulation',
                  minHeight: '44px'
                }}
              >
                {day}
                {hasAvailability && !isPast && !isSelected && (
                  <div 
                    className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full bg-blue-500"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Time slots — always visible */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Clock className="h-4 w-4 mr-2 text-blue-600" />
              Available Times
            </h4>
            {!scheduleData.startDate ? (
              <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                Select a date above to see available times
              </div>
            ) : !isReschedule && (!postalCode || postalCode.trim() === '') ? (
              <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                Please set the booking address above to see available time slots. The postal code will be automatically extracted from the address.
              </div>
            ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {generateTimeSlots().map((time) => {
                const date = new Date(scheduleData.startDate);
                const isAvailable = isTimeSlotAvailable(date, time);
                const availableResources = isAvailable ? getAvailableResourcesForTimeSlot(date, time) : [];
                const isSelected = scheduleData.startTime === time;
                
                return (
                  <button
                    key={time}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => {
                      if (isAvailable) {
                        handleTimeSlotSelect(time);
                      }
                    }}
                    className={`
                      px-3 py-2 text-sm font-medium rounded-lg text-center transition-colors border
                      ${isSelected 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : isAvailable 
                          ? 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300' 
                          : 'bg-gray-100 text-gray-400 border-gray-200'
                      }
                      ${!isAvailable ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    style={{ 
                      touchAction: 'manipulation',
                      minHeight: '44px'
                    }}
                  >
                    <div className="font-bold">
                      {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                    </div>
                    {isAvailable && availableResources.length > 1 && (
                      <div className={`text-xs mt-0.5 ${isSelected ? 'text-purple-200' : 'text-purple-600'}`}>
                        {availableResources.length} available
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            )}
                  </div>

      {/* Customer Source Selection Modal */}
      {isCustomerSourceSelectionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: baseZ + 2, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gray-800 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Select Customer Source</h3>
                    <p className="text-sm text-gray-300">How did this customer find you?</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCustomerSourceSelectionModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {/* Existing Customer Sources */}
                {customerSources.map((source) => (
                  <button
                    key={source.id}
                    onClick={async () => {
                      setSelectedCustomerSource(source);
                      setIsCustomerSourceSelectionModalOpen(false);
                      // Reset referral source when source changes (unless it's referral)
                      const sourceNameLower = source.name?.toLowerCase() || '';
                      if (!sourceNameLower.includes('refer')) {
                        setReferralSourceCustomer(null);
                      } else {
                        // If referral is selected, reload referral program status to ensure it's up to date
                        await loadReferralProgramStatus();
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-200"
                  >
                    <span className="font-medium text-gray-900">{source.name}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}

                {/* Add New Source Option */}
                <button
                  onClick={() => {
                    setIsCustomerSourceSelectionModalOpen(false);
                    setIsCreateSourceModalOpen(true);
                  }}
                  className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all duration-200"
                >
                  <Plus className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="font-medium text-purple-600">Add New Source</span>
                </button>
              </div>

              {/* Referral Source - Only show when: new customer + source=referral + program active */}
              {(() => {
                const hasCustomer = !!selectedCustomer;
                // Check if customer is new: repeat is false, null, or undefined (not explicitly true)
                const isNewCustomer = selectedCustomer?.repeat !== true;
                // Check if source name contains "refer" (case-insensitive) to handle variations like "referral", "referal", etc.
                const sourceNameLower = selectedCustomerSource?.name?.toLowerCase() || '';
                const sourceIsReferral = sourceNameLower.includes('refer');
                const shouldShow = hasCustomer && isNewCustomer && sourceIsReferral && referralProgramActive;
                
                return shouldShow;
              })() && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Referral Source
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomerSourceSelectionModalOpen(false);
                      setIsSelectReferralSourceModalOpen(true);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm bg-white rounded-xl hover:bg-gray-50 focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                      referralSourceCustomer 
                        ? 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-2 border-dashed border-blue-300 bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span className={referralSourceCustomer ? 'text-gray-700' : 'text-blue-600 font-medium'}>
                      {referralSourceCustomer 
                        ? `${referralSourceCustomer.first_name} ${referralSourceCustomer.last_name}`
                        : 'Select referring customer...'}
                    </span>
                    <Users className={`h-4 w-4 ${referralSourceCustomer ? 'text-gray-400' : 'text-blue-400'}`} />
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setIsCustomerSourceSelectionModalOpen(false)}
                className="w-full px-4 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  // Update the fetchResourceNames function to directly query resources and assets tables
  const fetchResourceNames = async (resourceIds: string[]) => {
    if (!supabase || !resourceIds.length) return;

    try {
      const resourceEntries = mobileAvailability.filter(ma => resourceIds.includes(ma.id) && !(ma as any)._isAsset);
      const assetEntries = mobileAvailability.filter(ma => resourceIds.includes(ma.id) && (ma as any)._isAsset);

      const resourceIdsToFetch = resourceEntries.map(ma => ma.resource_id);
      const assetIdsToFetch = assetEntries.map(ma => ma.resource_id);

      let resourceData: any[] = [];
      let assetData: any[] = [];

      if (resourceIdsToFetch.length > 0) {
        const { data, error } = await supabase
          .from('resources')
          .select('id, first_name, last_name')
          .in('id', resourceIdsToFetch);
        if (!error && data) resourceData = data;
      }

      if (assetIdsToFetch.length > 0) {
        const { data, error } = await supabase
          .from('assets')
          .select('id, name')
          .in('id', assetIdsToFetch);
        if (!error && data) assetData = data;
      }
      
      const names = mobileAvailability.reduce((acc, ma) => {
        if ((ma as any)._isAsset) {
          const asset = assetData.find(a => a.id === ma.resource_id);
          return { ...acc, [ma.id]: asset?.name || `Asset ${ma.resource_id.slice(0, 6)}` };
        }
        const resource = resourceData.find(r => r.id === ma.resource_id);
        if (resource) {
          return { ...acc, [ma.id]: `${resource.first_name} ${resource.last_name}` };
        }
        return { ...acc, [ma.id]: `Resource ${ma.resource_id}` };
      }, {} as Record<string, string>);

      setResourceNames(names);
    } catch (err) {
      console.error('Error fetching resource names:', err);
    }
  };

  // Update useEffect to fetch resource names when mobileAvailability changes
  useEffect(() => {
    if (mobileAvailability.length > 0) {
      const resourceIds = mobileAvailability.map(r => r.id);
      fetchResourceNames(resourceIds);
    }
  }, [mobileAvailability]);

  // Update the handleTimeSlotSelect function to separate buffer time from displayed end time
  const handleTimeSlotSelect = (time: string) => {
    console.log('Time slot selected:', {
      time,
      currentScheduleData: scheduleData,
      hasStartDate: !!scheduleData.startDate,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (!scheduleData.startDate) {
      console.error('No start date set when selecting time slot');
      return;
    }

    const date = new Date(scheduleData.startDate);
    const resources = getAvailableResourcesForTimeSlot(date, time);
    
    console.log('Available resources for time slot:', {
      time,
      date: date.toISOString(),
      resourceCount: resources.length,
      resources: resources.map(r => r.id)
    });

    if (resources.length === 0) {
      console.error('No available resources for selected time slot');
      return;
    }

    // Create start date/time
    const [hours, minutes] = time.split(':').map(Number);
    const startDateTime = new Date(scheduleData.startDate + 'T' + time);
    
    // Calculate end date/time by adding only the service duration (not buffer time)
    // Buffer time is used for availability but is not shown to the customer
    const endDateTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60000);
    
    // Format end date and time separately
    const endDate = format(endDateTime, 'yyyy-MM-dd');
    const endTime = format(endDateTime, 'HH:mm');

    // Prevent multi-day bookings for mobile bookings
    if (endDate !== scheduleData.startDate) {
      setError('Mobile bookings cannot span multiple days. Please select a shorter service duration or an earlier start time.');
      return;
    }

    console.log('Setting schedule data:', {
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      startDate: scheduleData.startDate,
      startTime: time,
      endDate,
      endTime,
      serviceDuration: totalDurationMinutes,
      bufferTime,
      totalWithBuffer: totalDurationMinutes + bufferTime
    });

    if (resources.length === 1) {
      // Auto-select the only available resource
      console.log('📱 Auto-selecting single available resource in CreateMobileBookingModal:', {
        resource: resources[0],
        resourceId: resources[0].resource_id,
        resourceName: resourceNames[resources[0].id]
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setSelectedResource(resources[0]);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with both schedule and resource data
      saveMobileBookingProgress({ 
        scheduleData: newScheduleData,
        selectedResource: resources[0]
      });
    } else {
      // Show resource selector for multiple available resources
      console.log('📱 Multiple resources available in CreateMobileBookingModal:', {
        count: resources.length,
        resources: resources.map(r => ({
          id: r.id,
          resourceId: r.resource_id,
          name: resourceNames[r.id]
        }))
      });
      
      const newScheduleData = {
        ...scheduleData,
        dropOffTime: time,
        startTime: time,
        endDate: endDate,
        endTime: endTime
      };
      
      setAvailableResources(resources);
      setIsResourceSelectorOpen(true);
      setScheduleData(newScheduleData);
      
      // Save progress immediately with schedule data
      saveMobileBookingProgress({ scheduleData: newScheduleData });
    }
  };

  // Add ResourceSelector modal component
  const ResourceSelector = () => {
    if (!isResourceSelectorOpen) return null;

    const hasUnloadedNames = availableResources.some(r => !resourceNames[r.id]);

    const handleResourceSelect = (resource: Resource) => {
      console.log('📱 Resource selected in CreateMobileBookingModal:', {
        resource,
        resourceId: resource.resource_id,
        resourceName: resourceNames[resource.id]
      });
      setSelectedResource(resource);
      setIsResourceSelectorOpen(false);
      
      // Save progress immediately with the new resource
      saveMobileBookingProgress({ selectedResource: resource });
    };

    return (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: baseZ + 3, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Select Resource</h3>
          <div className="space-y-2">
            {hasUnloadedNames ? (
              <div className="text-sm text-gray-600">Loading resources...</div>
            ) : (
              availableResources.map(resource => (
                <button
                  key={resource.id}
                  onClick={() => handleResourceSelect(resource)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {resourceNames[resource.id] || resource.name}
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => setIsResourceSelectorOpen(false)}
            className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Update the payment summary section to show end time with buffer
  const renderPaymentSummary = () => (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
        <DollarSign className="h-4 w-4 mr-2 text-blue-600" />
        Payment Summary
      </h3>
      
      {scheduleData?.startDate && scheduleData?.startTime && (
        <div className="mb-4 space-y-2 pb-4 border-b border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Date: <span className="text-blue-700 font-semibold">{format(new Date(scheduleData.startDate + 'T12:00:00Z'), 'EEEE, MMMM d, yyyy')}</span>
            </div>
            <div className="text-sm font-medium text-gray-700">
              Start Time: <span className="text-blue-700 font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`), 'h:mm a')}</span>
            </div>
            <div className="text-sm font-medium text-gray-700">
              End Time: <span className="text-blue-700 font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + totalDurationMinutes * 60000, 'h:mm a')}</span>
            </div>
            <div className="text-sm font-medium text-blue-600 bg-blue-50 rounded-lg p-2 border border-blue-200">
              End Time with Buffer: <span className="font-semibold">{format(new Date(`2000-01-01T${scheduleData.startTime}`).getTime() + (totalDurationMinutes + bufferTime) * 60000, 'h:mm a')}</span>
            </div>
            {bookingsByServiceAreaEnabled ? (
              selectedResource && (
                <div className="text-sm font-medium text-gray-700">
                  {(selectedResource as any)?._isAsset ? 'Asset' : 'Resource'}: <span className={`font-semibold ${(selectedResource as any)?._isAsset ? 'text-orange-700' : 'text-blue-700'}`}>{resourceNames[selectedResource.id] || selectedResource.name}</span>
                </div>
              )
            ) : (
              (selectedResources.length > 0 || selectedAssets.length > 0) && (
                <div className="text-sm font-medium text-gray-700">
                  {selectedResources.length > 0 && (
                    <div>Resources:{' '}
                      <span className="text-blue-700 font-semibold">
                        {selectedResources.map(resource => `${resource.first_name || ''} ${resource.last_name || ''}`.trim() || resource.email || 'Resource').join(', ')}
                      </span>
                    </div>
                  )}
                  {selectedAssets.length > 0 && (
                    <div>Assets:{' '}
                      <span className="text-orange-700 font-semibold">
                        {selectedAssets.map(asset => asset.name).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {!prefilledData?.isQuickBooking && (
        <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-700 flex-1">Service Duration</span>
            <span className="text-sm font-semibold text-blue-700">
              {totalDurationMinutes >= 1440 
                ? `${Math.floor(totalDurationMinutes / 1440)} days` 
                : totalDurationMinutes >= 60
                  ? `${Math.floor(totalDurationMinutes / 60)} hours ${totalDurationMinutes % 60 > 0 ? `${totalDurationMinutes % 60} mins` : ''}`
                  : `${totalDurationMinutes} mins`}
            </span>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="h-4 w-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-700 flex-1">Total Duration with Buffer Time</span>
            <span className="text-sm font-semibold text-blue-800">
              {(totalDurationMinutes + bufferTime) >= 1440 
                ? `${Math.floor((totalDurationMinutes + bufferTime) / 1440)} days` 
                : (totalDurationMinutes + bufferTime) >= 60
                  ? `${Math.floor((totalDurationMinutes + bufferTime) / 60)} hours ${(totalDurationMinutes + bufferTime) % 60 > 0 ? `${(totalDurationMinutes + bufferTime) % 60} mins` : ''}`
                  : `${totalDurationMinutes + bufferTime} mins`}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {/* Service Breakdown */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
          {selectedServices.map((item) => {
            const addons = selectedServiceAddons[item._uid] || [];
            const isPerUnitOrSqft = item.priceOption.pricing_type === 'per_unit' || item.priceOption.pricing_type === 'per_sqft';
            return (
              <div key={item._uid}>
                <div className="text-sm text-gray-600 flex items-center">
                  <div className="h-2 w-2 bg-blue-500 rounded-full mr-2 flex-shrink-0"></div>
                  <span className="font-medium flex-1">{item.service.name} {addons.length > 0 ? `(+ ${addons.length} add-ons)` : ''}</span>
                  <span className="font-semibold text-gray-900 ml-2">${(item.priceOption.price || 0).toFixed(2)}</span>
                </div>
                {isPerUnitOrSqft && (item.priceOption.quantity || 0) > 0 && (
                  <div className="ml-4 mt-0.5 text-[11px] text-gray-400">
                    {(item.priceOption.quantity || 0).toLocaleString()} {item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'units'} × ${(item.priceOption.unit_price || 0).toFixed(2)}/{item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg p-3 border border-gray-200 space-y-2">
          <div className="flex justify-between text-sm text-gray-700">
            <span className="font-medium">Subtotal</span>
            <span className="font-semibold text-gray-900">${(selectedServices.reduce((sum, s) => {
              let total = sum + s.priceOption.price;
              const addons = selectedServiceAddons[s._uid] || [];
              total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
              return total;
            }, 0)).toLocaleString()}</span>
          </div>

          {(() => {
            const svcSubs = selectedServices.map(s => {
              let total = s.priceOption.price;
              const addons = selectedServiceAddons[s._uid] || [];
              total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
              return { serviceId: s.service.id, total, taxExempt: s.service.tax_exempt };
            });
            const grossSubtotal = svcSubs.reduce((sum, s) => sum + s.total, 0);
            const discountAmt = pendingDiscount
              ? pendingDiscount.type === 'percentage'
                ? Math.round((grossSubtotal * (pendingDiscount.amount / 100)) * 100) / 100
                : Math.min(pendingDiscount.amount, grossSubtotal)
              : 0;
            const gstBase = Math.max(0, Math.round((grossSubtotal - discountAmt) * 100) / 100);
            const lineMultiplier = grossSubtotal > 0 ? gstBase / grossSubtotal : 0;
            const businessTax = Math.round((gstBase * combinedBusinessRate) / 100 * 100) / 100;
            const additionalTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
            for (const atx of serviceAdditionalTaxes) {
              const svc = svcSubs.find(s => s.serviceId === atx.service_id);
              if (!svc) continue;
              const amt = svc.total * lineMultiplier * atx.rate / 100;
              const key = `${atx.name}_${atx.rate}`;
              if (additionalTaxMap.has(key)) additionalTaxMap.get(key)!.amount += amt;
              else additionalTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
            }
            const additionalLines = Array.from(additionalTaxMap.values());
            const totalTax = businessTax + additionalLines.reduce((s, l) => s + l.amount, 0);
            const grand = svcSubs.reduce((sum, s) => sum + s.total, 0) + totalTax;
            return (
              <>
                <div className="flex justify-between text-sm text-gray-700">
                  <span className="font-medium">{businessTaxLabel}</span>
                  <span className="font-semibold text-gray-900">+ ${businessTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                {additionalLines.map((line, i) => (
                  <div key={i} className="flex justify-between text-sm text-gray-700">
                    <span className="font-medium">{line.name} ({line.rate}%)</span>
                    <span className="font-semibold text-gray-900">+ ${line.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </>
            );
          })()}
        </div>

        <div className="bg-blue-600 rounded-lg p-4 border border-blue-600">
          <div className="flex justify-between text-base font-semibold text-white">
            <span>Total</span>
            <span>${(() => {
              const svcSubs = selectedServices.map(s => {
                let total = s.priceOption.price;
                const addons = selectedServiceAddons[s._uid] || [];
                total += addons.reduce((addonSum, addon) => addonSum + addon.price, 0);
                return { serviceId: s.service.id, total, taxExempt: s.service.tax_exempt };
              });
              const grossSubtotal = svcSubs.reduce((sum, s) => sum + s.total, 0);
              const discountAmt = pendingDiscount
                ? pendingDiscount.type === 'percentage'
                  ? Math.round((grossSubtotal * (pendingDiscount.amount / 100)) * 100) / 100
                  : Math.min(pendingDiscount.amount, grossSubtotal)
                : 0;
              const gstBase = Math.max(0, Math.round((grossSubtotal - discountAmt) * 100) / 100);
              const lineMultiplier = grossSubtotal > 0 ? gstBase / grossSubtotal : 0;
              const businessTax = Math.round((gstBase * combinedBusinessRate) / 100 * 100) / 100;
              let additionalTotal = 0;
              for (const atx of serviceAdditionalTaxes) {
                const svc = svcSubs.find(s => s.serviceId === atx.service_id);
                if (svc) additionalTotal += svc.total * lineMultiplier * atx.rate / 100;
              }
              return (svcSubs.reduce((sum, s) => sum + s.total, 0) + businessTax + additionalTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            })()}</span>
          </div>
        </div>

        {/* Workflow Checkbox - Only show if workflows power-up is enabled and any selected services have workflows */}
        {workflowsPowerUpEnabled && selectedServices.some(service => servicesWithWorkflows.has(service.service.id)) && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mt-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={workflowEnabled}
                onChange={(e) => setWorkflowEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <CheckCircle className="h-3 w-3 text-white" />
                </div>
                <div>
                  <span className="text-xs sm:text-sm font-medium text-blue-800">Include Workflow</span>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Auto-assign {bookingsByServiceAreaEnabled
                      ? (selectedResource ? resourceNames[selectedResource.id] || selectedResource.name : 'selected resource')
                      : (selectedResources[0]
                        ? `${selectedResources[0].first_name || ''} ${selectedResources[0].last_name || ''}`.trim()
                        : 'selected resource')} to workflow steps
                  </p>
                </div>
              </div>
            </label>
          </div>
        )}
      </div>
    </div>
  );

  // Update the getAvailableResourcesForTimeSlot function to include buffer time in availability calculations
  const getAvailableResourcesForTimeSlot = (date: Date, timeString: string): Resource[] => {
    const shouldCheckPostalCode = bookingsByServiceAreaEnabled && postalCode && postalCode.trim() !== '';

    // For non-reschedule mode, require postal code to be set only when service area matching is enabled
    if (bookingsByServiceAreaEnabled && !isReschedule && (!postalCode || postalCode.trim() === '')) {
      return [];
    }
    
    // Convert date to local timezone string
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + offset);
    const dateStr = format(localDate, 'yyyy-MM-dd');
    
    // If we're still loading blocked slots, return empty array
    if (blockedSlotsLoading) return [];

    // Calculate total duration including buffer time for the new booking
    // Use same logic as totalDurationMinutes: prefer service.duration_minutes, fall back to priceOption
    const serviceDurationMinutes = selectedServices.reduce((total, service) => {
      let duration = total + (service.service.duration_minutes || service.priceOption?.duration_minutes || 0);
      const addons = selectedServiceAddons[service._uid] || [];
      duration += addons.reduce((addonSum, addon) => addonSum + (addon.duration_minutes || 0), 0);
      return duration;
    }, 0);
    
    // Add buffer time to total duration for scheduling purposes
    const totalDurationWithBuffer = serviceDurationMinutes + bufferTime;

    // Convert booking start time to minutes since midnight
    const [startHour, startMinute] = timeString.split(':').map(Number);
    const bookingStartMinutes = startHour * 60 + startMinute;
    const bookingEndMinutes = bookingStartMinutes + totalDurationWithBuffer;

    console.log('Checking availability with buffer time:', {
      serviceDuration: serviceDurationMinutes,
      bufferTime,
      totalDurationWithBuffer,
      startTime: timeString,
      endMinutes: bookingEndMinutes
    });

    const availableResources = mobileAvailability.filter(resource => {
      try {
        // Check if any time during the booking duration (including buffer) overlaps with blocked slots
        const resourceBlockedSlots = blockedTimeSlots[resource.resource_id];
        if (resourceBlockedSlots) {
          const dateEntry = resourceBlockedSlots.find(entry => entry.date === dateStr);
          if (dateEntry) {
            // Convert blocked slots to minute ranges
            const blockedRanges = dateEntry.blockedSlots.map(slot => {
              const [hour, minute] = slot.split(':').map(Number);
              const slotMinutes = hour * 60 + minute;
              return {
                start: slotMinutes,
                end: slotMinutes + 30 // Each slot is 30 minutes
              };
            });

            // Check if booking overlaps with any blocked range
            const hasOverlap = blockedRanges.some(range => {
              // Booking starts before blocked slot ends AND booking ends after blocked slot starts
              return (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
            });

            if (hasOverlap) {
              return false;
            }
          }
        }

        const dayOfWeek = format(localDate, 'EEEE').toLowerCase();

        // Only check postal code availability if service area matching is enabled
        if (shouldCheckPostalCode) {
          let postalCodes = resource.postal_codes;
          if (typeof postalCodes === 'string') {
            try {
              postalCodes = JSON.parse(postalCodes);
            } catch (err) {
              if (isReschedule) console.log('❌ TimeSlot: Failed to parse postal codes for resource:', resource.id);
              // For reschedule mode, don't fail completely on parse errors
              if (!isReschedule) return false;
            }
          }
          
          const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
          const enteredFSA = postalCode.toUpperCase().replace(/\s+/g, '').substring(0, 3);
          
          const matchesFSA = availablePostalCodes.some((pc: string) => {
            const pcFSA = pc.toUpperCase().replace(/\s+/g, '').substring(0, 3);
            return pcFSA === enteredFSA;
          });
          
          if (isReschedule) {
            console.log('📮 TimeSlot postal code check:', {
              resourceId: resource.id,
              dayOfWeek,
              enteredFSA,
              availablePostalCodesCount: availablePostalCodes.length,
              matchesFSA
            });
          }
          
          if (!isReschedule && !matchesFSA) {
            return false;
          }
          
          if (isReschedule && !matchesFSA) {
            (resource as any)._postalCodeMismatch = true;
          }
        }

        // Check working hours
        let workingHours = resource.working_hours;
        if (typeof workingHours === 'string') {
          workingHours = JSON.parse(workingHours);
        }

        const daySchedule = workingHours?.[dayOfWeek];
        if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
          return false;
        }

        // Check if entire booking duration (including buffer) fits within any shift
        const isAvailable = daySchedule.shifts.some(shift => {
          const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
          const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;

          const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
          const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
          
          return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
        });

        return isAvailable;
      } catch (err) {
        console.error('Error checking resource availability:', err);
        return false;
      }
    }).map(resource => ({
      id: resource.id,                     // mobile_availability record ID
      resource_id: resource.resource_id,    // actual resource ID (or asset_id for assets)
      name: resourceNames[resource.id] || `Resource ${resource.resource_id}`,
      business_id: selectedBusiness?.id || '',
      mobile_available: true,
      working_hours: resource.working_hours,
      postal_codes: resource.postal_codes,
      created_at: resource.created_at,
      updated_at: resource.updated_at,
      _isAsset: (resource as any)._isAsset || false
    }));

    // Fallback for reschedule mode: if no resources are available due to postal code mismatch,
    // try again with only working hours check
    if (availableResources.length === 0 && isReschedule && shouldCheckPostalCode) {
      console.log('🔄 TimeSlot fallback: No resources found, trying without postal code restriction');
      
      const fallbackResources = mobileAvailability.filter(resource => {
        try {
          // Check if any time during the booking duration (including buffer) overlaps with blocked slots
          const resourceBlockedSlots = blockedTimeSlots[resource.resource_id];
          if (resourceBlockedSlots) {
            const dateEntry = resourceBlockedSlots.find(entry => entry.date === dateStr);
            if (dateEntry) {
              // Convert blocked slots to minute ranges
              const blockedRanges = dateEntry.blockedSlots.map(slot => {
                const [hour, minute] = slot.split(':').map(Number);
                const slotMinutes = hour * 60 + minute;
                return {
                  start: slotMinutes,
                  end: slotMinutes + 30 // Each slot is 30 minutes
                };
              });

              // Check if booking overlaps with any blocked range
              const hasOverlap = blockedRanges.some(range => {
                // Booking starts before blocked slot ends AND booking ends after blocked slot starts
                return (bookingStartMinutes < range.end && bookingEndMinutes > range.start);
              });

              if (hasOverlap) {
                return false;
              }
            }
          }

          const dayOfWeek = format(localDate, 'EEEE').toLowerCase();

          // Skip postal code check for fallback
          
          // Check working hours only
          let workingHours = resource.working_hours;
          if (typeof workingHours === 'string') {
            workingHours = JSON.parse(workingHours);
          }

          const daySchedule = workingHours?.[dayOfWeek];
          if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
            return false;
          }

          // Check if entire booking duration (including buffer) fits within any shift
          const isAvailable = daySchedule.shifts.some(shift => {
            const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
            const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;

            const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
            const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
            
            return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
          });

          if (isAvailable) {
            console.log('✅ TimeSlot fallback: Found resource with working hours:', {
              resourceId: resource.id,
              dayOfWeek,
              timeString
            });
          }

          return isAvailable;
        } catch (err) {
          console.error('Error in TimeSlot fallback check:', err);
          return false;
        }
      }).map(resource => ({
        id: resource.id,
        resource_id: resource.resource_id,
        name: resourceNames[resource.id] || `Resource ${resource.resource_id}`,
        business_id: selectedBusiness?.id || '',
        mobile_available: true,
        working_hours: resource.working_hours,
        postal_codes: resource.postal_codes,
        created_at: resource.created_at,
        updated_at: resource.updated_at,
        _isAsset: (resource as any)._isAsset || false
      }));
      
      if (fallbackResources.length > 0) {
        console.log('✅ TimeSlot fallback successful: Found resources using working hours only');
        return fallbackResources;
      }
    }

    return availableResources;
  };


  // Update the generateTimeSlots function to use resource availability
  const generateTimeSlots = (): string[] => {
    try {
      // If no date is selected, return empty array
      if (!scheduleData.startDate) {
        console.log('generateTimeSlots: No start date selected');
        return [];
      }

      // Get day of week for the selected date
      const selectedDate = new Date(scheduleData.startDate + 'T12:00:00Z');
      const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
      
      console.log('generateTimeSlots: Processing for', {
        date: scheduleData.startDate,
        dayOfWeek,
        mobileAvailabilityCount: mobileAvailability.length,
        postalCode,
        isReschedule
      });
      
      // Collect all shifts from available resources for this day
      const allShifts: Array<{ start: string; end: string }> = [];
      let resourcesChecked = 0;
      let resourcesWithShifts = 0;
      
      mobileAvailability.forEach(resource => {
        try {
          resourcesChecked++;
          let workingHours = resource.working_hours;
          if (typeof workingHours === 'string') {
            workingHours = JSON.parse(workingHours);
          }
          
          const daySchedule = workingHours?.[dayOfWeek];
          
          // Check if resource is available for this postal code (only when service area matching is enabled)
          if (bookingsByServiceAreaEnabled && postalCode && postalCode.trim() !== '' && !isReschedule) {
            let postalCodes = resource.postal_codes;
            if (typeof postalCodes === 'string') {
              try {
                postalCodes = JSON.parse(postalCodes);
              } catch (err) {
                console.log('Skipping resource - postal codes parse error:', resource.resource_id);
                return; // Skip this resource if postal codes can't be parsed
              }
            }
            
            const availablePostalCodes = postalCodes?.[dayOfWeek] || [];
            // Use first 3 characters for FSA comparison (postal code area code)
            const enteredPostalCode = postalCode.toUpperCase().replace(/\s+/g, '').substring(0, 3);
            
            // Check if any of the available postal codes match (using FSA - first 3 chars)
            const matchesPostalCode = availablePostalCodes.some((pc: string) => {
              const pcFSA = pc.toUpperCase().replace(/\s+/g, '').substring(0, 3);
              return pcFSA === enteredPostalCode;
            });
            
            if (!matchesPostalCode) {
              console.log('Skipping resource - postal code mismatch:', {
                resourceId: resource.resource_id,
                enteredPostalCode,
                availablePostalCodes
              });
              return; // Skip this resource if it doesn't serve this postal code
            }
          }
          
          // If day is enabled and has shifts, add them
          if (daySchedule?.enabled && daySchedule.shifts?.length > 0) {
            resourcesWithShifts++;
            daySchedule.shifts.forEach(shift => {
              allShifts.push(shift);
            });
          } else {
            console.log('Resource has no shifts for this day:', {
              resourceId: resource.resource_id,
              dayOfWeek,
              enabled: daySchedule?.enabled,
              shiftsCount: daySchedule?.shifts?.length || 0
            });
          }
        } catch (err) {
          console.error('Error processing resource availability:', err, resource);
        }
      });
      
      console.log('generateTimeSlots: Resource processing complete:', {
        resourcesChecked,
        resourcesWithShifts,
        totalShifts: allShifts.length
      });
      
      // If no shifts found, return empty array
      if (allShifts.length === 0) {
        console.log('No available shifts found for', dayOfWeek, '- returning empty array');
        return [];
      }
      
      // Generate slots for each shift individually (like public booking does)
      // This ensures we show slots starting from the actual shift start times
      const slotSet = new Set<string>();
      const now = new Date();
      
      allShifts.forEach(shift => {
        const [startHour, startMin] = shift.start.split(':').map(Number);
        const [endHour, endMin] = shift.end.split(':').map(Number);
        
        // Create Date objects for shift start and end on the selected date
        // Use local date to avoid timezone issues
        const shiftStart = new Date(scheduleData.startDate + 'T00:00:00');
        shiftStart.setHours(startHour, startMin, 0, 0);
        
        const shiftEnd = new Date(scheduleData.startDate + 'T00:00:00');
        shiftEnd.setHours(endHour, endMin, 0, 0);
        
        console.log('Processing shift:', {
          shiftStart: shiftStart.toISOString(),
          shiftEnd: shiftEnd.toISOString(),
          startTime: shift.start,
          endTime: shift.end
        });
        
        // Generate 30-minute slots starting from the shift start time
        const current = new Date(shiftStart);
        while (current < shiftEnd) {
          const hours = current.getHours();
          const minutes = current.getMinutes();
          const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // Only add the slot if it hasn't passed (for today) or it's a future date
          const isToday = current.toDateString() === now.toDateString();
          const hasPassed = isToday && current < now;
          
          if (!hasPassed) {
            slotSet.add(timeString);
            console.log('Added slot:', timeString);
          } else {
            console.log('Skipped past slot:', timeString);
        }
          
          // Move to next 30-minute slot
          current.setMinutes(current.getMinutes() + 30);
        }
      });
      
      // Convert set to sorted array
      const slots = Array.from(slotSet).sort();

      console.log('Generated time slots from resource availability:', {
        dayOfWeek,
        shiftsCount: allShifts.length,
        shifts: allShifts.map(s => `${s.start}-${s.end}`),
        slotsCount: slots.length,
        slots: slots.slice(0, 10) // Show first 10 slots for debugging
      });
      
      return slots;
    } catch (err) {
      console.error('Error generating time slots:', err);
      return [];
    }
  };

  // Update the location mode handler to use the new prop
  const handleLocationModeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    console.log("Switching from mobile to location booking");
    // If we have the onSwitchToLocation prop, use it
    if (onSwitchToLocation) {
      onSwitchToLocation();
    } else {
      // Otherwise, just close the modal (fallback to old behavior)
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 transition-opacity duration-200 create-mobile-booking-modal ${
          isOpen ? 'bg-black/50' : 'bg-black/0'
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        onTransitionEnd={handleTransitionEnd}
        style={{
          zIndex: baseZ,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
      >
        <div 
          className={`bg-white transform transition-all duration-200 ${
            isMobile 
              ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
              : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                  isOpen ? 'translate-x-0' : 'translate-x-full'
                }`
          }`}
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            zIndex: baseZ + 48,
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            ...(isMobile && { left: 0, right: 0 }),
            ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col overflow-hidden">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
              <h2 className="text-2xl font-bold text-gray-900" style={{ 
                letterSpacing: '-0.02em'
              }}>
                Create Mobile Booking
              </h2>
              {onSwitchToLocation && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    type="button"
                    onClick={handleLocationModeClick}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Location
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl"
                  >
                    Mobile
                  </button>
                </div>
              )}
            </div>

            {/* Form Content */}
            <form 
              onSubmit={handleSubmit} 
              className="flex-1 overflow-y-auto bg-white"
              style={{ 
                WebkitOverflowScrolling: 'touch',
                paddingBottom: 0
              }}
            >
              <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
                paddingBottom: '2rem'
              }}>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                {/* Title Section — collapsible */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div
                    className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                    onClick={() => setIsTitleExpanded(!isTitleExpanded)}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isTitleExpanded ? '' : '-rotate-90'}`} />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Booking Title</span>
                    {!isTitleExpanded && (
                      <span className="text-sm font-medium text-gray-900 truncate">{title || 'Mobile Booking'}</span>
                    )}
                  </div>
                  {isTitleExpanded && (
                    <div className="px-3 pb-3">
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        placeholder="Enter booking title..."
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  )}
                </div>



                {/* Customer Source Dropdown Portal */}
                {isCustomerSourceDropdownOpen && customerSourceButtonRef.current && createPortal(
                  <>
                    <div 
                      className="fixed inset-0" 
                      style={{ zIndex: baseZ }}
                      onClick={() => setIsCustomerSourceDropdownOpen(false)}
                    />
                    <div 
                      className="fixed bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl max-h-60 overflow-y-auto"
                      style={{ 
                        zIndex: baseZ + 1,
                        backdropFilter: 'blur(20px)', 
                        WebkitBackdropFilter: 'blur(20px)',
                        left: customerSourceButtonRef.current.getBoundingClientRect().left,
                        top: customerSourceButtonRef.current.getBoundingClientRect().bottom + 4,
                        width: customerSourceButtonRef.current.getBoundingClientRect().width,
                        maxWidth: '400px'
                      }}
                    >
                      {selectedCustomerSource ? (
                        <div 
                          onClick={() => {
                            setSelectedCustomerSource(null);
                            setIsCustomerSourceDropdownOpen(false);
                          }}
                          className="px-4 py-3 text-sm text-gray-500 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150"
                        >
                          Clear source
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-400">
                          No source selected
                        </div>
                      )}
                      
                      {/* Regular Customer Sources (includes system defaults like Repeat Customer) */}
                      {customerSources.map((source) => {
                        const isSystemRepeat =
                          source.id === 'repeat-customer' ||
                          source.name.toLowerCase() === 'repeat customer';
                        const isSelected =
                          selectedCustomerSource?.id === source.id ||
                          (!!selectedCustomerSource &&
                            selectedCustomerSource.name.toLowerCase() === source.name.toLowerCase());
                        return (
                        <div
                          key={source.id}
                          onClick={async () => {
                            setSelectedCustomerSource(source);
                            setIsCustomerSourceDropdownOpen(false);
                            // Reset referral source when source changes (unless it's referral)
                            const sourceNameLower = source.name?.toLowerCase() || '';
                            if (!sourceNameLower.includes('refer')) {
                              setReferralSourceCustomer(null);
                            } else {
                              // If referral is selected, reload referral program status to ensure it's up to date
                              await loadReferralProgramStatus();
                            }
                          }}
                          className={`px-4 py-3 text-sm cursor-pointer transition-colors duration-150 border-t border-gray-100/50 flex items-center justify-between ${
                            isSelected
                              ? 'bg-blue-50 text-blue-900 font-medium'
                              : 'text-gray-900 hover:bg-purple-50/50'
                          }`}
                        >
                          <span>{source.name}</span>
                          {isSystemRepeat ? (
                            <span className="text-xs text-green-600 font-medium">System</span>
                          ) : null}
                        </div>
                        );
                      })}
                      
                      {/* Add Source Option */}
                      <div
                        onClick={() => {
                          setIsCustomerSourceDropdownOpen(false);
                          setIsCreateSourceModalOpen(true);
                        }}
                        className="px-4 py-3 text-sm text-purple-600 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150 border-t border-gray-100/50 flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Source</span>
                      </div>
                    </div>
                  </>,
                  document.body
                )}

                {/* Customer Section */}
                {selectedCustomer ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    {/* Collapsible header — always visible */}
                    <div
                      className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                      onClick={() => setIsCustomerSectionExpanded(!isCustomerSectionExpanded)}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isCustomerSectionExpanded ? '' : '-rotate-90'}`} />
                      <div className="h-5 w-5 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-bold text-[9px] flex-shrink-0 uppercase">
                        {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate block">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </span>
                        {!isCustomerSectionExpanded && (
                          <span className="text-[11px] truncate block flex items-center gap-1">
                            <span className={selectedCustomerSource ? 'text-gray-400' : 'text-red-500 font-semibold'}>
                              {selectedCustomerSource ? selectedCustomerSource.name : 'No Source'}
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className={bookingAddress && bookingAddress.trim() ? 'text-gray-400' : 'text-red-500 font-semibold'}>
                              {bookingAddress && bookingAddress.trim() ? bookingAddress.split(',')[0] : 'No Booking Address'}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isCustomerSectionExpanded && (
                    <div className="space-y-4 px-3 pb-3">
                      {/* Customer Card */}
                      <div className="bg-gray-50 rounded-xl p-2.5 relative transition-all duration-150 cursor-pointer active:scale-[0.98] touch-manipulation"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsEditCustomerModalOpen(true);
                        }}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center space-x-2">
                            <div className="h-6 w-6 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0 uppercase">
                              {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                            </div>
                            <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight">
                              {selectedCustomer.first_name} {selectedCustomer.last_name}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsSelectCustomerModalOpen(true);
                            }}
                            className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation flex-shrink-0"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Change
                          </button>
                        </div>
                        <div className="space-y-1 pl-8">
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                          {selectedCustomer.email && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Mail className="h-3.5 w-3.5 text-gray-400" />
                              <span className="truncate">{selectedCustomer.email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                    {/* Customer Source */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5">Customer Source</label>
                      <button
                        ref={customerSourceButtonRef}
                        type="button"
                        onClick={() => setIsCustomerSourceDropdownOpen(!isCustomerSourceDropdownOpen)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-sm bg-white rounded-xl hover:bg-gray-50 focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                          selectedCustomerSource 
                            ? "border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                            : "border-2 border-red-300 bg-red-50 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent', fontSize: '16px' }}
                      >
                        <span className={selectedCustomerSource ? "text-gray-700" : "text-red-600"}>
                          {selectedCustomerSource ? selectedCustomerSource.name : "Select source..."}
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isCustomerSourceDropdownOpen ? 'rotate-180' : ''} ${selectedCustomerSource ? 'text-gray-400' : 'text-red-400'}`} />
                      </button>
                    </div>

                    {/* Referral Source - Only show when: new customer + source=referral + program active */}
                    {(() => {
                      const hasCustomer = !!selectedCustomer;
                      // Check if customer is new: repeat is false, null, or undefined (not explicitly true)
                      const isNewCustomer = selectedCustomer?.repeat !== true;
                      // Check if source name contains "refer" (case-insensitive) to handle variations like "referral", "referal", etc.
                      const sourceNameLower = selectedCustomerSource?.name?.toLowerCase() || '';
                      const sourceIsReferral = sourceNameLower.includes('refer');
                      const shouldShow = hasCustomer && isNewCustomer && sourceIsReferral && referralProgramActive;
                      
                      if (sourceNameLower.includes('refer')) {
                        console.log('🔍 Referral source field visibility check:', {
                          hasCustomer,
                          isNewCustomer,
                          customerRepeat: selectedCustomer?.repeat,
                          sourceIsReferral,
                          sourceName: selectedCustomerSource?.name,
                          programActive: referralProgramActive,
                          shouldShow
                        });
                      }
                      
                      return shouldShow;
                    })() && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                          Referral Source
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsSelectReferralSourceModalOpen(true)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-sm bg-white rounded-xl hover:bg-gray-50 focus:outline-none transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                            referralSourceCustomer 
                              ? 'border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                              : 'border-2 border-dashed border-blue-300 bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <span className={referralSourceCustomer ? 'text-gray-700' : 'text-blue-600 font-medium'}>
                            {referralSourceCustomer 
                              ? `${referralSourceCustomer.first_name} ${referralSourceCustomer.last_name}`
                              : 'Select referring customer...'}
                          </span>
                          <Users className={`h-4 w-4 ${referralSourceCustomer ? 'text-gray-400' : 'text-blue-400'}`} />
                        </button>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                        Booking Notes
                      </label>
                      <textarea
                        value={scheduleData.customer_notes || ''}
                        onChange={(e) => setScheduleData(prev => ({
                          ...prev,
                          customer_notes: e.target.value
                        }))}
                        placeholder="Add booking notes..."
                        className="w-full h-24 px-4 py-3 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                      {/* Booking Address */}
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                          <label className={`block text-xs font-semibold ${
                            !bookingAddress || bookingAddress.trim() === ''
                              ? 'text-red-600'
                              : 'text-gray-500'
                          }`}>
                            Booking Address
                          </label>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                            {savedAddressOptions.length >= 2 && (
                              <button
                                ref={savedAddressButtonRef}
                                type="button"
                                onClick={() => setSavedAddressPickerOpen((v) => !v)}
                                className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation inline-flex items-center gap-1"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Use saved address
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${savedAddressPickerOpen ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                            {bookingAddress && selectedCustomer?.address && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (selectedCustomer?.address) {
                                    applyBookingAddressFromSaved(
                                      selectedCustomer.address,
                                      selectedCustomer.lat ?? null,
                                      selectedCustomer.lng ?? null,
                                    );
                                    console.log('📍 Reset to customer coordinates:', {
                                      lat: selectedCustomer.lat,
                                      lng: selectedCustomer.lng,
                                    });
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Reset to Customer Address
                              </button>
                            )}
                          </div>
                        </div>
                        {savedAddressPickerOpen &&
                          savedAddressOptions.length >= 2 &&
                          savedAddressButtonRef.current &&
                          (() => {
                            const rect = savedAddressButtonRef.current!.getBoundingClientRect();
                            const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
                            const menuWidth = Math.min(288, vw * 0.85);
                            const left = Math.max(8, rect.right - menuWidth);
                            const top = rect.bottom + 4;
                            return createPortal(
                              <div
                                ref={savedAddressMenuPortalRef}
                                role="listbox"
                                className="fixed max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                style={{ zIndex: baseZ + 1, top, left, width: menuWidth }}
                              >
                                {savedAddressOptions.map((opt) => (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    role="option"
                                    className="w-full px-3 py-2.5 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                                    onClick={() =>
                                      applyBookingAddressFromSaved(opt.address, opt.lat, opt.lng)
                                    }
                                  >
                                    <div className="text-xs font-semibold text-gray-900">{opt.label}</div>
                                    <div className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">{opt.address}</div>
                                  </button>
                                ))}
                              </div>,
                              document.body
                            );
                          })()}
                        <AddressAutocompleteModal
                          value={bookingAddress}
                          onChange={(value) => {
                            console.log('Address changed:', value);
                            setBookingAddress(value);
                            // Clear stale coordinates when address text is manually changed
                            setBookingLat(null);
                            setBookingLng(null);
                          }}
                          onSelect={(address) => {
                            console.log('Address selected from Google Autocomplete:', address);
                            setBookingAddress(address.formatted);
                            // Store coordinates for auto job tracking
                            setBookingLat(address.lat || null);
                            setBookingLng(address.lng || null);
                            console.log('📍 Stored booking coordinates:', { lat: address.lat, lng: address.lng });
                            if (address.postalCode) {
                              const cleanPostalCode = address.postalCode.replace(/\s+/g, '').toUpperCase();
                              console.log(`Setting postal code from Google Autocomplete: ${cleanPostalCode} (Full), ${cleanPostalCode.substring(0, 3)} (FSA)`);
                              setPostalCode(cleanPostalCode.substring(0, 3));
                            } else {
                              console.log('No postal code returned from Google Autocomplete');
                              // Try to extract from formatted address as fallback
                              const postalCodeRegex = /[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d/i;
                              const match = address.formatted.match(postalCodeRegex);
                              if (match) {
                                const extractedPostalCode = match[0].replace(/\s+/g, '').toUpperCase();
                                console.log(`Extracted postal code ${extractedPostalCode} from formatted address as fallback`);
                                setPostalCode(extractedPostalCode.substring(0, 3));
                              }
                            }
                          }}
                          placeholder="Enter service location address"
                          className={`w-full px-4 py-3 text-base rounded-xl focus:outline-none focus:ring-2 transition-all duration-150 ${
                            !bookingAddress || bookingAddress.trim() === ''
                              ? 'bg-red-50 border-2 border-red-300 focus:ring-red-500 focus:border-red-500'
                              : 'bg-white border border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                        />
                      </div>

                    </div>
                    )}
                  </div>
                  ) : (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Customer *
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsSelectCustomerModalOpen(true)}
                        className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Users className="h-5 w-5 mr-2" />
                        Select Customer
                      </button>
                    </div>
                  )}



                {/* Services Section */}
                {selectedCustomer && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <div
                      className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                      onClick={() => setIsServicesSectionExpanded(!isServicesSectionExpanded)}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isServicesSectionExpanded ? '' : '-rotate-90'}`} />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex-shrink-0">Services</span>
                      {!isServicesSectionExpanded && selectedServices.length > 0 && (
                        <span className="text-sm text-gray-700 truncate">
                          {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} — {selectedServices.map(s => s.service.name).join(', ')}
                        </span>
                      )}
                      {!isServicesSectionExpanded && selectedServices.length === 0 && (
                        <span className="text-sm text-red-500 font-medium">No services added</span>
                      )}
                    </div>
                    {isServicesSectionExpanded && (
                  <div className="px-3 pb-3 space-y-6">
                    {/* Group services by vehicle */}
                    {Object.entries(servicesByVehicle).map(([vehicleId, services]) => {
                      const vehicle = services[0].vehicle; // Get vehicle info from first service
                      const isNoVehicle = vehicleId === 'no-vehicle' || !vehicle;
                      return (
                        <div key={vehicleId} className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-4">
                            {/* Vehicle Header */}
                            {!isNoVehicle && (
                              <div className="flex items-center justify-between p-4 bg-gray-900 rounded-xl mb-4">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <Car className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  <span className="text-base font-semibold text-white truncate">
                                    {vehicle.year} {vehicle.make} {vehicle.model}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setVehicleIdToSwap(vehicleId);
                                      setIsSelectVehicleModalOpen(true);
                                    }}
                                    className="px-2.5 py-1 text-xs font-semibold text-blue-400 hover:text-blue-300 bg-white/10 hover:bg-white/15 rounded-lg transition-colors touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedVehicleToEdit(vehicle);
                                      setIsEditVehicleModalOpen(true);
                                    }}
                                    className="px-2.5 py-1 text-xs font-semibold text-gray-400 hover:text-gray-300 bg-white/10 hover:bg-white/15 rounded-lg transition-colors touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Home Services header when no vehicle — only show for non-home-services businesses */}
                            {isNoVehicle && !isHomeServices && (
                              <div className="flex items-center p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                                <div>
                                  <div className="text-base font-semibold text-blue-900">Services</div>
                                  <div className="text-sm text-blue-700 mt-0.5">No vehicle required</div>
                                </div>
                              </div>
                            )}

                            {/* Services List */}
                            <div className="space-y-3">
                              {services.map((item, serviceIndex) => {
                                const isDescExpanded = expandedServiceDescriptions.has(item._uid);
                                const hasAddonsForService = serviceHasAddons[item.service.id] ?? (selectedServiceAddons[item._uid]?.length > 0);
                                return (
                                <div key={item._uid} className="rounded-xl border border-gray-100 bg-gray-50/50 overflow-hidden">
                                  {/* Service header row */}
                                  <div className="px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                      {/* Expand description toggle */}
                                      <button
                                        type="button"
                                        onClick={() => setExpandedServiceDescriptions(prev => {
                                          const next = new Set(prev);
                                          next.has(item._uid) ? next.delete(item._uid) : next.add(item._uid);
                                          return next;
                                        })}
                                        className="flex-shrink-0 p-0.5"
                                      >
                                        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isDescExpanded ? '' : '-rotate-90'}`} />
                                      </button>
                                      <input
                                        type="text"
                                        value={item.service.name}
                                        onChange={(e) => {
                                          setSelectedServices(prev => prev.map(s =>
                                            s._uid === item._uid ? { ...s, service: { ...s.service, name: e.target.value } } : s
                                          ));
                                        }}
                                        className="text-sm font-semibold text-gray-900 flex-1 min-w-0 bg-transparent border-none focus:outline-none focus:ring-0 px-0 py-0 truncate"
                                        style={{ fontSize: '14px' }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(item._uid)}
                                        className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50 transition-colors"
                                      >
                                        <X className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                                      </button>
                                    </div>

                                    {/* Inline price + duration */}
                                    {(item.priceOption.pricing_type === 'per_unit' || item.priceOption.pricing_type === 'per_sqft') ? (
                                      <div className="mt-2 space-y-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                            {item.priceOption.pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            ${(item.priceOption.unit_price || 0).toFixed(2)}/{item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} · {item.priceOption.duration_per_unit || 0} hrs/{item.priceOption.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[11px] font-medium text-gray-500">{item.priceOption.pricing_type === 'per_sqft' ? 'Sq Ft' : 'Units'}</span>
                                            <input
                                              type="number"
                                              value={item.priceOption.quantity || ''}
                                              placeholder="0"
                                              onChange={(e) => {
                                                const qty = parseFloat(e.target.value) || 0;
                                                const computedPrice = Math.round(qty * (item.priceOption.unit_price || 0) * 100) / 100;
                                                const updates: any = { quantity: qty, price: computedPrice };
                                                if (!item.priceOption._durationOverridden) {
                                                  updates.duration_minutes = Math.round(qty * (item.priceOption.duration_per_unit || 0) * 60);
                                                }
                                                setSelectedServices(prev => prev.map(s =>
                                                  s._uid === item._uid ? { ...s, priceOption: { ...s.priceOption, ...updates } } : s
                                                ));
                                              }}
                                              className="w-24 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              style={{ fontSize: '14px' }}
                                              min="0"
                                            />
                                          </div>
                                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <span>=</span>
                                            <span className="font-semibold text-gray-900">${(item.priceOption.price || 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Clock className="h-3 w-3 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((item.priceOption.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const newDuration = Math.round(hours * 60);
                                                setSelectedServices(prev => prev.map(s =>
                                                  s._uid === item._uid ? { ...s, priceOption: { ...s.priceOption, duration_minutes: newDuration, _durationOverridden: true } } : s
                                                ));
                                              }}
                                              className="w-16 px-1.5 py-0.5 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                    <div className="flex items-center gap-3 mt-2">
                                      <div className="flex items-center gap-1.5">
                                        <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                                        <input
                                          type="number"
                                          value={item.priceOption.price}
                                          onChange={(e) => {
                                            const newPrice = parseFloat(e.target.value) || 0;
                                            setSelectedServices(prev => prev.map(s =>
                                              s._uid === item._uid ? { ...s, priceOption: { ...s.priceOption, price: newPrice } } : s
                                            ));
                                          }}
                                          className="w-20 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          style={{ fontSize: '14px' }}
                                          step="0.01"
                                          min="0"
                                        />
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                                        <input
                                          type="number"
                                          value={parseFloat(((item.priceOption.duration_minutes || 0) / 60).toFixed(2))}
                                          onChange={(e) => {
                                            const hours = parseFloat(e.target.value) || 0;
                                            const newDuration = Math.round(hours * 60);
                                            setSelectedServices(prev => prev.map(s =>
                                              s._uid === item._uid ? { ...s, priceOption: { ...s.priceOption, duration_minutes: newDuration } } : s
                                            ));
                                          }}
                                          className="w-16 px-2 py-1 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                          style={{ fontSize: '14px' }}
                                          step="0.25"
                                          min="0"
                                        />
                                        <span className="text-xs text-gray-400">hrs</span>
                                      </div>
                                    </div>
                                    )}

                                    {/* Action buttons row */}
                                    <div className="flex items-center gap-2 mt-2">
                                      {hasAddonsForService && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!item.service?.id) return;
                                            setCurrentService(item.service);
                                            setCurrentServiceUid(item._uid);
                                            setIsSelectAddonsModalOpen(true);
                                          }}
                                          className="px-2.5 py-1 text-[11px] font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                                        >
                                          Add-ons{selectedServiceAddons[item._uid]?.length ? ` (${selectedServiceAddons[item._uid].length})` : ''}
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {/* Expandable description */}
                                  {isDescExpanded && (
                                    <div className="px-3 pb-3 pt-0">
                                      <div className="relative">
                                        <textarea
                                          id={`desc-textarea-${item._uid}`}
                                          value={item.service.description || ''}
                                          onChange={(e) => {
                                            setSelectedServices(prev => prev.map(s =>
                                              s._uid === item._uid ? { ...s, service: { ...s.service, description: e.target.value } } : s
                                            ));
                                          }}
                                          placeholder="Add service description..."
                                          className="w-full px-3 py-2 pb-5 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
                                          rows={3}
                                          style={{ fontSize: '13px', minHeight: '60px' }}
                                        />
                                        <div
                                          className="absolute bottom-0 right-0 w-8 h-8 flex items-center justify-center cursor-s-resize touch-none text-gray-400 active:text-gray-600"
                                          onTouchStart={(e) => {
                                            const touch = e.touches[0];
                                            const textarea = document.getElementById(`desc-textarea-${item._uid}`) as HTMLTextAreaElement;
                                            if (!textarea) return;
                                            const startY = touch.clientY;
                                            const startH = textarea.offsetHeight;
                                            const onMove = (ev: TouchEvent) => {
                                              ev.preventDefault();
                                              const dy = ev.touches[0].clientY - startY;
                                              textarea.style.height = Math.max(60, startH + dy) + 'px';
                                            };
                                            const onEnd = () => {
                                              document.removeEventListener('touchmove', onMove);
                                              document.removeEventListener('touchend', onEnd);
                                            };
                                            document.addEventListener('touchmove', onMove, { passive: false });
                                            document.addEventListener('touchend', onEnd);
                                          }}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            const textarea = document.getElementById(`desc-textarea-${item._uid}`) as HTMLTextAreaElement;
                                            if (!textarea) return;
                                            const startY = e.clientY;
                                            const startH = textarea.offsetHeight;
                                            const onMove = (ev: MouseEvent) => {
                                              const dy = ev.clientY - startY;
                                              textarea.style.height = Math.max(60, startH + dy) + 'px';
                                            };
                                            const onUp = () => {
                                              document.removeEventListener('mousemove', onMove);
                                              document.removeEventListener('mouseup', onUp);
                                            };
                                            document.addEventListener('mousemove', onMove);
                                            document.addEventListener('mouseup', onUp);
                                          }}
                                        >
                                          <svg width="10" height="10" viewBox="0 0 10 10">
                                            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            <line x1="9" y1="5" x2="5" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Service Add-ons */}
                                  {selectedServiceAddons[item._uid]?.length > 0 && (
                                    <div className="px-3 pb-2.5 space-y-1">
                                      {selectedServiceAddons[item._uid].map(addon => (
                                        <div key={addon.id} className="flex items-center justify-between py-1 px-2.5 bg-green-50/60 rounded-lg">
                                          <span className="text-xs text-gray-700">{addon.name}</span>
                                          <span className="text-xs font-medium text-gray-500">${addon.price.toFixed(2)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                              
                              {/* Add Service Button for this specific vehicle - hide for membership services */}
                              {(() => {
                                const hasMembershipService = false /* memberships retired — recurring bookings replace them */;
                                return !hasMembershipService && (
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('🔍 DEBUG: Add Service button clicked for vehicle:', vehicle);
                                  console.log('🔍 DEBUG: Setting currentVehicle to:', vehicle);
                                  setCurrentVehicle(vehicle);
                                  console.log('🔍 DEBUG: Setting SelectServiceModal to open');
                                  setIsSelectServiceModalOpen(true);
                                  console.log('🔍 DEBUG: isSelectServiceModalOpen should now be true');
                                }}
                                className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                {isHomeServices && (vehicleId === 'no-vehicle' || !vehicle)
                                  ? 'Add Service'
                                  : `Add Service to ${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="space-y-3">
                      {/* Add Service Button - only show when no services exist yet */}
                      {selectedServices.length === 0 && (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <button
                            type="button"
                            onClick={() => {
                              console.log('🔍 DEBUG: Main Add Service button clicked');
                              console.log('🔍 DEBUG: selectedCustomer:', selectedCustomer);
                              console.log('🔍 DEBUG: selectedCustomer?.vehicles?.length:', selectedCustomer?.vehicles?.length);
                              
                              if (isHomeServices) {
                                setCurrentVehicle(buildHomeServicesVehicle(selectedCustomer?.id));
                                setIsSelectServiceModalOpen(true);
                                return;
                              }

                              if (!selectedCustomer?.vehicles?.length) {
                                console.log('🔍 DEBUG: No vehicles found, opening CreateVehicleModal');
                                setIsCreateVehicleModalOpen(true);
                                return;
                              }
                              console.log('🔍 DEBUG: Vehicles found, opening SelectVehicleModal');
                              // Start with vehicle selection since no services exist
                              setIsSelectVehicleModalOpen(true);
                            }}
                            className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-5 w-5 mr-2" />
                            Add Service
                          </button>
                        </div>
                      )}

                      {/* Add Another Vehicle Button - hide for membership services */}
                      {(() => {
                        const hasMembershipService = false /* memberships retired — recurring bookings replace them */;
                        return !isHomeServices && selectedCustomer?.vehicles && selectedCustomer.vehicles.length > 0 && !hasMembershipService && (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentVehicle(null);
                              setIsSelectVehicleModalOpen(true);
                            }}
                            className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Car className="h-5 w-5 mr-2" />
                            Add Another Vehicle
                          </button>
                        </div>
                        );
                      })()}

                      {/* Create Vehicle Button - show when customer has no vehicles */}
                      {selectedCustomer && !isHomeServices && (!selectedCustomer.vehicles || selectedCustomer.vehicles.length === 0) && (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <button
                            type="button"
                            onClick={() => setIsCreateVehicleModalOpen(true)}
                            className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <Plus className="h-5 w-5 mr-2" />
                            Create Vehicle & Add Service
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                    )}
                  </div>
                )}

                {/* Membership Frequency Configuration */}
                {(() => {
                  const hasMembershipService = false /* memberships retired — recurring bookings replace them */;
                  
                  if (!hasMembershipService) return null;
                  
                  return (
                    <div className="bg-white rounded-[12px] border border-blue-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Membership Schedule
                      </label>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Frequency Selector */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Frequency
                          </label>
                          <select
                            value={membershipFrequency}
                            onChange={(e) => {
                              setMembershipFrequency(e.target.value as 'daily' | 'weekly' | 'monthly');
                              setMembershipInterval(1); // Reset interval when frequency changes
                            }}
                            className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>

                        {/* Interval Selector */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Repeat Every
                          </label>
                          <select
                            value={membershipInterval}
                            onChange={(e) => setMembershipInterval(parseInt(e.target.value))}
                            className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                          >
                            {Array.from({ length: 20 }, (_, i) => i + 1).map(num => {
                              const periodLabel = membershipFrequency === 'daily' 
                                ? num === 1 ? 'day' : 'days'
                                : membershipFrequency === 'weekly'
                                  ? num === 1 ? 'week' : 'weeks'
                                  : num === 1 ? 'month' : 'months';
                              
                              return (
                                <option key={num} value={num}>
                                  Every {num} {periodLabel}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>
                      
                      {/* End Date Configuration */}
                      <div className="mt-4 space-y-3">
                        <label className="block text-xs font-medium text-gray-600">
                          End Date
                        </label>
                        
                        <select
                          value={membershipEndType}
                          onChange={(e) => setMembershipEndType(e.target.value as 'after_occurrences')}
                          className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="after_occurrences"># of Bookings</option>
                        </select>
                        
                        {/* Number of Bookings Selector */}
                        {membershipEndType === 'after_occurrences' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Number of Bookings
                            </label>
                            <select
                              value={membershipOccurrences}
                              onChange={(e) => setMembershipOccurrences(parseInt(e.target.value))}
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            >
                              {Array.from({ length: 52 }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num}>
                                  {num} {num === 1 ? 'booking' : 'bookings'}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Start Date Configuration */}
                      <div className="mt-4 space-y-3">
                        <label className="block text-xs font-medium text-gray-600">
                          Choose Start Date
                        </label>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Day of Week Selector */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Day of Week
                            </label>
                            <select
                              value={membershipDayOfWeek}
                              onChange={(e) => setMembershipDayOfWeek(parseInt(e.target.value))}
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            >
                              <option value={1}>Monday</option>
                              <option value={2}>Tuesday</option>
                              <option value={3}>Wednesday</option>
                              <option value={4}>Thursday</option>
                              <option value={5}>Friday</option>
                              <option value={6}>Saturday</option>
                              <option value={0}>Sunday</option>
                            </select>
                          </div>

                          {/* Start Time Selector */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Start Time
                            </label>
                            <input
                              type="time"
                              value={membershipStartTime}
                              onChange={(e) => setMembershipStartTime(e.target.value)}
                              className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                        </div>
                        
                        {/* Check Availability Button */}
                        <button
                          type="button"
                          onClick={handleCheckMembershipAvailability}
                          disabled={checkingAvailability}
                          className="w-full mt-3 flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ 
                            WebkitTapHighlightColor: 'transparent',
                            minHeight: '44px'
                          }}
                        >
                          {checkingAvailability ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                              Checking Availability...
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4 mr-2" />
                              Check Availability
                            </>
                          )}
                        </button>
                      </div>

                      {/* Availability Results */}
                      {membershipAvailabilityResults.length > 0 && (
                        <div className="mt-4 space-y-2" key={`results-${resultsUpdateCounter}`}>
                          <label className="block text-xs font-medium text-gray-600 mb-2">
                            Booking Schedule ({membershipAvailabilityResults.filter(r => r.available).length} available, {membershipAvailabilityResults.filter(r => !r.available).length} conflicts)
                          </label>
                          <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                            {membershipAvailabilityResults.map((result, index) => {
                              console.log(`🎫 RENDER Booking #${index + 1}:`, {
                                available: result.available,
                                hasResource: !!result.resource,
                                resource: result.resource
                              });
                              
                              return (
                                <div
                                  key={`${resultsUpdateCounter}-${index}-${result.date}-${result.time}`}
                                  className={`p-3 rounded-xl border ${
                                    result.available
                                      ? 'bg-green-50 border-green-200'
                                      : 'bg-red-50 border-red-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <p className={`text-sm font-semibold ${
                                        result.available ? 'text-green-900' : 'text-red-900'
                                      }`}>
                                        Booking #{index + 1}
                                      </p>
                                    <p className={`text-xs mt-1 ${
                                      result.available ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {(() => {
                                        // Parse date string in local timezone to avoid UTC conversion
                                        const [year, month, day] = result.date.split('-').map(Number);
                                        const displayDate = new Date(year, month - 1, day);
                                        return displayDate.toLocaleDateString('en-US', { 
                                        weekday: 'long', 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                        });
                                      })()} at {result.time}
                                    </p>
                                    {result.available && result.resource && (
                                      <p className="text-xs text-green-600 mt-1">
                                        👤 Resource: {result.resource.name}
                                      </p>
                                    )}
                                    {!result.available && result.conflict && (
                                      <p className="text-xs text-red-600 mt-1">
                                        ⚠️ {result.conflict}
                                      </p>
                                    )}
                                  </div>
                                  <div className="ml-3 flex flex-col gap-2">
                                    {result.available ? (
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <>
                                        <AlertCircle className="h-5 w-5 text-red-600" />
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingBookingIndex(index);
                                            setEditingBookingDate(result.date);
                                            setEditingBookingTime(result.time);
                                            // Set calendar to show the month of this booking
                                            // Parse date in local timezone to avoid UTC conversion
                                            const [year, month, day] = result.date.split('-').map(Number);
                                            setCurrentMonth(new Date(year, month - 1, day));
                                            // Clear schedule data to force fresh selection
                                            setScheduleData({
                                              dropOffDate: '',
                                              dropOffTime: '',
                                              startDate: '',
                                              startTime: '',
                                              endDate: '',
                                              endTime: '',
                                              customer_notes: scheduleData.customer_notes
                                            });
                                            setIsCalendarPickerOpen(true);
                                          }}
                                          className="p-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-all touch-manipulation"
                                          style={{ WebkitTapHighlightColor: 'transparent' }}
                                          title="Change date/time"
                                        >
                                          <Calendar className="h-4 w-4 text-white" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Preview Text */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-sm text-blue-900 font-medium">
                          {membershipInterval === 1 
                            ? `Recurring ${membershipFrequency}`
                            : `Every ${membershipInterval} ${
                                membershipFrequency === 'daily' ? 'days' :
                                membershipFrequency === 'weekly' ? 'weeks' : 'months'
                              }`
                          } • {membershipOccurrences} {membershipOccurrences === 1 ? 'booking' : 'bookings'} total
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Quick Booking Details */}
                {prefilledData?.isQuickBooking && (
                  <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Clock className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">Quick Booking Details</h3>
                          <p className="text-xs text-gray-600">Pre-selected time slot</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">{prefilledData.asset ? 'Asset:' : 'Resource:'}</span>
                            <span className={`text-sm font-semibold ${prefilledData.asset ? 'text-orange-600' : 'text-blue-600'}`}>
                              {prefilledData.asset?.name || prefilledData.resource?.name}
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Date:</span>
                            <span className="text-sm font-semibold text-blue-600">
                              {formatReadableDate(prefilledData.scheduleData?.startDate || '')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Time:</span>
                            <span className="text-sm font-semibold text-blue-600">
                              {formatReadableTime(prefilledData.scheduleData?.startTime || '')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Available Time:</span>
                            <span className="text-sm font-semibold text-blue-600">
                              {prefilledData.scheduleData?.availableHours || 0} hours
                            </span>
                          </div>
                        </div>
                        
                        {selectedServices.length > 0 && (
                          <>
                            <div className="bg-white rounded-[12px] border border-purple-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ backgroundColor: 'rgba(250, 245, 255, 0.5)' }}>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-700">Service Duration:</span>
                                <span className="text-sm font-semibold text-purple-700">
                                  {totalDurationMinutes >= 1440 
                                    ? `${Math.floor(totalDurationMinutes / 1440)} days` 
                                    : totalDurationMinutes >= 60
                                      ? `${Math.floor(totalDurationMinutes / 60)} hours ${totalDurationMinutes % 60 > 0 ? `${totalDurationMinutes % 60} mins` : ''}`
                                      : `${totalDurationMinutes} mins`}
                                </span>
                              </div>
                            </div>
                            
                            <div className="bg-white rounded-[12px] border border-purple-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ backgroundColor: 'rgba(250, 245, 255, 0.5)' }}>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-700">Duration with Buffer:</span>
                                <span className="text-sm font-semibold text-purple-700">
                                  {(totalDurationMinutes + bufferTime) >= 1440 
                                    ? `${Math.floor((totalDurationMinutes + bufferTime) / 1440)} days` 
                                    : (totalDurationMinutes + bufferTime) >= 60
                                      ? `${Math.floor((totalDurationMinutes + bufferTime) / 60)} hours ${(totalDurationMinutes + bufferTime) % 60 > 0 ? `${(totalDurationMinutes + bufferTime) % 60} mins` : ''}`
                                      : `${totalDurationMinutes + bufferTime} mins`}
                                </span>
                              </div>
                            </div>
                            
                            {(() => {
                              const availableHours = prefilledData.scheduleData?.availableHours || 0;
                              const durationWithBufferHours = (totalDurationMinutes + bufferTime) / 60;
                              const isDurationValid = durationWithBufferHours <= availableHours;
                              
                              console.log('🔍 Quick booking duration check:', {
                                selectedServicesCount: selectedServices.length,
                                totalDurationMinutes,
                                bufferTime,
                                durationWithBufferHours,
                                availableHours,
                                isDurationValid
                              });
                              
                              return (
                                <div className={`bg-white rounded-[12px] border p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
                                  isDurationValid 
                                    ? 'border-green-200/60' 
                                    : 'border-red-200/60'
                                }`} style={{ 
                                  backgroundColor: isDurationValid 
                                    ? 'rgba(240, 253, 244, 0.5)' 
                                    : 'rgba(254, 242, 242, 0.5)' 
                                }}>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-700">Time Check:</span>
                                    <span className={`text-sm font-semibold ${
                                      isDurationValid ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {isDurationValid ? '✓ Fits in time slot' : '✗ Exceeds available time'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Calendar and Time Selection - Hide for quick booking and membership services */}
                {(() => {
                  // Check if any selected service is a membership
                  const hasMembershipService = false /* memberships retired — recurring bookings replace them */;
                  
                  // Show calendar only if: has services, not quick booking, and no membership services
                  return selectedServices.length > 0 && !prefilledData?.isQuickBooking && !hasMembershipService && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    {bookingsByServiceAreaEnabled ? (
                      renderCalendar()
                    ) : (
                      <div className="space-y-3">
                        {/* Schedule button */}
                        <div>
                          <button
                            type="button"
                            onClick={() => !isUnscheduled && setSchedulePickerOpen(true)}
                            disabled={loading || isUnscheduled}
                            className="w-full px-3 py-2.5 text-left text-sm bg-white border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              {isUnscheduled ? (
                                <span className="text-gray-500">No date — unscheduled</span>
                              ) : scheduleData.startDate && scheduleData.startTime ? (
                                <div className="space-y-0.5">
                                  <div className="text-gray-900 font-medium">
                                    {format(new Date(`${scheduleData.startDate}T${scheduleData.startTime}`), 'EEE, MMM d — h:mm a')}
                                    {scheduleData.endDate && scheduleData.endTime && (
                                      <span className="text-gray-400 font-normal"> → </span>
                                    )}
                                    {scheduleData.endDate && scheduleData.endTime && (
                                      <span className="text-gray-700">
                                        {scheduleData.endDate !== scheduleData.startDate
                                          ? format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'EEE, MMM d — h:mm a')
                                          : format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'h:mm a')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">Set schedule</span>
                              )}
                            </div>
                            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                          </button>
                        </div>

                        {/* Schedule Picker Panel */}
                        {schedulePickerOpen && selectedBusiness?.id && (
                          <SchedulePickerPanel
                            isOpen={schedulePickerOpen}
                            onClose={() => setSchedulePickerOpen(false)}
                            businessId={selectedBusiness.id}
                            startDate={scheduleData.startDate}
                            startTime={scheduleData.startTime}
                            endDate={scheduleData.endDate}
                            endTime={scheduleData.endTime}
                            serviceDuration={totalDurationMinutes}
                            onConfirm={async (sDate, sTime, eDate, eTime, resourceId, _dropOffDate, _dropOffTime, resourceIsAsset) => {
                              handleScheduleChange('start', sDate, sTime);
                              if (eDate && eTime) {
                                handleScheduleChange('end', eDate, eTime);
                              }
                              setIsServicesSectionExpanded(false);
                              // Asset column picked in the week grid → assign the asset
                              if (resourceId && resourceIsAsset) {
                                if (!selectedAssets.some((a: any) => a.id === resourceId)) {
                                  try {
                                    const { data } = await supabase
                                      .from('assets')
                                      .select('id, name, asset_type, image_url')
                                      .eq('id', resourceId)
                                      .single();
                                    if (data) setSelectedAssets((prev: any[]) => [...prev, data]);
                                  } catch (err) {
                                    console.error('Failed to fetch asset for schedule selection:', err);
                                  }
                                }
                                return;
                              }
                              if (resourceId && !selectedResources.some(r => r.id === resourceId)) {
                                try {
                                  const { data } = await supabase
                                    .from('resources')
                                    .select('id, first_name, last_name, avatar_url, email, is_mobile_available')
                                    .eq('id', resourceId)
                                    .single();
                                  if (data) {
                                    const newResources = [...selectedResources, data];
                                    setSelectedResources(newResources);
                                    saveMobileBookingProgress({ selectedResources: newResources });
                                    return;
                                  }
                                } catch (err) {
                                  console.error('Failed to fetch resource for schedule selection:', err);
                                }
                              }
                              if (!resourceId && selectedResources.length === 0 && selectedAssets.length === 0 && selectedCustomer) {
                                setIsSelectResourceModalOpen(true);
                              }
                            }}
                            disabled={loading}
                          />
                        )}

                        {/* Unscheduled toggle — create the booking with no date yet */}
                        <button
                          type="button"
                          onClick={() => {
                            setIsUnscheduled((prev) => {
                              const next = !prev;
                              if (next) {
                                setSchedulePickerOpen(false);
                                setScheduleData((sd) => ({
                                  ...sd,
                                  dropOffDate: '',
                                  dropOffTime: '',
                                  startDate: '',
                                  startTime: '',
                                  endDate: '',
                                  endTime: '',
                                }));
                              }
                              return next;
                            });
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left active:scale-[0.98] touch-manipulation ${
                            isUnscheduled
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${isUnscheduled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                            <CalendarClock className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                              {isUnscheduled ? 'Unscheduled — no date set' : 'Set as unscheduled'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {isUnscheduled
                                ? 'This booking will be saved without a date.'
                                : "Don't have a date yet? Save it to the unscheduled list."}
                            </div>
                          </div>
                          <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0 ${isUnscheduled ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                            {isUnscheduled ? <Check className="h-3 w-3 text-white" /> : null}
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Resource Selection - Only when service area matching is disabled and schedule is set */}
                {!bookingsByServiceAreaEnabled && selectedServices.length > 0 && scheduleData.startDate && scheduleData.startTime && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <button
                      type="button"
                      onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                      className="w-full flex items-center justify-end mb-4"
                    >
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isResourcesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Collapsed State - Show resource/asset icons and add button */}
                    {!isResourcesExpanded && (
                      <div className="mb-2">
                        {(selectedResources.length > 0 || selectedAssets.length > 0) && (
                          <div className="flex items-center space-x-2 mb-2 flex-wrap gap-y-2">
                            {selectedResources.map((resource) => (
                              <div key={resource.id} className="relative group">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm overflow-hidden">
                                  {resource.avatar_url ? (
                                    <img 
                                      src={resource.avatar_url} 
                                      alt={`${resource.first_name} ${resource.last_name}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-xs font-bold text-white">
                                      {(resource.first_name || 'R')[0]}{(resource.last_name || '')[0]}
                                    </span>
                                  )}
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  {resource.first_name} {resource.last_name}
                                </div>
                              </div>
                            ))}
                            {selectedAssets.map((asset) => (
                              <div key={asset.id} className="relative group">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm overflow-hidden">
                                  {asset.image_url ? (
                                    <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xs font-bold text-white">
                                      {asset.name.split(/\s+/).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
                                    </span>
                                  )}
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                  {asset.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => setIsSelectResourceModalOpen(true)}
                          className="w-full px-2 py-1.5 border-2 border-dashed border-blue-300 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                          style={{ minHeight: '32px' }}
                        >
                          {selectedResources.length === 0 && selectedAssets.length === 0 ? 'Add Resources' : 'Add More'}
                        </button>
                      </div>
                    )}
                    
                    {/* Expanded State - Show full resource and asset details */}
                    {isResourcesExpanded && (
                      <>
                        {(selectedResources.length > 0 || selectedAssets.length > 0) ? (
                          <div className="space-y-3">
                            {selectedResources.map((resource) => (
                              <div key={resource.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex items-center space-x-3">
                                  <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden">
                                    {resource.avatar_url ? (
                                      <img 
                                        src={resource.avatar_url} 
                                        alt={`${resource.first_name} ${resource.last_name}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <span className="text-sm font-medium text-white">
                                        {(resource.first_name || 'R')[0]}{(resource.last_name || '')[0]}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {resource.first_name} {resource.last_name}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {resource.email}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleResourceRemove(resource.id)}
                                  className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            {selectedAssets.map((asset) => (
                              <div key={asset.id} className="flex items-center justify-between bg-orange-50 rounded-lg p-3 border border-orange-200">
                                <div className="flex items-center space-x-3">
                                  <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center overflow-hidden">
                                    {asset.image_url ? (
                                      <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-sm font-medium text-white">
                                        {asset.name.split(/\s+/).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {asset.name}
                                    </div>
                                    <div className="text-xs text-orange-600">
                                      {asset.asset_type ? asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1) : 'Asset'}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAssetRemove(asset.id)}
                                  className="text-sm font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => setIsSelectResourceModalOpen(true)}
                              className="w-full mt-2 px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                              Add More
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsSelectResourceModalOpen(true)}
                            className="w-full px-3 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            style={{ minHeight: '48px' }}
                          >
                            Add Resources
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Workflow Checkbox - moved from payment summary, only show in scrollable area */}
                {(() => {
                  const hasMembershipService = false /* memberships retired — recurring bookings replace them */;
                  const hasSchedule = bookingsByServiceAreaEnabled || (scheduleData.startDate && scheduleData.startTime);
                  return selectedServices.length > 0 && !hasMembershipService && hasSchedule && workflowsPowerUpEnabled && selectedServices.some(service => servicesWithWorkflows.has(service.service.id)) && (
                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={workflowEnabled}
                          onChange={(e) => setWorkflowEnabled(e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <CheckCircle className="h-3 w-3 text-white" />
                          </div>
                          <div>
                            <span className="text-xs sm:text-sm font-medium text-blue-800">Include Workflow</span>
                            <p className="text-xs text-blue-600 mt-0.5">
                              Auto-assign {bookingsByServiceAreaEnabled
                                ? (selectedResource ? resourceNames[selectedResource.id] || selectedResource.name : 'selected resource')
                                : (selectedResources[0]
                                  ? `${selectedResources[0].first_name || ''} ${selectedResources[0].last_name || ''}`.trim()
                                  : 'selected resource')} to workflow steps
                            </p>
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })()}

              </div>
            </form>

            {/* Fixed bottom — payment summary + submit button */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
              {/* Compact payment summary — shows as services are added */}
              {selectedServices.length > 0 && (
                <div className="mb-2">
                  {(() => {
                    // Per-service discount (carried from a proposal) reduces that service's line.
                    const lineDisc = (s: any): number => {
                      const base = s?.priceOption?.price || 0;
                      const dv = s?.discount_value || 0;
                      if (!dv || base <= 0) return 0;
                      const d = s?.discount_type === 'percentage' ? (base * dv) / 100 : dv;
                      return Math.min(Math.max(Math.round(d * 100) / 100, 0), base);
                    };
                    const lineDiscountTotal = selectedServices.reduce((sum, s) => sum + lineDisc(s), 0);

                    const subtotal = selectedServices.reduce((sum, s) => {
                      let t = s.priceOption.price;
                      const addons = selectedServiceAddons[s._uid] || [];
                      t += addons.reduce((a, addon) => a + addon.price, 0);
                      return sum + t;
                    }, 0);
                    const svcSubs = selectedServices.map(s => {
                      let total = Math.max(0, s.priceOption.price - lineDisc(s));
                      const addons = selectedServiceAddons[s._uid] || [];
                      total += addons.reduce((a, addon) => a + addon.price, 0);
                      return { serviceId: s.service.id, total, taxExempt: s.service.tax_exempt };
                    });
                    const baseForBookingDisc = Math.max(0, subtotal - lineDiscountTotal);
                    const bookingDiscountAmt = pendingDiscount
                      ? pendingDiscount.type === 'percentage'
                        ? Math.round((baseForBookingDisc * (pendingDiscount.amount / 100)) * 100) / 100
                        : Math.min(pendingDiscount.amount, baseForBookingDisc)
                      : 0;
                    const discountAmt = Math.round((lineDiscountTotal + bookingDiscountAmt) * 100) / 100;
                    const gstBase = Math.max(0, Math.round((subtotal - discountAmt) * 100) / 100);
                    const lineMultiplier = subtotal > 0 ? gstBase / subtotal : 0;
                    const taxLines: { label: string; amount: number }[] = [];
                    for (const rate of businessTaxRates) {
                      taxLines.push({
                        label: `${rate.name} (${rate.rate}%)`,
                        amount: Math.round((gstBase * rate.rate) / 100 * 100) / 100,
                      });
                    }
                    const additionalTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
                    for (const atx of serviceAdditionalTaxes) {
                      const svc = svcSubs.find(s => s.serviceId === atx.service_id);
                      if (!svc) continue;
                      const key = `${atx.name}_${atx.rate}`;
                      const scaledLine = svc.total * lineMultiplier;
                      if (additionalTaxMap.has(key)) additionalTaxMap.get(key)!.amount += scaledLine * atx.rate / 100;
                      else additionalTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: scaledLine * atx.rate / 100 });
                    }
                    for (const atx of additionalTaxMap.values()) {
                      taxLines.push({ label: `${atx.name} (${atx.rate}%)`, amount: atx.amount });
                    }
                    const totalTax = taxLines.reduce((s, l) => s + l.amount, 0);
                    const grandTotal = subtotal - discountAmt + totalTax;

                    const resourceName = bookingsByServiceAreaEnabled
                      ? (selectedResource ? (resourceNames[selectedResource.resource_id] || selectedResource.name || 'Resource') : null)
                      : (selectedResources.length > 0 ? selectedResources.map(r => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Resource').join(', ') : null);

                    return (
                      <div className="space-y-1">
                        {/* Resource & Schedule summary */}
                        {(resourceName || (scheduleData.startDate && scheduleData.startTime)) && (
                          <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-wrap mb-0.5">
                            {resourceName && (
                              <span className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                                <span className="font-medium text-gray-700">{resourceName}</span>
                              </span>
                            )}
                            {resourceName && scheduleData.startDate && scheduleData.startTime && (
                              <span className="text-gray-300">·</span>
                            )}
                            {scheduleData.startDate && scheduleData.startTime && (
                              <span className="font-medium text-gray-700">
                                {format(new Date(`${scheduleData.startDate}T${scheduleData.startTime}`), 'EEE, MMM d — h:mm a')}
                                {scheduleData.endDate && scheduleData.endTime && (
                                  <span className="text-gray-400 font-normal"> → </span>
                                )}
                                {scheduleData.endDate && scheduleData.endTime && (
                                  scheduleData.endDate !== scheduleData.startDate
                                    ? format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'EEE, MMM d — h:mm a')
                                    : format(new Date(`${scheduleData.endDate}T${scheduleData.endTime}`), 'h:mm a')
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-3 text-gray-500 flex-wrap">
                            <span>Subtotal <span className="font-medium text-gray-700">${subtotal.toFixed(2)}</span></span>
                            {discountAmt > 0 && (
                              <span className="text-green-600">
                                Discount <span className="font-medium">-${discountAmt.toFixed(2)}</span>
                              </span>
                            )}
                            {taxLines.map((line, i) => (
                              <span key={i}>{line.label} <span className="font-medium text-gray-700">${line.amount.toFixed(2)}</span></span>
                            ))}
                          </div>
                          <div className="text-sm font-bold text-gray-900 flex-shrink-0">
                            Total ${grandTotal.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setIsDiscountModalOpen(true)}
                            className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            {pendingDiscount ? 'Edit Discount' : '+ Add Discount'}
                          </button>
                          {pendingDiscount && (
                            <button
                              type="button"
                              onClick={() => setPendingDiscount(null)}
                              className="text-[11px] font-semibold text-red-500 hover:text-red-700 transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedServices.length > 0 && totalDurationMinutes < 30 && (
                <div className="mb-2 flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl">
                  <svg className="h-3.5 w-3.5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                  </svg>
                  <span className="text-xs text-red-700 font-medium">
                    Min 30 minutes required ({totalDurationMinutes} min currently)
                  </span>
                </div>
              )}
              {/* Recurring series — hidden for unscheduled and reschedules */}
              {!isUnscheduled && !isReschedule && (
                <div className="mb-2">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                    />
                    <span className="text-[13px] text-gray-600">Recurring booking</span>
                  </label>
                  {isRecurring && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Repeats</label>
                        <select
                          value={recurringFrequency}
                          onChange={(e) => setRecurringFrequency(e.target.value as SeriesFrequency)}
                          className="w-full px-2.5 py-1.5 text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                        >
                          {SERIES_FREQUENCIES.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-0.5">For</label>
                        <select
                          value={recurringLengthMonths}
                          onChange={(e) => setRecurringLengthMonths(Number(e.target.value))}
                          className="w-full px-2.5 py-1.5 text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                        >
                          {SERIES_LENGTHS.map(l => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <label className="mb-2 flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={removeFromAutomations}
                  onChange={(e) => setRemoveFromAutomations(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                />
                <span className="text-[13px] text-gray-600">Remove from automations</span>
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={(() => {
                  const basicChecks = loading || !selectedCustomer || selectedServices.length === 0;
                  const durationTooShort = selectedServices.length > 0 && totalDurationMinutes < 30;
                  const isMembershipBooking = false /* memberships retired — recurring bookings replace them */;
                  
                  if (isMembershipBooking) {
                    const hasCheckedAvailability = membershipAvailabilityResults.length > 0;
                    const hasConflicts = membershipAvailabilityResults.some(r => !r.available);
                    return basicChecks || durationTooShort || !hasCheckedAvailability || hasConflicts;
                  }
                  
                  if (prefilledData?.isQuickBooking && selectedServices.length > 0) {
                    const availableHours = prefilledData.scheduleData?.availableHours || 0;
                    const durationWithBufferHours = (totalDurationMinutes + bufferTime) / 60;
                    return basicChecks || durationTooShort || durationWithBufferHours > availableHours;
                  }

                  const noResourceOrAsset = bookingsByServiceAreaEnabled
                    ? (!selectedResource && selectedAssets.length === 0)
                    : (selectedResources.length === 0 && selectedAssets.length === 0);
                  const noSchedule = !scheduleData.startDate || !scheduleData.startTime;
                  
                  return basicChecks || durationTooShort || (!isUnscheduled && (noResourceOrAsset || noSchedule));
                })()}
                className={`w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:cursor-not-allowed disabled:transform-none ${
                  (() => {
                    const basicChecks = loading || !selectedCustomer || selectedServices.length === 0;
                    const durationTooShort = selectedServices.length > 0 && totalDurationMinutes < 30;
                    const isMembershipBooking = false /* memberships retired — recurring bookings replace them */;
                    
                    if (isMembershipBooking) {
                      const hasCheckedAvailability = membershipAvailabilityResults.length > 0;
                      const hasConflicts = membershipAvailabilityResults.some(r => !r.available);
                      return basicChecks || durationTooShort || !hasCheckedAvailability || hasConflicts;
                    }
                    
                    if (prefilledData?.isQuickBooking && selectedServices.length > 0) {
                      const availableHours = prefilledData.scheduleData?.availableHours || 0;
                      const durationWithBufferHours = (totalDurationMinutes + bufferTime) / 60;
                      return basicChecks || durationTooShort || durationWithBufferHours > availableHours;
                    }

                    const noResourceOrAsset = bookingsByServiceAreaEnabled
                      ? (!selectedResource && selectedAssets.length === 0)
                      : (selectedResources.length === 0 && selectedAssets.length === 0);
                    const noSchedule = !scheduleData.startDate || !scheduleData.startTime;

                    return basicChecks || durationTooShort || (!isUnscheduled && (noResourceOrAsset || noSchedule));
                  })()
                    ? 'bg-gray-300'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    {prefilledData?.isQuickBooking ? 'Creating Quick Booking...' : 'Creating Mobile Booking...'}
                  </>
                ) : (
                  <>
                    <Smartphone className="h-4 w-4 mr-2" />
                    {prefilledData?.isQuickBooking ? 'Create Quick Booking' : 'Create Mobile Booking'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inline Discount Modal */}
      {isDiscountModalOpen && createPortal(
        <div className="fixed inset-0 flex items-end sm:items-center justify-center" style={{ zIndex: baseZ + 198 }} onClick={() => setIsDiscountModalOpen(false)}>
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="relative bg-white w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl p-5 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Add Discount</h3>
              <button type="button" onClick={() => setIsDiscountModalOpen(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <InlineDiscountForm
              initialDiscount={pendingDiscount}
              bookingSubtotal={selectedServices.reduce((sum, s) => {
                let t = s.priceOption.price;
                const addons = selectedServiceAddons[s._uid] || [];
                t += addons.reduce((a, addon) => a + addon.price, 0);
                return sum + t;
              }, 0)}
              onApply={(discount) => { setPendingDiscount(discount); setIsDiscountModalOpen(false); }}
            />
          </div>
        </div>,
        document.body
      )}

      {createPortal(
        <SelectCustomerModal
          isOpen={isSelectCustomerModalOpen}
          onClose={() => setIsSelectCustomerModalOpen(false)}
          onSelect={customer => {
            console.log('📱 Customer selected in CreateMobileBookingModal:', customer);
            setSelectedCustomer(customer);
            setCurrentVehicle(null);
            setIsSelectCustomerModalOpen(false);
            setConvertedProposal(null);
            
            // Reset referral source when customer changes
            setReferralSourceCustomer(null);
            
            // Auto-detect customer source based on repeat status
            detectCustomerSource(customer);
            
            // Save progress immediately with the new customer data
            saveMobileBookingProgress({ selectedCustomer: customer });

            const openResourceIfReady = () => {
              if (
                !isUnscheduled &&
                scheduleData.startDate &&
                scheduleData.startTime &&
                selectedResources.length === 0 &&
                selectedAssets.length === 0
              ) {
                setIsSelectResourceModalOpen(true);
              }
            };

            // Background check: open/approved proposals for this customer
            if (!initialProposal && selectedBusiness?.id && customer.id) {
              void (async () => {
                try {
                  const openProposals = await getConvertibleProposalsForCustomer(selectedBusiness.id, customer.id);
                  if (openProposals.length > 0) {
                    setExistingProposals(openProposals);
                    setIsExistingProposalsModalOpen(true);
                    return;
                  }
                } catch (err) {
                  console.warn('Could not check customer proposals on mobile booking create:', err);
                }
                openResourceIfReady();
              })();
            } else {
              openResourceIfReady();
            }
          }}
        />,
        document.body
      )}

      <ExistingCustomerProposalsModal
        isOpen={isExistingProposalsModalOpen}
        proposals={existingProposals}
        customerName={
          selectedCustomer
            ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()
            : undefined
        }
        onIgnore={() => {
          setIsExistingProposalsModalOpen(false);
          setExistingProposals([]);
          if (
            !isUnscheduled &&
            scheduleData.startDate &&
            scheduleData.startTime &&
            selectedResources.length === 0 &&
            selectedAssets.length === 0
          ) {
            setIsSelectResourceModalOpen(true);
          }
        }}
        onConvert={async (summary) => {
          try {
            setConvertingExistingProposal(true);
            const full = await getProposal(summary.id);
            setConvertedProposal(full);
            setIsExistingProposalsModalOpen(false);
            setExistingProposals([]);
          } catch (err) {
            console.error('Error converting existing proposal into mobile booking:', err);
          } finally {
            setConvertingExistingProposal(false);
          }
        }}
        converting={convertingExistingProposal}
      />

      {createPortal(
        <SelectCustomerModal
          isOpen={isSelectReferralSourceModalOpen}
          onClose={() => setIsSelectReferralSourceModalOpen(false)}
          onSelect={async (customer) => {
            setReferralSourceCustomer(customer);
            setIsSelectReferralSourceModalOpen(false);
          }}
        />,
        document.body
      )}

      {createPortal(
        <SelectVehicleModal
          isOpen={isSelectVehicleModalOpen}
          onClose={() => { setIsSelectVehicleModalOpen(false); setVehicleIdToSwap(null); }}
          onSelect={handleSelectVehicle}
          vehicles={selectedCustomer?.vehicles || []}
          selectedVehicles={selectedServices.map(s => s.vehicle)}
          onAddVehicle={() => {
            setIsSelectVehicleModalOpen(false);
            setIsCreateVehicleModalOpen(true);
          }}
        />,
        document.body
      )}

      {createPortal(
        <CreateVehicleModal
          isOpen={isCreateVehicleModalOpen}
          onClose={() => setIsCreateVehicleModalOpen(false)}
          onSuccess={(vehicle: any) => handleVehicleCreated(vehicle)}
          customerId={selectedCustomer?.id || ''}
        />,
        document.body
      )}

      {createPortal(
        <EditCustomerModal
          isOpen={isEditCustomerModalOpen}
          onClose={() => setIsEditCustomerModalOpen(false)}
          onSuccess={(updatedCustomer) => {
            setIsEditCustomerModalOpen(false);
            setSelectedCustomer(updatedCustomer);
          }}
          customer={selectedCustomer}
        />,
        document.body
      )}

      {createPortal(
        <SelectServiceModal
          isOpen={isSelectServiceModalOpen}
          onClose={() => setIsSelectServiceModalOpen(false)}
          onAdd={async (service, priceOption) => {
            handleAddService(service, priceOption);
            return Promise.resolve();
          }}
        />,
        document.body
      )}

      {createPortal(
        <SelectResourceModal
          isOpen={isSelectResourceModalOpen}
          onClose={() => setIsSelectResourceModalOpen(false)}
          onConfirm={({ resources, assets }) => {
            setSelectedResources(resources);
            saveMobileBookingProgress({ selectedResources: resources });
            if (assetsEnabled) {
              setSelectedAssets(assets);
            }
          }}
          selectedResources={selectedResources}
          selectedAssetIds={selectedAssets.map(a => a.id)}
          assetsEnabled={assetsEnabled}
          confirmLabel="Add to booking"
        />,
        document.body
      )}

      {createPortal(
        <EditServiceModal
          isOpen={isEditServiceModalOpen}
          onClose={() => {
            setIsEditServiceModalOpen(false);
                setSelectedServiceToEdit(null);
              }}
              onSave={async (updatedService) => {
                if (!selectedServiceToEdit) return;
                
                // Update the service in the selectedServices array
                setSelectedServices(prev => prev.map(s => 
                  s._uid === selectedServiceToEdit._uid
                    ? {
                        ...s,
                        service: {
                          ...s.service,
                          name: updatedService.name,
                          description: updatedService.description,
                          tax_exempt: updatedService.tax_exempt,
                          tax_rate: updatedService.tax_rate
                        },
                        priceOption: {
                          ...s.priceOption,
                          price: updatedService.price,
                          duration_minutes: updatedService.duration_minutes
                        }
                      }
                    : s
                ));

                // Update the add-ons for this service
                if (updatedService.addons) {
                  setSelectedServiceAddons(prev => ({
                    ...prev,
                    [selectedServiceToEdit._uid]: updatedService.addons
                  }));
                }

                setIsEditServiceModalOpen(false);
                setSelectedServiceToEdit(null);
              }}
              service={selectedServiceToEdit ? {
                name: selectedServiceToEdit.service.name,
                description: selectedServiceToEdit.service.description,
                price: selectedServiceToEdit.priceOption.price,
                duration_minutes: selectedServiceToEdit.priceOption.duration_minutes,
                is_active: true,
                tax_rate: selectedServiceToEdit.service.tax_rate || 5,
                tax_exempt: selectedServiceToEdit.service.tax_exempt || false,
                addons: selectedServiceAddons[selectedServiceToEdit._uid] || []
              } : null}
            />,
            document.body
          )}

      {createPortal(
        <EditVehicleModal
          isOpen={isEditVehicleModalOpen}
          onClose={() => {
            setIsEditVehicleModalOpen(false);
            setSelectedVehicleToEdit(null);
          }}
          vehicle={selectedVehicleToEdit}
          onUpdate={async (updatedVehicle: CustomerVehicle) => {
            // Update customer data to get the updated vehicle
            const updatedCustomer = await getCustomer(selectedCustomer?.id || '');
            setSelectedCustomer(updatedCustomer);
            
            // Update the vehicle in selectedServices
            setSelectedServices(prev => prev.map(s => 
              s.vehicle.id === updatedVehicle.id
                ? { ...s, vehicle: updatedVehicle }
                : s
            ));
            
            setIsEditVehicleModalOpen(false);
            setSelectedVehicleToEdit(null);
          }}
        />,
        document.body
      )}

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => {
          setIsSelectAddonsModalOpen(false);
          setCurrentService(null);
          setCurrentServiceUid(null);
        }}
        serviceId={currentService?.id || ''}
        onSelect={handleAddAddons}
        selectedAddons={currentServiceUid ? selectedServiceAddons[currentServiceUid] || [] : []}
      />

      {isResourceSelectorOpen && (
        <div className="fixed inset-0" style={{ zIndex: baseZ + 6, pointerEvents: isResourceSelectorOpen ? 'auto' : 'none'}}>
          <ResourceSelector />
        </div>
      )}

      {/* Field Quote Link Picker — shown after create is pressed, before saving */}
      {fqPickerOpen && createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: baseZ + 40 }}
          onClick={() => { setFqPickerOpen(false); setLoading(false); }}
        >
          <div className="fixed inset-0 bg-black/50" style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }} />
          <div
            className="relative bg-white w-full sm:w-[440px] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Link a field quote?</h3>
              <p className="text-sm text-gray-500 mt-1">
                This customer has field quotes that may have led to this booking. Select any to link, or skip.
              </p>
            </div>

            <div className="px-5 py-3 overflow-y-auto space-y-2">
              {fqCandidates.map(fq => {
                const checked = fqSelectedIds.includes(fq.id);
                const matchLabel =
                  fq.matched_by === 'proposal' ? 'From proposal'
                  : fq.matched_by === 'opportunity' ? 'Same opportunity'
                  : fq.matched_by === 'project' ? 'Same project'
                  : 'Same customer';
                const dateLabel = fq.start_date
                  ? format(new Date(fq.start_date), 'MMM d, yyyy')
                  : 'No date set';
                return (
                  <button
                    type="button"
                    key={fq.id}
                    onClick={() => setFqSelectedIds(prev =>
                      prev.includes(fq.id) ? prev.filter(id => id !== fq.id) : [...prev, fq.id]
                    )}
                    className={`w-full text-left flex items-start gap-3 rounded-[12px] border px-3 py-3 transition-colors ${
                      checked ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
                      checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'
                    }`}>
                      {checked && <span className="text-[11px] leading-none">✓</span>}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {fq.project_title || 'Field quote'}
                        </span>
                        <span className="text-[11px] font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 flex-shrink-0">
                          {matchLabel}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500 truncate">
                        {dateLabel}
                        {fq.field_quote_address ? ` · ${fq.field_quote_address}` : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex gap-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
              <button
                type="button"
                onClick={() => {
                  resolvedFieldQuoteIdsRef.current = [];
                  setFqPickerOpen(false);
                  void handleSubmit();
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => {
                  resolvedFieldQuoteIdsRef.current = [...fqSelectedIds];
                  setFqPickerOpen(false);
                  void handleSubmit();
                }}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-colors disabled:opacity-50"
              >
                {fqSelectedIds.length > 0 ? `Link ${fqSelectedIds.length} & create` : 'Create without link'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create Customer Source Modal */}
      {isCreateSourceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: baseZ + 4, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          <div className="backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl p-6 border border-white/20 w-full max-w-md mx-4"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Add Customer Source
              </h3>
              <button
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source Name
              </label>
              <input
                type="text"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter source name (e.g., Google Ads, Referral)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSourceName.trim()) {
                    handleCreateCustomerSource();
                  }
                }}
                autoFocus
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsCreateSourceModalOpen(false);
                  setNewSourceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={createSourceLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCustomerSource}
                disabled={!newSourceName.trim() || createSourceLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createSourceLoading ? 'Creating...' : 'Create Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Picker Modal for Editing Conflict Bookings */}
      {isCalendarPickerOpen && editingBookingIndex !== null && (() => {
        // Simplified: just check if date and time are selected
        const isButtonDisabled = !scheduleData.startDate || !scheduleData.startTime;
        
        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-200 p-4"
            style={{
              zIndex: baseZ + 98,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              paddingLeft: 'env(safe-area-inset-left, 0px)',
              paddingRight: 'env(safe-area-inset-right, 0px)',
            }}
            onClick={() => setIsCalendarPickerOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Choose New Date & Time - Booking #{editingBookingIndex + 1}
                </h3>
                <button
                  onClick={() => setIsCalendarPickerOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Original Conflict Info */}
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
                <p className="text-sm text-red-800">
                  <span className="font-semibold">Original conflict:</span>{' '}
                  {(() => {
                    const resultDate = membershipAvailabilityResults[editingBookingIndex]?.date || '';
                    if (!resultDate) return '';
                    // Parse date string in local timezone to avoid UTC conversion
                    const [year, month, day] = resultDate.split('-').map(Number);
                    const displayDate = new Date(year, month - 1, day);
                    return displayDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                    });
                  })()} at {membershipAvailabilityResults[editingBookingIndex]?.time}
                </p>
              </div>
            </div>

            {/* Calendar Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              {renderCalendar()}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsCalendarPickerOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (editingBookingIndex === null) {
                    console.log('🎫 ❌ No booking index set, aborting');
                    return;
                  }
                  
                  if (checkingAvailability) {
                    console.log('🎫 ❌ Already processing, aborting');
                    return;
                  }
                  
                  setCheckingAvailability(true);
                  
                  console.log('\n🎫 ========== UPDATE BOOKING START ==========');
                  console.log('🎫 Booking #' + (editingBookingIndex + 1), '(array index:', editingBookingIndex + ')');
                  console.log('🎫 Original:', editingBookingDate, 'at', editingBookingTime);
                  console.log('🎫 New:', scheduleData.startDate, 'at', scheduleData.startTime);
                  console.log('🎫 Current results array length:', membershipAvailabilityResults.length);
                  
                  // Get the selected date and time
                  const newDate = scheduleData.startDate;
                  const newTime = scheduleData.startTime;
                  
                  // Calculate service duration for availability check
                  const serviceDuration = selectedServices[0]?.priceOption?.duration_minutes || 0;
                  const addons = selectedServiceAddons[selectedServices[0]?._uid] || [];
                  const addonDuration = addons.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0);
                  const totalDuration = serviceDuration + addonDuration + bufferTime;
                  
                  // Re-check availability for this new date/time
                  let available = true;
                  let conflictReason = '';
                  let resourceInfo: { id: string; name: string } | undefined = undefined;
                  
                  try {
                    // Adjust for timezone offset (same as calendar picker logic)
                    const occurrenceDate = new Date(newDate);
                    const offset = occurrenceDate.getTimezoneOffset();
                    occurrenceDate.setMinutes(occurrenceDate.getMinutes() + offset);
                    const dayOfWeek = format(occurrenceDate, 'EEEE').toLowerCase();
                    
                    console.log('🎫 Timezone check:', {
                      originalDate: newDate,
                      afterOffset: format(occurrenceDate, 'yyyy-MM-dd'),
                      dayOfWeek,
                      offset
                    });
                    
                    // Check resources availability for this new slot
                    const availableResourcesForSlot = mobileAvailability.filter(resource => {
                      // Check postal code
                      let postalCodes: any = resource.postal_codes;
                      if (typeof postalCodes === 'string') {
                        postalCodes = JSON.parse(postalCodes);
                      }
                      const dayPostalCodes = postalCodes?.[dayOfWeek];
                      if (!Array.isArray(dayPostalCodes) || !dayPostalCodes.includes(postalCode)) {
                        return false;
                      }
                      
                      // Check working hours
                      let workingHours = resource.working_hours;
                      if (typeof workingHours === 'string') {
                        workingHours = JSON.parse(workingHours);
                      }
                      
                      const daySchedule = workingHours?.[dayOfWeek];
                      if (!daySchedule?.enabled || !daySchedule?.shifts?.length) {
                        return false;
                      }
                      
                      // Check if time fits within shifts
                      const [startHour, startMinute] = newTime.split(':').map(Number);
                      const bookingStartMinutes = startHour * 60 + startMinute;
                      const bookingEndMinutes = bookingStartMinutes + totalDuration;
                      
                      return daySchedule.shifts.some(shift => {
                        const [shiftStartHour, shiftStartMinute] = shift.start.split(':').map(Number);
                        const shiftStartMinutes = shiftStartHour * 60 + shiftStartMinute;
                        const [shiftEndHour, shiftEndMinute] = shift.end.split(':').map(Number);
                        const shiftEndMinutes = shiftEndHour * 60 + shiftEndMinute;
                        
                        return bookingStartMinutes >= shiftStartMinutes && bookingEndMinutes <= shiftEndMinutes;
                      });
                    });
                    
                    if (availableResourcesForSlot.length === 0) {
                      available = false;
                      conflictReason = 'No resources available for this time slot';
                    }
                    
                    // Get resource info if available
                    let resourceInfo = undefined;
                    if (availableResourcesForSlot.length > 0) {
                      const firstResource = availableResourcesForSlot[0];
                      const resourceName = resourceNames[firstResource.id] || 
                                          resourceNames[firstResource.resource_id] || 
                                          `Resource ${firstResource.resource_id}`;
                      
                      resourceInfo = {
                        id: firstResource.resource_id,
                        name: resourceName
                      };
                      
                      console.log(`🎫 Assigned resource for updated booking:`, resourceName);
                    }
                  } catch (err) {
                    console.error('Error re-checking availability:', err);
                    available = false;
                    conflictReason = 'Error checking availability';
                  }
                  
                  // Update the specific booking in the results using functional update
                  setMembershipAvailabilityResults(prevResults => {
                    const updatedResults = [...prevResults];
                    const oldBooking = updatedResults[editingBookingIndex];
                    
                    console.log('🎫 ✅ Updating index', editingBookingIndex, ':', {
                      from: `${oldBooking.date} ${oldBooking.time}`,
                      to: `${newDate} ${newTime}`,
                      status: available ? '✅ Available' : '❌ Conflict',
                      resource: (resourceInfo as any)?.name || 'None'
                    });
                    
                    updatedResults[editingBookingIndex] = {
                      date: newDate,
                      time: newTime,
                      available,
                      conflict: available ? undefined : conflictReason,
                      resource: resourceInfo
                    } as any;
                    
                    console.log('🎫 Result after update:', updatedResults[editingBookingIndex]);
                    return updatedResults;
                  });
                  
                  setResultsUpdateCounter(prev => prev + 1); // Force re-render
                  setCheckingAvailability(false);
                  console.log('🎫 ========== UPDATE BOOKING END ==========\n');
                  
                  // Reset editing state
                  setEditingBookingIndex(null);
                  setEditingBookingDate('');
                  setEditingBookingTime('09:00');
                  
                  setIsCalendarPickerOpen(false);
                }}
                disabled={isButtonDisabled}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 touch-manipulation"
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: '44px'
                }}
              >
                Update Booking
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 